// src/dto/aap.dto.ts
import { z as z2 } from "zod";

// src/dto/primitives.ts
import { z } from "zod";

// src/lib/geo-codes.ts
var COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "MX", name: "Mexico" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" }
];
var COUNTRY_INDEX = new Map(COUNTRIES.map((c) => [c.code, c]));
function countryLabel(code) {
  if (!code) return "";
  return COUNTRY_INDEX.get(code.toUpperCase())?.name ?? code.toUpperCase();
}
var US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "AS", name: "American Samoa" },
  { code: "GU", name: "Guam" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "PR", name: "Puerto Rico" },
  { code: "VI", name: "U.S. Virgin Islands" }
];
var CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" }
];
function stateOptionsForCountry(country) {
  return country?.toUpperCase() === "CA" ? CA_PROVINCES : US_STATES;
}
var US_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu"
];
var CA_TIMEZONES = [
  "America/St_Johns",
  "America/Halifax",
  "America/Toronto",
  "America/Winnipeg",
  "America/Regina",
  "America/Edmonton",
  "America/Vancouver",
  "America/Whitehorse",
  "America/Iqaluit"
];
function timezonesForCountry(country) {
  return country?.toUpperCase() === "CA" ? CA_TIMEZONES : US_TIMEZONES;
}
function currencyForCountry(country) {
  return country?.toUpperCase() === "CA" ? "CAD" : "USD";
}

// src/dto/primitives.ts
var E164_RE = /^\+[1-9]\d{6,14}$/;
var US_ZIP_RE = /^\d{5}(-\d{4})?$/;
var CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
var EIN_RE = /^\d{2}-?\d{7}$/;
var VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/;
var PHONE_MESSAGE = "Use E.164 format, e.g. +15551234567.";
var ZIP_MESSAGE = "Use ZIP (12345) or ZIP+4 (12345-6789).";
var CA_POSTAL_MESSAGE = "Use a Canadian postal code, e.g. A1A 1A1.";
var ZIP_OR_POSTAL_MESSAGE = "Use a US ZIP (12345) or Canadian postal code (A1A 1A1).";
var STATE_MESSAGE = "Pick a US state (2-letter code).";
var STATE_OR_PROVINCE_MESSAGE = "Pick a US state or Canadian province (2-letter code).";
var COUNTRY_MESSAGE = "Pick a supported country (2-letter code).";
var EIN_MESSAGE = "Use a 9-digit EIN, e.g. 12-3456789.";
var VIN_MESSAGE = "VIN must be 17 characters \u2014 letters & digits, no I/O/Q.";
var US_STATE_CODES = new Set(US_STATES.map((s) => s.code));
var STATE_OR_PROVINCE_CODES = /* @__PURE__ */ new Set([
  ...US_STATES.map((s) => s.code),
  ...CA_PROVINCES.map((p) => p.code)
]);
var COUNTRY_CODES = new Set(COUNTRIES.map((c) => c.code));
var PhoneE164Schema = z.string().trim().regex(E164_RE, PHONE_MESSAGE);
var OptionalPhoneE164Schema = PhoneE164Schema.nullable().optional();
var UsZipSchema = z.string().trim().regex(US_ZIP_RE, ZIP_MESSAGE);
var OptionalUsZipSchema = UsZipSchema.nullable().optional();
var CaPostalCodeSchema = z.string().trim().regex(CA_POSTAL_RE, CA_POSTAL_MESSAGE);
var OptionalCaPostalCodeSchema = CaPostalCodeSchema.nullable().optional();
var ZipOrPostalCodeSchema = z.string().trim().refine((v) => US_ZIP_RE.test(v) || CA_POSTAL_RE.test(v), ZIP_OR_POSTAL_MESSAGE);
var OptionalZipOrPostalCodeSchema = ZipOrPostalCodeSchema.nullable().optional();
var UsStateCodeSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().refine((c) => US_STATE_CODES.has(c), STATE_MESSAGE));
var OptionalUsStateCodeSchema = UsStateCodeSchema.nullable().optional();
var StateOrProvinceCodeSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().refine((c) => STATE_OR_PROVINCE_CODES.has(c), STATE_OR_PROVINCE_MESSAGE));
var OptionalStateOrProvinceCodeSchema = StateOrProvinceCodeSchema.nullable().optional();
var CountryCodeSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().refine((c) => COUNTRY_CODES.has(c), COUNTRY_MESSAGE));
var OptionalCountryCodeSchema = CountryCodeSchema.nullable().optional();
var EinSchema = z.string().trim().regex(EIN_RE, EIN_MESSAGE);
var OptionalEinSchema = EinSchema.nullable().optional();
var VinSchema = z.string().trim().transform((s) => s.toUpperCase()).pipe(z.string().regex(VIN_RE, VIN_MESSAGE));
var OptionalVinSchema = VinSchema.nullable().optional();
var EmailSchema = z.string().trim().email("Enter a valid email address.");
var OptionalEmailSchema = EmailSchema.nullable().optional();
var UrlSchema = z.string().trim().url("Use a full URL, e.g. https://example.com.");
var OptionalUrlSchema = UrlSchema.nullable().optional();
var WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
var DayHoursSchema = z.object({
  open: z.boolean(),
  startMinute: z.number().int().multipleOf(15).min(0).max(1425),
  endMinute: z.number().int().multipleOf(15).min(15).max(1440)
}).refine((h) => !h.open || h.endMinute > h.startMinute, {
  message: "Closing time must be after the opening time.",
  path: ["endMinute"]
});
var WeeklyBusinessHoursSchema = z.object({
  monday: DayHoursSchema,
  tuesday: DayHoursSchema,
  wednesday: DayHoursSchema,
  thursday: DayHoursSchema,
  friday: DayHoursSchema,
  saturday: DayHoursSchema,
  sunday: DayHoursSchema
});

// src/dto/aap.dto.ts
var AAP_EXTENSION_URI = "https://autoagentprotocol.org/extensions/a2a-automotive-retail/v0.1";
var AAP_SKILL_IDS = [
  "dealer.information",
  "inventory.facets",
  "inventory.search",
  "inventory.vehicle",
  "lead.submit"
];
var AAP_METHODS = [
  "dealer.information.request",
  "inventory.facets.request",
  "inventory.search.request",
  "inventory.vehicle.request",
  "lead.submit.request"
];
var AgentCardSkillSchema = z2.object({
  id: z2.string(),
  name: z2.string(),
  description: z2.string().optional()
});
var AgentCardExtensionSchema = z2.object({
  uri: z2.string(),
  params: z2.record(z2.unknown()).optional()
});
var AgentCardInterfaceSchema = z2.object({
  protocol_binding: z2.enum(["JSONRPC", "HTTP+JSON"])
});
var AgentCardSchema = z2.object({
  name: z2.string(),
  description: z2.string().optional(),
  url: z2.string().url(),
  version: z2.string(),
  documentationUrl: z2.string().url().optional(),
  provider: z2.object({
    organization: z2.string().optional(),
    url: z2.string().url().optional()
  }).optional(),
  defaultInputModes: z2.array(z2.string()).optional(),
  defaultOutputModes: z2.array(z2.string()).optional(),
  capabilities: z2.object({
    /**
     * Standard A2A capability flags. Boolean false is the
     * accurate value for v0.1 — we don't implement SSE
     * streaming, push notifications, or state-transition
     * history yet. A compliant client treats absent fields as
     * `false`, but emitting them explicitly is friendlier.
     */
    streaming: z2.boolean().optional(),
    pushNotifications: z2.boolean().optional(),
    stateTransitionHistory: z2.boolean().optional(),
    extensions: z2.array(AgentCardExtensionSchema).optional()
  }),
  /** AAP-required interface advertisement. */
  supported_interfaces: z2.array(AgentCardInterfaceSchema).optional(),
  skills: z2.array(AgentCardSkillSchema),
  authentication: z2.object({
    schemes: z2.array(z2.string()).optional()
  }).optional()
});
var JsonRpcRequestSchema = z2.object({
  jsonrpc: z2.literal("2.0"),
  id: z2.union([z2.string(), z2.number()]).optional(),
  method: z2.string(),
  params: z2.unknown().optional()
});
var JsonRpcErrorSchema = z2.object({
  code: z2.number().int(),
  message: z2.string(),
  data: z2.unknown().optional()
});
var DealerInformationRequestSchema = z2.object({}).optional();
var DealerInformationResponseSchema = z2.object({
  name: z2.string(),
  phone: z2.string().nullable(),
  email: z2.string().nullable(),
  website: z2.string().nullable(),
  address: z2.string().nullable(),
  timezone: z2.string().nullable(),
  salesHours: z2.record(z2.unknown()).nullable(),
  serviceHours: z2.record(z2.unknown()).nullable()
});
var InventoryQuerySchema = z2.object({
  search: z2.string().optional(),
  make: z2.array(z2.string()).optional(),
  model: z2.array(z2.string()).optional(),
  body: z2.array(z2.string()).optional(),
  fuel: z2.array(z2.string()).optional(),
  driveTrain: z2.array(z2.string()).optional(),
  year: z2.object({ from: z2.number().optional(), to: z2.number().optional() }).optional(),
  price: z2.object({ from: z2.number().optional(), to: z2.number().optional() }).optional(),
  mileage: z2.object({ from: z2.number().optional(), to: z2.number().optional() }).optional(),
  pagination: z2.object({
    skip: z2.number().int().min(0).default(0),
    limit: z2.number().int().min(1).max(100).default(20)
  }).optional()
});
var InventoryVehicleRequestSchema = z2.object({
  vin: z2.string()
});
var LeadSubmitRequestSchema = z2.object({
  customer: z2.object({
    firstName: z2.string().max(80).optional(),
    lastName: z2.string().max(80).optional(),
    email: z2.string().email().optional(),
    phone: PhoneE164Schema.optional(),
    addressLine1: z2.string().max(120).optional(),
    city: z2.string().max(80).optional(),
    state: StateOrProvinceCodeSchema.optional(),
    zip: ZipOrPostalCodeSchema.optional()
  }),
  intent: z2.enum(["sales", "service", "test_drive", "trade_in", "general"]).default("sales"),
  message: z2.string().optional(),
  vehicle: z2.object({
    vin: z2.string().optional(),
    year: z2.number().int().optional(),
    make: z2.string().optional(),
    model: z2.string().optional(),
    trim: z2.string().optional()
  }).optional()
});
var LeadSubmitResponseSchema = z2.object({
  leadId: z2.string(),
  contactId: z2.string()
});
var ConnectAapChannelSchema = z2.object({
  rooftopId: z2.string().uuid(),
  name: z2.string().trim().min(1).max(80),
  /**
   * The bare hostname the dealer hosts the agent card on, e.g.
   * `dealer.example.com`. Stored on `channels.config.aapDomain`
   * and used by the Validate flow to fetch
   * `https://<domain>/.well-known/agent-card.json`.
   */
  domain: z2.string().trim().toLowerCase().min(3).max(253).regex(/^(?!https?:\/\/)[a-z0-9.\-:]+$/, "Use a bare hostname like dealer.example.com.")
});
var AapValidationResultSchema = z2.object({
  ok: z2.boolean(),
  fetchedUrl: z2.string(),
  /** Reason the fetch / compare failed; null on success. */
  error: z2.string().nullable(),
  /**
   * When `ok=false` because the hosted JSON differs from the
   * expected one, this is the first diffing field path (e.g.
   * `skills[1].id`). Null when ok or when the issue isn't a
   * field diff (HTTP error, parse error).
   */
  mismatchPath: z2.string().nullable()
});

// src/dto/api-key.dto.ts
import { z as z3 } from "zod";
var API_KEY_PREFIX = "lmk_";
var ApiKeyScopeSchema = z3.enum(["read"]);
var ApiKeySchema = z3.object({
  id: z3.string().uuid(),
  dealershipId: z3.string().uuid(),
  name: z3.string(),
  scopes: z3.array(z3.string()),
  /** First 8 chars of the raw key (prefix incl.) for recognition. */
  keyHint: z3.string(),
  lastUsedAt: z3.string().datetime().nullable(),
  revokedAt: z3.string().datetime().nullable(),
  createdAt: z3.string().datetime()
});
var CreateApiKeySchema = z3.object({
  name: z3.string().trim().min(1).max(120),
  scopes: z3.array(ApiKeyScopeSchema).min(1).default(["read"])
});
var ApiKeyCreatedSchema = ApiKeySchema.extend({
  key: z3.string()
});

// src/dto/automation.dto.ts
import { z as z4 } from "zod";
var AutomationConditionsSchema = z4.object({
  /** Matches `contact.leadSource` (e.g. "lumika - SMS", "lumika - Website Chat"). */
  source: z4.string().trim().min(1).max(200).optional(),
  /**
   * Matches `lead.primaryChannel` — the canonical channel-type vocab
   * (sms, email, web, voice, messenger, instagram, whatsapp, telegram, api).
   */
  channel: z4.string().trim().min(1).max(50).optional()
}).strict();
var AssignActionConfigSchema = z4.object({ assignedUserId: z4.string().uuid() }).strict();
var AddTagActionConfigSchema = z4.object({ tagId: z4.string().uuid() }).strict();
var CreateTaskActionConfigSchema = z4.object({
  title: z4.string().trim().min(1).max(200),
  assignedUserId: z4.string().uuid().optional(),
  /** Hours from lead creation to set as dueAt; null/absent = no due date. */
  dueInHours: z4.number().int().min(1).max(8760).optional()
}).strict();
var AutomationActionTypeSchema = z4.enum(["assign", "add_tag", "create_task"]);
var AutomationRuleSchema = z4.object({
  id: z4.string().uuid(),
  dealershipId: z4.string().uuid(),
  name: z4.string(),
  enabled: z4.boolean(),
  trigger: z4.string(),
  conditions: AutomationConditionsSchema,
  actionType: AutomationActionTypeSchema,
  actionConfig: z4.union([
    AssignActionConfigSchema,
    AddTagActionConfigSchema,
    CreateTaskActionConfigSchema
  ]),
  createdByUserId: z4.string().uuid().nullable(),
  createdAt: z4.string().datetime(),
  updatedAt: z4.string().datetime()
});
var CreateAutomationRuleSchema = z4.object({
  name: z4.string().trim().min(1).max(200),
  enabled: z4.boolean().default(true),
  conditions: AutomationConditionsSchema.default({}),
  actionType: AutomationActionTypeSchema,
  actionConfig: z4.union([
    AssignActionConfigSchema,
    AddTagActionConfigSchema,
    CreateTaskActionConfigSchema
  ])
});
var UpdateAutomationRuleSchema = z4.object({
  name: z4.string().trim().min(1).max(200),
  enabled: z4.boolean(),
  conditions: AutomationConditionsSchema,
  actionType: AutomationActionTypeSchema,
  actionConfig: z4.union([
    AssignActionConfigSchema,
    AddTagActionConfigSchema,
    CreateTaskActionConfigSchema
  ])
}).partial();

// src/dto/calendar-sync.dto.ts
import { z as z5 } from "zod";
var CalendarAccountStatusSchema = z5.enum([
  "active",
  "errored",
  "revoked"
]);
var CalendarAccountSchema = z5.object({
  id: z5.string().uuid(),
  provider: z5.enum(["google", "microsoft"]),
  calendarId: z5.string().nullable(),
  status: CalendarAccountStatusSchema,
  createdAt: z5.string().datetime()
});
var CalendarSyncStatusSchema = z5.object({
  /** False when the deployment has no Google OAuth app configured. */
  configured: z5.boolean(),
  account: CalendarAccountSchema.nullable()
});
var CalendarConnectUrlSchema = z5.object({
  url: z5.string().url()
});

// src/dto/admin-activity.dto.ts
import { z as z6 } from "zod";
var AdminActivitySchema = z6.object({
  id: z6.string().uuid(),
  adminId: z6.string().uuid(),
  /** Display name of the operator who performed the action. */
  adminName: z6.string(),
  dealershipId: z6.string().uuid().nullable(),
  rooftopId: z6.string().uuid().nullable(),
  entityType: z6.string().nullable(),
  entityAction: z6.string().nullable(),
  entityId: z6.string().uuid().nullable(),
  type: z6.string(),
  data: z6.record(z6.unknown()),
  createdAt: z6.string().datetime()
});
var AdminActivityListSchema = z6.array(AdminActivitySchema);
var AdminActivityDateSchema = z6.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD.");
var AdminActivityPageQuerySchema = z6.object({
  dealershipId: z6.string().uuid().optional(),
  adminId: z6.string().uuid().optional(),
  entityType: z6.string().trim().min(1).max(100).optional(),
  entityAction: z6.string().trim().min(1).max(100).optional(),
  dateFrom: AdminActivityDateSchema.optional(),
  dateTo: AdminActivityDateSchema.optional(),
  page: z6.coerce.number().int().min(1).default(1),
  pageSize: z6.coerce.number().int().min(1).max(100).default(50)
}).refine(
  (query) => !query.dateFrom || !query.dateTo || query.dateFrom <= query.dateTo,
  { message: "From date must be on or before To date.", path: ["dateTo"] }
);
var AdminActivityFacetsSchema = z6.object({
  admins: z6.array(z6.object({ id: z6.string().uuid(), name: z6.string() })),
  entityTypes: z6.array(z6.string()),
  entityActions: z6.array(z6.string())
});
var AdminActivityPageSchema = z6.object({
  items: z6.array(AdminActivitySchema),
  total: z6.number().int().min(0),
  page: z6.number().int().min(1),
  pageSize: z6.number().int().min(1),
  facets: AdminActivityFacetsSchema
});

// src/dto/admin-connections.dto.ts
import { z as z7 } from "zod";
var AdminConnectionKindSchema = z7.enum([
  "channel",
  "integration",
  "telephony",
  "api_key"
]);
var AdminConnectionHealthSchema = z7.enum([
  "healthy",
  "needs_setup",
  "in_progress",
  "paused",
  "error",
  "unknown"
]);
var AdminConnectionLocationScopeSchema = z7.object({
  kind: z7.enum(["dealership", "rooftop"]),
  id: z7.string().uuid().nullable(),
  name: z7.string()
});
var AdminConnectionSchema = z7.object({
  /** Stable, cross-kind row id. */
  id: z7.string(),
  /** The underlying entity id, used only for diagnostics and deep links. */
  connectionId: z7.string().uuid(),
  kind: AdminConnectionKindSchema,
  connectionName: z7.string(),
  provider: z7.string(),
  type: z7.string(),
  locationScope: AdminConnectionLocationScopeSchema,
  health: AdminConnectionHealthSchema,
  /** Plain-language evidence behind `health`; never an invented timestamp. */
  healthDetail: z7.string(),
  /** Last recorded successful provider event/sync. Null means no proof exists. */
  lastSuccessfulAt: z7.string().datetime().nullable(),
  enabled: z7.boolean(),
  /** Canonical admin destination for configuration and diagnostics. */
  detailDestination: z7.string().startsWith("/")
});
var AdminConnectionsResponseSchema = z7.object({
  items: z7.array(AdminConnectionSchema),
  canManage: z7.boolean(),
  generatedAt: z7.string().datetime()
});

// src/dto/admin-search.dto.ts
import { z as z8 } from "zod";
var AdminSearchKindSchema = z8.enum([
  "route",
  "dealership",
  "contact",
  "lead",
  "telephony_account",
  "outreach_prospect"
]);
var AdminSearchQuerySchema = z8.object({
  q: z8.string().trim().max(120).default(""),
  limit: z8.coerce.number().int().min(1).max(8).default(5)
});
var AdminSearchItemSchema = z8.object({
  id: z8.string().min(1),
  kind: AdminSearchKindSchema,
  label: z8.string().min(1).max(160),
  detail: z8.string().min(1).max(240),
  dealershipId: z8.string().uuid().nullable(),
  dealershipName: z8.string().min(1).max(160).nullable(),
  href: z8.string().regex(/^\/(?!\/)/)
});
var AdminSearchGroupSchema = z8.object({
  kind: AdminSearchKindSchema,
  label: z8.string().min(1),
  items: z8.array(AdminSearchItemSchema).max(8),
  total: z8.number().int().nonnegative(),
  isTruncated: z8.boolean()
}).superRefine((group, context) => {
  if (group.items.some((item) => item.kind !== group.kind)) {
    context.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["items"],
      message: "Every result must match its group kind."
    });
  }
  if (group.total < group.items.length) {
    context.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["total"],
      message: "Group total cannot be smaller than the returned results."
    });
  }
  if (group.isTruncated !== group.total > group.items.length) {
    context.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["isTruncated"],
      message: "Truncation must reflect the exact group total."
    });
  }
});
var AdminSearchResponseSchema = z8.object({
  query: z8.string().max(120),
  groups: z8.array(AdminSearchGroupSchema).max(6)
});

// src/dto/admin-contact-directory.dto.ts
import { z as z10 } from "zod";

// src/dto/bdc/vocab.dto.ts
import { z as z9 } from "zod";
var CONTACT_LANGUAGES = [
  { code: "en", label: "English", voiceLocale: "en-US" },
  { code: "es", label: "Spanish", voiceLocale: "es-US" },
  { code: "ru", label: "Russian", voiceLocale: "ru-RU" }
];
var ContactLanguageSchema = z9.enum(
  CONTACT_LANGUAGES.map((l) => l.code)
);
function contactLanguageLabelList() {
  const labels = CONTACT_LANGUAGES.map((l) => l.label);
  if (labels.length <= 1) return labels[0] ?? "English";
  return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
}
function contactLanguageAltLabelList() {
  const labels = CONTACT_LANGUAGES.filter((l) => l.code !== "en").map(
    (l) => l.label
  );
  if (labels.length === 0) return "another supported language";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}
function normalizeContactLanguage(value) {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  for (const lang of CONTACT_LANGUAGES) {
    if (v === lang.code || v === lang.label.toLowerCase() || v === lang.voiceLocale.toLowerCase()) {
      return lang.code;
    }
  }
  return null;
}
function contactLanguageLabel(code) {
  if (code) {
    const found = CONTACT_LANGUAGES.find((l) => l.code === code);
    if (found) return found.label;
  }
  return "English";
}
function contactLanguageVoiceLocale(code) {
  if (!code || code === "en") return null;
  const found = CONTACT_LANGUAGES.find((l) => l.code === code);
  return found ? found.voiceLocale : null;
}
var PreferredCommunicationChannelSchema = z9.enum([
  "voice",
  "sms",
  "email",
  "web",
  "messenger",
  "instagram",
  "whatsapp",
  "telegram"
]);
var ContactChannelTypeSchema = z9.enum([
  "phone",
  "email",
  "web",
  "telegram",
  "messenger",
  "instagram",
  "whatsapp"
]);
var ContactStatusSchema = z9.enum([
  "anonymous",
  "new_lead",
  "engaged",
  "appointment_scheduled",
  "appointment_attended",
  "sold",
  "service_customer",
  "inactive",
  "lost"
]);
var VehicleStatusSchema = z9.enum([
  "available",
  "intransit",
  "pending",
  "sold"
]);
var VehicleConditionSchema = z9.enum(["new", "used", "certified"]);
var VehicleSourceSchema = z9.enum(["feed", "manual", "api"]);
var ChannelTypeSchema = z9.enum([
  "phone",
  // SMS is its own channel even though it shares the underlying
  // E.164 with the `phone` (voice) row — the 10DLC compliance gate
  // fires independently from voice readiness, and the dispatcher /
  // inbox / outbound sender treat them as separate inboxes. See
  // `telephony-numbers.service.ts ensureSmsChannel` for the
  // provisioning lifecycle and the `blocked_compliance` status.
  "sms",
  "email_alias",
  "gmail_oauth",
  "outlook_oauth",
  "email_adf_inbound",
  "web",
  "web_form",
  "facebook_messenger",
  "instagram",
  "whatsapp",
  "telegram",
  "api",
  "aap"
]);
var PROVIDER_MANAGED_CHANNEL_TYPES = [
  "facebook_messenger",
  "instagram",
  "whatsapp",
  "telegram"
];
function isProviderManagedChannelType(type) {
  return PROVIDER_MANAGED_CHANNEL_TYPES.includes(type);
}
var AI_ALWAYS_ON_CHANNEL_TYPES = [
  "web",
  "web_form",
  "api",
  "aap",
  "phone"
];
function isAiAlwaysOnChannelType(type) {
  return AI_ALWAYS_ON_CHANNEL_TYPES.includes(type);
}
var ChannelStatusSchema = z9.enum([
  "draft",
  "provisioning",
  "active",
  "paused",
  "failing",
  "released",
  // Provisioned but cannot send/receive yet because an external
  // compliance gate hasn't cleared. Today this is set on
  // `type='sms'` rows whose phone number is still awaiting 10DLC
  // campaign approval; voice on the same number remains `active`.
  // The UI surfaces an explicit disclaimer; the outbound
  // dispatcher refuses to send.
  "blocked_compliance"
]);
var ChannelCapabilitySchema = z9.enum(["sms", "mms", "voice"]);
var OPERATOR_PAUSABLE_CHANNEL_TYPES = [
  "email_alias",
  "gmail_oauth",
  "outlook_oauth",
  "web",
  "web_form",
  "api",
  "aap",
  "facebook_messenger",
  "instagram",
  "whatsapp",
  "telegram"
];
function isOperatorPausableChannelType(type) {
  return OPERATOR_PAUSABLE_CHANNEL_TYPES.includes(type);
}
function channelStatusActions(type, status) {
  if (!isOperatorPausableChannelType(type)) return [];
  switch (status) {
    case "active":
      return [{ action: "pause", label: "Pause", target: "paused" }];
    case "paused":
      return [{ action: "activate", label: "Activate", target: "active" }];
    case "failing":
      return [
        { action: "activate", label: "Activate", target: "active" },
        { action: "pause", label: "Pause", target: "paused" }
      ];
    default:
      return [];
  }
}
var LeadPrimaryChannelSchema = z9.enum([
  "sms",
  "voice",
  "email",
  "web",
  "web_form",
  "messenger",
  "instagram",
  "whatsapp",
  "telegram",
  "aap"
]);
var ActivityDirectionSchema = z9.enum(["inbound", "outbound", "internal", "system"]);
var ActivityActorTypeSchema = z9.enum(["contact", "user", "ai", "system"]);
var DeliveryStatusSchema = z9.enum([
  "queued",
  // Transient claim state: the outbox consumer compare-and-swaps
  // `queued` → `sending` before calling the provider so SQS
  // at-least-once redeliveries can't double-send. Released back to
  // `queued` on retryable errors; otherwise ends at `sent`/`failed`.
  "sending",
  "sent",
  "delivered",
  "failed",
  // R5 (SES feedback): terminal deliverability outcomes — a hard/soft
  // bounce, or the recipient marking the mail as spam. Already in the
  // DB CHECK vocabulary (migration 2100000000152).
  "bounced",
  "complained",
  "received",
  "read",
  // C2 (approval mode): an AI reply persisted as a draft awaiting a
  // human on an approve-first channel — never handed to the outbox
  // until approved ('pending_approval' → 'queued'), or declined by a
  // human ('rejected', terminal). CHECK extended in migration
  // 2100000000175.
  "pending_approval",
  "rejected"
]);
var AppointmentTypeSchema = z9.enum([
  "test_drive",
  "trade_appraisal",
  "delivery",
  "sales",
  "service",
  "parts",
  "phone",
  "virtual",
  "other"
]);
var AppointmentStatusSchema = z9.enum([
  "unconfirmed",
  "pending_customer",
  "confirmed",
  "checked_in",
  "in_service",
  "ready_for_pickup",
  "completed",
  "cancelled",
  "no_show",
  "rescheduled",
  "outcome_unknown"
]);
var AppointmentTransportTypeSchema = z9.enum(["in_person", "phone", "video"]);

// src/dto/admin-contact-directory.dto.ts
var AdminContactDirectorySortSchema = z10.enum([
  "activity_desc",
  "activity_asc",
  "name_asc",
  "name_desc",
  "created_desc",
  "created_asc",
  "status_asc",
  "status_desc",
  "owner_asc",
  "owner_desc",
  "source_asc",
  "source_desc"
]);
var AdminContactDirectoryQuerySchema = z10.object({
  page: z10.coerce.number().int().min(1).default(1),
  pageSize: z10.coerce.number().int().min(1).max(100).default(25),
  search: z10.string().trim().max(200).optional(),
  status: ContactStatusSchema.optional(),
  assignedUserId: z10.string().uuid().optional(),
  unassigned: z10.enum(["true"]).optional(),
  source: z10.string().trim().min(1).max(120).optional(),
  preferredChannel: PreferredCommunicationChannelSchema.optional(),
  businessAccount: z10.enum(["true", "false"]).optional(),
  sort: AdminContactDirectorySortSchema.default("activity_desc")
}).superRefine((query, context) => {
  if (query.assignedUserId && query.unassigned === "true") {
    context.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["unassigned"],
      message: "Choose either an owner or Unassigned, not both"
    });
  }
});

// src/dto/billing.dto.ts
import { z as z25 } from "zod";

// src/dto/bdc/contacts.dto.ts
import { z as z12 } from "zod";

// src/dto/bdc/shared.dto.ts
import { z as z11 } from "zod";
var OptionalTextSchema = z11.string().trim().min(1).nullable().optional();
var OptionalEmailSchema2 = z11.string().email().nullable().optional();
var OptionalUrlSchema2 = z11.string().url().nullable().optional();
var DateSchema = z11.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var JsonObjectSchema = z11.record(z11.unknown());
var JsonArraySchema = z11.array(z11.unknown());
var PageNumberSchema = z11.number().int().positive();

// src/dto/bdc/contacts.dto.ts
var LeadStatusSchema = z12.enum(["new", "working", "appointment", "won", "lost"]);
var ContactTopOpenLeadSchema = z12.object({
  id: z12.string().uuid(),
  status: z12.enum(["new", "working", "appointment"]),
  interest: z12.string().nullable(),
  interestVehicle: z12.object({
    vin: z12.string(),
    year: z12.number().int().nullable(),
    make: z12.string().nullable(),
    model: z12.string().nullable(),
    trim: z12.string().nullable(),
    status: VehicleStatusSchema
  }).nullable()
});
var ContactSchema = z12.object({
  id: z12.string().uuid(),
  dealershipId: z12.string().uuid(),
  status: ContactStatusSchema,
  firstName: z12.string().nullable(),
  lastName: z12.string().nullable(),
  avatarUrl: z12.string().nullable(),
  businessAccount: z12.boolean(),
  businessName: z12.string().nullable(),
  primaryEmail: z12.string().email().nullable(),
  primaryPhone: z12.string().nullable(),
  // Last-resort display label for contacts with no name/email/phone — a
  // short snippet of their first inbound message — so the UI shows
  // something identifying instead of "Anonymous". Computed at read time on
  // list/detail endpoints; null when the contact has no inbound message.
  fallbackLabel: z12.string().nullable(),
  // Server-computed canonical display label (businessName → first+last →
  // email → phone → socialHandle → fallbackLabel → "Anonymous"). Always
  // populated by backend mappers; nullable/optional here for frontend
  // optimistic constructions that may not have the full contact available.
  displayName: z12.string().nullable().optional(),
  state: z12.string().nullable(),
  city: z12.string().nullable(),
  zip: z12.string().nullable(),
  addressLine1: z12.string().nullable(),
  timezone: z12.string().nullable(),
  preferredLanguage: ContactLanguageSchema.nullable(),
  preferredChannel: PreferredCommunicationChannelSchema.nullable(),
  birthDate: DateSchema.nullable(),
  assignedUserId: z12.string().uuid().nullable(),
  assignedAt: z12.string().datetime().nullable(),
  lastActivityAt: z12.string().datetime().nullable(),
  leadSource: z12.string().nullable(),
  utmSource: z12.string().nullable(),
  utmMedium: z12.string().nullable(),
  utmCampaign: z12.string().nullable(),
  optoutEmail: z12.boolean(),
  optoutSms: z12.boolean(),
  optoutVoice: z12.boolean(),
  aiAutopilot: z12.boolean(),
  notes: z12.string().nullable(),
  internalNotes: z12.string().nullable(),
  createdAt: z12.string().datetime(),
  updatedAt: z12.string().datetime(),
  // Timestamp of the most recent inbound or outbound message (sms/email/social).
  lastMessageAt: z12.string().datetime().nullable(),
  // Per-channel message counts (read-time). Enriched by the list endpoint.
  channelCounts: z12.object({
    phone: z12.number().int().nonnegative(),
    sms: z12.number().int().nonnegative(),
    email: z12.number().int().nonnegative(),
    social: z12.number().int().nonnegative()
  }),
  // Total activity count across all leads for this contact. Enriched by the list endpoint.
  activityCount: z12.number().int().nonnegative(),
  // Vehicles the contact is shopping for (joined to inventory where linked).
  vehiclesOfInterest: z12.array(
    z12.object({
      vin: z12.string().nullable(),
      year: z12.number().int().nullable(),
      make: z12.string().nullable(),
      model: z12.string().nullable(),
      trim: z12.string().nullable(),
      status: VehicleStatusSchema.nullable()
    })
  ),
  // Vehicles the contact owns (free-text from contact_vehicles).
  vehiclesOwning: z12.array(
    z12.object({
      year: z12.number().int().nullable(),
      make: z12.string().nullable(),
      model: z12.string().nullable(),
      trim: z12.string().nullable()
    })
  ),
  // All lead pipeline statuses this contact currently holds.
  leadStatuses: z12.array(LeadStatusSchema),
  // Highest-priority open lead and total open count for directory scanning.
  // Ranking is defined by the contacts-list service, never reconstructed by
  // clients from the lossy aggregate fields above.
  topOpenLead: ContactTopOpenLeadSchema.nullable().default(null),
  openLeadCount: z12.number().int().nonnegative().default(0),
  // Most-recent lead id per channel bucket — populated by the contacts-list
  // enrichment pass (one batched DISTINCT ON query, no per-row fetch).
  // Null when the contact has no lead on that channel; retained for clients
  // that need a channel-specific conversation deep link.
  recentLeadByChannel: z12.object({
    phone: z12.string().uuid().nullable(),
    sms: z12.string().uuid().nullable(),
    email: z12.string().uuid().nullable(),
    social: z12.string().uuid().nullable()
  })
});
var CreateContactSchema = z12.object({
  dealershipId: z12.string().uuid(),
  status: ContactStatusSchema.default("anonymous"),
  firstName: z12.string().trim().min(1).max(120).nullable().optional(),
  lastName: z12.string().trim().min(1).max(120).nullable().optional(),
  businessAccount: z12.boolean().default(false),
  businessName: OptionalTextSchema,
  primaryEmail: OptionalEmailSchema2,
  primaryPhone: OptionalPhoneE164Schema,
  state: OptionalStateOrProvinceCodeSchema,
  city: z12.string().trim().min(1).max(120).nullable().optional(),
  zip: OptionalZipOrPostalCodeSchema,
  addressLine1: z12.string().trim().min(1).max(200).nullable().optional(),
  timezone: OptionalTextSchema,
  preferredLanguage: ContactLanguageSchema.nullable().optional(),
  preferredChannel: PreferredCommunicationChannelSchema.nullable().optional(),
  birthDate: DateSchema.nullable().optional(),
  assignedUserId: z12.string().uuid().nullable().optional(),
  assignedAt: z12.string().datetime().nullable().optional(),
  leadSource: OptionalTextSchema,
  utmSource: OptionalTextSchema,
  utmMedium: OptionalTextSchema,
  utmCampaign: OptionalTextSchema,
  optoutEmail: z12.boolean().default(false),
  optoutSms: z12.boolean().default(false),
  optoutVoice: z12.boolean().default(false),
  aiAutopilot: z12.boolean().optional(),
  notes: z12.string().nullable().optional(),
  internalNotes: z12.string().nullable().optional()
});
var UpdateContactSchema = CreateContactSchema.omit({ dealershipId: true }).partial();
var ImportContactRowSchema = z12.object({
  firstName: z12.string().trim().max(120).optional(),
  lastName: z12.string().trim().max(120).optional(),
  email: z12.string().trim().max(320).optional(),
  phone: z12.string().trim().max(40).optional(),
  optedOutSms: z12.boolean().optional(),
  optedOutEmail: z12.boolean().optional(),
  tags: z12.array(z12.string().trim().min(1).max(60)).max(20).optional()
});
var ImportContactsSchema = z12.object({
  rows: z12.array(ImportContactRowSchema).min(1).max(1e4),
  /** Where the batch came from (e.g. "csv-import") — stamped as the
      contacts' leadSource and the consent-event evidence. */
  source: z12.string().trim().min(1).max(80)
});
var ImportContactsResultSchema = z12.object({
  created: z12.number().int().nonnegative(),
  mergedIntoExisting: z12.number().int().nonnegative(),
  skipped: z12.array(
    z12.object({
      /** 1-based position within the submitted `rows` array. */
      row: z12.number().int().positive(),
      reason: z12.string()
    })
  )
});
var ContactChannelSchema = z12.object({
  id: z12.string().uuid(),
  dealershipId: z12.string().uuid(),
  contactId: z12.string().uuid(),
  type: ContactChannelTypeSchema,
  value: z12.string().nullable(),
  externalId: z12.string(),
  label: z12.string().nullable(),
  isPrimary: z12.boolean(),
  isMarketingOptedIn: z12.boolean(),
  transactionalOptedOutAt: z12.string().datetime().nullable(),
  verifiedAt: z12.string().datetime().nullable(),
  /**
   * R5 deliverability suppression: stamped when a send to this email
   * identity hard-bounced or drew a spam complaint — senders refuse it
   * from then on, and the UI should say so instead of looking sendable.
   */
  suppressedAt: z12.string().datetime().nullable(),
  createdAt: z12.string().datetime(),
  updatedAt: z12.string().datetime()
});
var ContactChannelInputObject = z12.object({
  dealershipId: z12.string().uuid(),
  contactId: z12.string().uuid(),
  type: ContactChannelTypeSchema,
  value: OptionalTextSchema,
  externalId: z12.string().trim().min(1),
  label: OptionalTextSchema,
  isPrimary: z12.boolean().default(false),
  isMarketingOptedIn: z12.boolean().default(false),
  transactionalOptedOutAt: z12.string().datetime().nullable().optional(),
  verifiedAt: z12.string().datetime().nullable().optional()
});
function refineContactChannelValue(data, ctx) {
  for (const field of ["value", "externalId"]) {
    const raw = data[field];
    if (!raw) continue;
    if (data.type === "email" && !z12.string().email().safeParse(raw).success) {
      ctx.addIssue({
        code: z12.ZodIssueCode.custom,
        path: [field],
        message: "Enter a valid email address."
      });
    }
    if ((data.type === "phone" || data.type === "whatsapp") && !PhoneE164Schema.safeParse(raw).success) {
      ctx.addIssue({
        code: z12.ZodIssueCode.custom,
        path: [field],
        message: "Use E.164 format, e.g. +15551234567."
      });
    }
  }
}
var CreateContactChannelSchema = ContactChannelInputObject.superRefine(refineContactChannelValue);
var CreateContactChannelBodySchema = ContactChannelInputObject.omit({
  dealershipId: true
}).superRefine(refineContactChannelValue);
var UpdateContactChannelSchema = ContactChannelInputObject.omit({
  dealershipId: true,
  contactId: true
}).partial().superRefine(refineContactChannelValue);

// src/dto/bdc/inventory.dto.ts
import { z as z13 } from "zod";
var InventoryVehicleSchema = z13.object({
  id: z13.string().uuid(),
  dealershipId: z13.string().uuid(),
  rooftopId: z13.string().uuid(),
  // `null` for manually-created vehicles (no inventory feed brought
  // them in). Feed-managed vehicles carry the source import's id.
  importId: z13.string().uuid().nullable(),
  vin: z13.string(),
  stock: z13.string().nullable(),
  status: VehicleStatusSchema,
  condition: VehicleConditionSchema.nullable(),
  year: z13.number().int().nullable(),
  make: z13.string().nullable(),
  model: z13.string().nullable(),
  trim: z13.string().nullable(),
  body: z13.string().nullable(),
  color: z13.string().nullable(),
  interiorColor: z13.string().nullable(),
  mileage: z13.number().int().nullable(),
  driveTrain: z13.string().nullable(),
  transmission: z13.string().nullable(),
  engine: z13.string().nullable(),
  fuel: z13.string().nullable(),
  doors: z13.number().int().nullable(),
  seats: z13.number().int().nullable(),
  features: z13.array(z13.string()).nullable(),
  msrp: z13.number().nullable(),
  price: z13.number().nullable(),
  listPrice: z13.number().nullable(),
  offeredPrice: z13.number().nullable(),
  photos: z13.array(z13.string()).nullable(),
  vdpUrl: z13.string().url().nullable(),
  description: z13.string().nullable(),
  source: VehicleSourceSchema.nullable(),
  lastSyncedAt: z13.string().datetime().nullable(),
  hash: z13.string().nullable(),
  stockDate: z13.string().datetime(),
  soldDate: z13.string().datetime().nullable(),
  /**
   * I8 — whole days since `stockDate` (until `soldDate` for sold
   * rows), computed server-side at read time. The manager's lot-age
   * lens and the input for aging cadences.
   */
  daysOnLot: z13.number().int(),
  createdAt: z13.string().datetime(),
  updatedAt: z13.string().datetime()
});
var CreateInventoryVehicleSchema = z13.object({
  dealershipId: z13.string().uuid(),
  rooftopId: z13.string().uuid(),
  vin: z13.string().trim().min(1),
  stock: OptionalTextSchema,
  status: VehicleStatusSchema.default("available"),
  condition: VehicleConditionSchema.nullable().optional(),
  year: z13.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  body: OptionalTextSchema,
  color: OptionalTextSchema,
  interiorColor: OptionalTextSchema,
  mileage: z13.number().int().nullable().optional(),
  driveTrain: OptionalTextSchema,
  transmission: OptionalTextSchema,
  engine: OptionalTextSchema,
  fuel: OptionalTextSchema,
  doors: z13.number().int().nonnegative().nullable().optional(),
  seats: z13.number().int().nonnegative().nullable().optional(),
  features: z13.array(z13.string().trim().min(1)).nullable().optional(),
  msrp: z13.number().nullable().optional(),
  price: z13.number().nullable().optional(),
  listPrice: z13.number().nullable().optional(),
  offeredPrice: z13.number().nullable().optional(),
  photos: z13.array(z13.string()).nullable().optional(),
  vdpUrl: OptionalUrlSchema2,
  description: OptionalTextSchema,
  source: VehicleSourceSchema.nullable().optional(),
  lastSyncedAt: z13.string().datetime().nullable().optional()
});
var UpdateInventoryVehicleSchema = CreateInventoryVehicleSchema.omit({
  dealershipId: true,
  vin: true
}).partial();
var ManualMoneySchema = z13.number().nullable().optional().transform((v) => v === void 0 ? void 0 : v == null || v <= 0 ? null : Math.floor(v));
var ManualVehicleCreateSchema = CreateInventoryVehicleSchema.omit({
  dealershipId: true,
  source: true,
  lastSyncedAt: true
}).extend({
  vin: VinSchema,
  msrp: ManualMoneySchema,
  price: ManualMoneySchema,
  listPrice: ManualMoneySchema,
  offeredPrice: ManualMoneySchema
});
var ManualVehicleUpdateSchema = ManualVehicleCreateSchema.omit({
  vin: true
}).partial();

// src/dto/bdc/channels.dto.ts
import { z as z16 } from "zod";

// src/dto/integration.dto.ts
import { z as z14 } from "zod";
var IntegrationProviderSchema = z14.enum([
  "spaceauto",
  "adf",
  "vinsolutions",
  "elead"
]);
var IntegrationProviderLabels = {
  spaceauto: "Space Auto",
  adf: "ADF Lead",
  vinsolutions: "VinSolutions",
  elead: "CDK Elead"
};
var CERTIFICATION_PENDING_PROVIDERS = /* @__PURE__ */ new Set(["vinsolutions", "elead"]);
var IntegrationStatusSchema = z14.enum([
  "synced",
  "syncing",
  "backfilling",
  "errored"
]);
var IntegrationBackfillStateSchema = z14.object({
  phase: z14.enum(["contacts", "activities", "appointments", "tradeins"]),
  offset: z14.number().int().min(0),
  // Total is null until the first searchCustomers response tells us
  // how many rows exist; the UI shows "loading…" in the meantime.
  total: z14.number().int().min(0).nullable(),
  startedAt: z14.string().datetime()
});
var IntegrationLocationsMappingSchema = z14.record(z14.string().uuid(), z14.string().trim().min(1).max(255)).nullable();
var IntegrationSchema = z14.object({
  id: z14.string().uuid(),
  dealershipId: z14.string().uuid(),
  /**
   * Location scope. Null = the whole dealership. For ADF this gates
   * which events emit a lead (only the scoped rooftop's); for Space
   * Auto it picks where newly imported contacts land.
   */
  rooftopId: z14.string().uuid().nullable(),
  provider: IntegrationProviderSchema,
  tokenMasked: z14.string(),
  notes: z14.string().nullable(),
  includeUnauthorized: z14.boolean(),
  /** On/off switch — `false` pauses both pull and push without losing
   *  the linker rows. `status` reflects the last sync independently. */
  enabled: z14.boolean(),
  status: IntegrationStatusSchema,
  lastPulledAt: z14.string().datetime().nullable(),
  lastPulledError: z14.string().nullable(),
  backfillState: IntegrationBackfillStateSchema.nullable(),
  locationsMapping: IntegrationLocationsMappingSchema,
  /**
   * Optional historical-sync start point — when set, the pull cron
   * runs a one-time backfill of every SA customer whose
   * `createdAt > startSyncAt`, ordered by `createdAt asc`. UI surfaces
   * this so the operator can see what they configured.
   */
  startSyncAt: z14.string().datetime().nullable(),
  /** Set by the backend when the historical backfill finishes. */
  historicalSyncCompletedAt: z14.string().datetime().nullable(),
  createdAt: z14.string().datetime(),
  updatedAt: z14.string().datetime()
});
var IntegrationListSchema = z14.array(IntegrationSchema);
var CreateIntegrationSchema = z14.object({
  provider: IntegrationProviderSchema,
  token: z14.string().trim().min(1).max(512),
  /** Location scope — a rooftop id, or null/absent for the whole dealership. */
  rooftopId: z14.string().uuid().nullable().optional(),
  notes: z14.string().trim().max(2e3).optional(),
  includeUnauthorized: z14.boolean().default(false),
  locationsMapping: IntegrationLocationsMappingSchema.optional(),
  /**
   * ISO 8601 datetime. When set, the pull cron does a one-time
   * historical backfill of every SA customer whose
   * `createdAt > startSyncAt`. Default null = no backfill.
   */
  startSyncAt: z14.string().datetime().nullable().optional()
}).superRefine((data, ctx) => {
  if (data.provider === "adf" && !z14.string().email().safeParse(data.token).success) {
    ctx.addIssue({
      path: ["token"],
      code: "custom",
      message: "Enter a valid recipient email."
    });
  }
});
var UpdateIntegrationSchema = z14.object({
  token: z14.string().trim().min(1).max(512).optional(),
  /** Location scope — a rooftop id, or null to widen back to the whole dealership. */
  rooftopId: z14.string().uuid().nullable().optional(),
  notes: z14.string().trim().max(2e3).optional(),
  includeUnauthorized: z14.boolean().optional(),
  enabled: z14.boolean().optional(),
  locationsMapping: IntegrationLocationsMappingSchema.optional(),
  /**
   * Re-setting `startSyncAt` resets the historical-sync watermark
   * so the cron re-runs the backfill from the new start point.
   * Setting it to `null` clears the watermark and disables backfill.
   */
  startSyncAt: z14.string().datetime().nullable().optional()
});
var IntegrationDeliveryStatusSchema = z14.enum([
  "sent",
  "failed",
  "bounced"
]);
var IntegrationDeliverySchema = z14.object({
  id: z14.string().uuid(),
  integrationId: z14.string().uuid(),
  provider: IntegrationProviderSchema,
  eventId: z14.string().uuid(),
  status: IntegrationDeliveryStatusSchema,
  providerMessageId: z14.string().nullable(),
  contactId: z14.string().uuid().nullable(),
  detail: z14.record(z14.string(), z14.unknown()).nullable(),
  createdAt: z14.string().datetime()
});
var VerifyIntegrationTokenRequestSchema = z14.object({
  provider: IntegrationProviderSchema,
  token: z14.string().trim().min(1).max(512)
});
var VerifyIntegrationLocationSchema = z14.object({
  id: z14.string(),
  name: z14.string(),
  timezone: z14.string().nullable(),
  city: z14.string().nullable(),
  state: z14.string().nullable()
});
var VerifyIntegrationTokenResponseSchema = z14.object({
  ok: z14.literal(true),
  dealership: z14.object({
    id: z14.string(),
    name: z14.string().nullable(),
    locations: z14.array(VerifyIntegrationLocationSchema)
  })
});

// src/dto/bdc/ai.dto.ts
import { z as z15 } from "zod";
var AiAgentStatusSchema = z15.enum(["draft", "active", "paused"]);
var AiAgentRespondToSchema = z15.enum([
  "assigned_only",
  "assigned_and_unassigned",
  "rooftop_all",
  "group_all"
]);
var AiAgentResponseSplitModeSchema = z15.enum(["single", "multiple"]);
var AiAgentSchema = z15.object({
  id: z15.string().uuid(),
  dealershipId: z15.string().uuid(),
  userId: z15.string().uuid(),
  name: z15.string(),
  description: z15.string().nullable(),
  model: z15.string(),
  temperature: z15.number().nullable(),
  /** Dealership-wide Knowledge & Behavior (was "system prompt"). */
  knowledgeAndBehavior: z15.string(),
  /** Layer the code-owned base BDC-manager persona beneath the K&B. */
  useBasePrompt: z15.boolean(),
  persona: JsonObjectSchema.nullable(),
  config: JsonObjectSchema.nullable(),
  status: AiAgentStatusSchema,
  isStreaming: z15.boolean(),
  allowInternet: z15.boolean(),
  // Reply delays + silence period (migration 210), the AI on/off
  // toggle, and the reply scope (migration 211) are PER CHANNEL
  // (ChannelSchema) — no behavioral fields on the agent.
  defaultSnoozeSeconds: z15.number().int().min(0),
  responseSplitMode: AiAgentResponseSplitModeSchema,
  // Voice config — admin-only. Null means "use env defaults."
  voiceListenModel: z15.string().nullable(),
  voiceSpeakModel: z15.string().nullable(),
  voiceThinkProvider: z15.string().nullable(),
  voiceThinkModel: z15.string().nullable(),
  // Per-channel persona + tool segregation — admin-only. The Phone
  // (voice) and SMS channels each carry their own K&B overlay
  // (appended on top of the shared knowledgeAndBehavior) and their own
  // tool exclusion list. Null = no overlay / code-default tools.
  voicePrompt: z15.string().nullable(),
  smsPrompt: z15.string().nullable(),
  voiceExcludedTools: z15.array(z15.string()).nullable(),
  smsExcludedTools: z15.array(z15.string()).nullable(),
  createdAt: z15.string().datetime(),
  updatedAt: z15.string().datetime()
});
var CreateAiAgentSchema = z15.object({
  dealershipId: z15.string().uuid(),
  userId: z15.string().uuid(),
  name: z15.string().trim().min(1).max(120),
  description: OptionalTextSchema,
  // Must be "<provider>:<modelId>" — the contract parseModelString
  // enforces at run time (@lumika/ai-agent). Guarding here turns a
  // silently-broken agent into a 400 at the API boundary instead of a
  // swallowed run-time WARN. We validate only the STRUCTURAL shape
  // (non-empty provider, ':', non-empty modelId), mirroring
  // parseModelString's own rule. The provider allowlist
  // (SUPPORTED_PROVIDERS) stays single-sourced in @lumika/ai-agent —
  // packages/shared cannot import that ESM-only package, and it is
  // still enforced at run time; duplicating the list here would risk
  // drift.
  model: z15.string().trim().regex(
    /^[^\s:]+:.+$/,
    'model must be "<provider>:<modelId>" (e.g. "openai:gpt-4o", "anthropic:claude-sonnet-4-5")'
  ),
  // 0..1 is the one range every provider pi-ai routes to accepts —
  // anthropic-messages endpoints (incl. Fireworks-hosted models) hard
  // 400 on temperature > 1. The knob used to be silently dropped, so
  // unbounded garbage was harmless; now that it reaches the provider
  // request, reject it at the API boundary. The runtime additionally
  // clamps legacy out-of-range rows (see @lumika/ai-agent buildAgent).
  temperature: z15.number().min(0).max(1).nullable().optional(),
  knowledgeAndBehavior: z15.string().trim().min(1).max(2e4),
  useBasePrompt: z15.boolean().default(true),
  persona: JsonObjectSchema.nullable().optional(),
  config: JsonObjectSchema.nullable().optional(),
  status: AiAgentStatusSchema.default("active"),
  isStreaming: z15.boolean().default(false),
  allowInternet: z15.boolean().default(false),
  defaultSnoozeSeconds: z15.number().int().min(0).default(0),
  responseSplitMode: AiAgentResponseSplitModeSchema.default("multiple"),
  voiceListenModel: z15.string().trim().min(1).nullable().optional(),
  voiceSpeakModel: z15.string().trim().min(1).nullable().optional(),
  voiceThinkProvider: z15.string().trim().min(1).nullable().optional(),
  voiceThinkModel: z15.string().trim().min(1).nullable().optional(),
  // Per-channel persona + tool segregation — admin-only. Empty/omitted
  // means no overlay (voice/sms run with shared K&B + code-default
  // tools); a tool-exclusion array (including []) is honoured verbatim.
  voicePrompt: z15.string().trim().max(2e4).nullable().optional(),
  smsPrompt: z15.string().trim().max(2e4).nullable().optional(),
  voiceExcludedTools: z15.array(z15.string().trim().min(1)).nullable().optional(),
  smsExcludedTools: z15.array(z15.string().trim().min(1)).nullable().optional()
});
var UpdateAiAgentSchema = CreateAiAgentSchema.pick({
  name: true,
  description: true,
  model: true,
  temperature: true,
  knowledgeAndBehavior: true,
  useBasePrompt: true,
  persona: true,
  config: true,
  status: true,
  isStreaming: true,
  allowInternet: true,
  defaultSnoozeSeconds: true,
  responseSplitMode: true,
  voiceListenModel: true,
  voiceSpeakModel: true,
  voiceThinkProvider: true,
  voiceThinkModel: true,
  voicePrompt: true,
  smsPrompt: true,
  voiceExcludedTools: true,
  smsExcludedTools: true
}).partial();
var AgentRunOutcomeSchema = z15.enum([
  "replied",
  "silence",
  "escalated",
  "error",
  "draft"
]);
var AgentRunToolCallSchema = z15.object({
  name: z15.string(),
  durationMs: z15.number().int().nonnegative(),
  ok: z15.boolean(),
  // Human-readable summary — present for CLIENT_VISIBLE_TOOL_NAMES;
  // internal tools show name + duration only.
  summary: z15.string().nullable().optional()
});
var AgentRunSchema = z15.object({
  id: z15.string().uuid(),
  dealershipId: z15.string().uuid(),
  leadId: z15.string().uuid().nullable(),
  activityId: z15.string().uuid().nullable(),
  channelType: z15.string().nullable(),
  model: z15.string(),
  provider: z15.string(),
  inputTokens: z15.number().int().nonnegative(),
  outputTokens: z15.number().int().nonnegative(),
  // Coerced: pg numeric serializes as a string.
  costUsd: z15.coerce.number().nullable(),
  durationMs: z15.number().int().nonnegative(),
  outcome: AgentRunOutcomeSchema,
  toolCalls: z15.array(AgentRunToolCallSchema),
  error: z15.string().nullable(),
  createdAt: z15.string().datetime()
});
var AgentRunListSchema = z15.array(AgentRunSchema);
var RejectActivityApprovalSchema = z15.object({
  reason: z15.string().trim().min(1).max(500).optional()
});
var AiBudgetSummarySchema = z15.object({
  monthUsd: z15.coerce.number(),
  budgetUsd: z15.coerce.number().nullable(),
  exhausted: z15.boolean()
});
var RejectAiDraftSchema = z15.object({
  reason: z15.string().trim().max(500).optional()
});
var SkillContentSchema = z15.string().trim().min(200, "Skill content must be at least 200 characters.");
var AiSkillSchema = z15.object({
  id: z15.string().uuid(),
  dealershipId: z15.string().uuid(),
  rooftopId: z15.string().uuid().nullable(),
  name: z15.string(),
  description: z15.string().nullable(),
  content: z15.string(),
  isEnabled: z15.boolean(),
  createdAt: z15.string().datetime(),
  updatedAt: z15.string().datetime()
});
var CreateAiSkillSchema = z15.object({
  dealershipId: z15.string().uuid(),
  rooftopId: z15.string().uuid().nullable().optional(),
  name: z15.string().trim().min(1).max(120),
  description: OptionalTextSchema,
  content: SkillContentSchema,
  isEnabled: z15.boolean().default(true)
});
var UpdateAiSkillSchema = z15.object({
  rooftopId: z15.string().uuid().nullable(),
  name: z15.string().trim().min(1),
  description: OptionalTextSchema,
  content: SkillContentSchema,
  isEnabled: z15.boolean()
}).partial();
var AiRooftopPromptSchema = z15.object({
  id: z15.string().uuid(),
  aiAgentId: z15.string().uuid(),
  rooftopId: z15.string().uuid(),
  knowledgeAndBehavior: z15.string(),
  createdAt: z15.string().datetime(),
  updatedAt: z15.string().datetime()
});
var AiRooftopPromptListSchema = z15.array(AiRooftopPromptSchema);
var UpsertAiRooftopPromptSchema = z15.object({
  rooftopId: z15.string().uuid(),
  knowledgeAndBehavior: z15.string().trim().min(1).max(2e4)
});
var AiAgentChannelAssignmentSchema = z15.object({
  id: z15.string().uuid(),
  dealershipId: z15.string().uuid(),
  rooftopId: z15.string().uuid(),
  aiAgentId: z15.string().uuid(),
  channelId: z15.string().uuid(),
  medium: z15.string().nullable(),
  isPrimary: z15.boolean(),
  priority: z15.number().int().nullable(),
  createdAt: z15.string().datetime(),
  updatedAt: z15.string().datetime()
});
var AiAssignmentMediumSchema = z15.enum(["sms", "voice"]);
var CreateAiAgentChannelAssignmentSchema = z15.object({
  dealershipId: z15.string().uuid(),
  rooftopId: z15.string().uuid(),
  aiAgentId: z15.string().uuid(),
  channelId: z15.string().uuid(),
  medium: AiAssignmentMediumSchema.nullable().optional(),
  isPrimary: z15.boolean().default(false),
  priority: z15.number().int().nullable().optional()
});
var AiAgentSkillSchema = z15.object({
  id: z15.string().uuid(),
  dealershipId: z15.string().uuid(),
  aiAgentId: z15.string().uuid(),
  aiSkillId: z15.string().uuid(),
  createdAt: z15.string().datetime()
});
var CreateAiAgentSkillSchema = z15.object({
  aiSkillId: z15.string().uuid()
});
var AiAgentDetailSchema = z15.object({
  agent: AiAgentSchema,
  channelAssignments: z15.array(AiAgentChannelAssignmentSchema),
  skillGrants: z15.array(AiAgentSkillSchema)
});
var AiAgentDispatchSkipReasonSchema = z15.enum([
  "agent_not_active",
  "conversation_closed",
  "contact_opted_out",
  "ai_autopilot_off",
  // The channel's AI toggle (`channels.ai_enabled`, successor of the
  // C2 autonomy dial — the value is kept stable) is off, so the AI
  // stays silent on this lead's channel.
  "ai_autonomy_off",
  "ai_suppressed_window",
  "channel_inactive",
  "routing_rule",
  "human_already_responded"
]);
var AiAgentDispatchDecisionSchema = z15.object({
  shouldRespond: z15.boolean(),
  scheduleAt: z15.string().datetime(),
  skipReason: AiAgentDispatchSkipReasonSchema.nullable()
});
var AiAgentDispatchCheckRequestSchema = z15.object({
  leadId: z15.string().uuid()
});

// src/dto/bdc/channels.dto.ts
var ChannelSchema = z16.object({
  id: z16.string().uuid(),
  dealershipId: z16.string().uuid(),
  rooftopId: z16.string().uuid().nullable(),
  type: ChannelTypeSchema,
  name: z16.string(),
  externalAccountId: z16.string().nullable(),
  providerResourceId: z16.string().nullable(),
  externalAccountLabel: z16.string().nullable(),
  /** CDN URL of the connected account's avatar, or null. */
  externalAccountAvatarUrl: z16.string().nullable(),
  /**
   * Computed (read-only) link that opens the connected account/inbox:
   * `m.me/<page>` for Messenger, `ig.me/m/<user>` for Instagram,
   * `wa.me/<e164>` for WhatsApp, `t.me/<bot>` for Telegram,
   * `mailto:<addr>` for email channels. Null when the channel type
   * has no meaningful external link (api/web/etc.) or the identifier
   * isn't known yet.
   */
  deepLink: z16.string().nullable(),
  e164: z16.string().nullable(),
  capabilities: z16.array(ChannelCapabilitySchema).nullable(),
  forwardingNumber: z16.string().nullable(),
  voicemailEnabled: z16.boolean().nullable(),
  /** Phone-only: multi-language voice mode (en/es/ru). Null elsewhere. */
  multilingual: z16.boolean().nullable(),
  /**
   * email_alias addressing. The inbox is
   * `<emailLocalPart>@<emailSubdomain>.<zone>`; both are null on every
   * other channel type.
   */
  emailLocalPart: z16.string().nullable(),
  emailSubdomain: z16.string().nullable(),
  auth: JsonObjectSchema,
  config: JsonObjectSchema,
  /**
   * "When does the AI respond?" — per-channel reply timing, in seconds,
   * keyed on who owns the lead. The delay is a "give humans first dibs"
   * window; a human reply within it cancels the AI's pending turn.
   */
  replyDelayAssignedSeconds: z16.number().int().min(0),
  replyDelayUnassignedSeconds: z16.number().int().min(0),
  replyDelayOtherAssigneeSeconds: z16.number().int().min(0),
  /**
   * "Silence period": how long the AI stays paused on a lead after a
   * human takes over on this channel.
   */
  aiSuppressionSeconds: z16.number().int().min(0),
  /**
   * "How much can the AI do?" on this channel, collapsed to on/off.
   * When false, the AI never answers customers here. Locked ON for
   * `AI_ALWAYS_ON_CHANNEL_TYPES` (web/web_form/api/aap/phone) — the
   * dispatcher ignores a stored false there.
   */
  aiEnabled: z16.boolean(),
  /**
   * "Whom does the AI respond to?" on this channel — the AI user's
   * reply permission, same four levels as a teammate's inbox
   * visibility.
   */
  respondTo: AiAgentRespondToSchema,
  /**
   * When true, new leads on this channel and existing open
   * leads that currently have no assignee are routed to the
   * dealership's AI user instead of through the human round-robin.
   * Server-side defaulted to true for `web` and `api` channels at
   * creation; otherwise false. Operators can toggle from the channel
   * settings UI at any time.
   */
  alwaysAssignToAi: z16.boolean(),
  status: ChannelStatusSchema,
  createdAt: z16.string().datetime(),
  updatedAt: z16.string().datetime()
});
var CreateChannelSchema = z16.object({
  dealershipId: z16.string().uuid(),
  rooftopId: z16.string().uuid().nullable(),
  type: z16.enum(["web", "api"]),
  name: z16.string().trim().min(1),
  config: JsonObjectSchema.default({}),
  replyDelayAssignedSeconds: z16.number().int().min(0).optional(),
  replyDelayUnassignedSeconds: z16.number().int().min(0).optional(),
  replyDelayOtherAssigneeSeconds: z16.number().int().min(0).optional(),
  aiSuppressionSeconds: z16.number().int().min(0).optional(),
  // No aiEnabled: the creatable types (web/api) are always-on.
  respondTo: AiAgentRespondToSchema.optional(),
  alwaysAssignToAi: z16.boolean().optional(),
  status: ChannelStatusSchema.default("active")
});
var UpdateChannelSchema = z16.object({
  rooftopId: z16.string().uuid().nullable(),
  type: ChannelTypeSchema,
  name: z16.string().trim().min(1),
  forwardingNumber: OptionalPhoneE164Schema,
  voicemailEnabled: z16.boolean().nullable(),
  multilingual: z16.boolean().nullable(),
  config: JsonObjectSchema,
  replyDelayAssignedSeconds: z16.number().int().min(0),
  replyDelayUnassignedSeconds: z16.number().int().min(0),
  replyDelayOtherAssigneeSeconds: z16.number().int().min(0),
  aiSuppressionSeconds: z16.number().int().min(0),
  aiEnabled: z16.boolean(),
  respondTo: AiAgentRespondToSchema,
  alwaysAssignToAi: z16.boolean(),
  status: ChannelStatusSchema
}).partial();
var AllowedDomainsSchema = z16.array(
  z16.string().trim().toLowerCase().min(1).max(253).regex(/^[a-z0-9.\-:*]+$/, "Enter a bare hostname, e.g. example.com.")
).max(50).optional();
var ConnectApiChannelSchema = z16.object({
  rooftopId: z16.string().uuid().nullable(),
  name: z16.string().trim().min(1).max(80),
  allowedDomains: AllowedDomainsSchema
});
var ConnectWidgetChannelSchema = z16.object({
  rooftopId: z16.string().uuid().nullable(),
  name: z16.string().trim().min(1).max(80),
  allowedDomains: AllowedDomainsSchema
});
var EmailSubdomainSchema = z16.string().trim().toLowerCase().min(1).max(63).regex(
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  "Use 1-63 lowercase letters, numbers, or hyphens."
);
var EmailLocalPartSchema = z16.string().trim().toLowerCase().min(1).max(64).regex(
  /^[a-z0-9](?:[a-z0-9._+-]{0,62}[a-z0-9])?$/,
  "Use 1-64 lowercase letters, numbers, dots, underscores, pluses, or hyphens."
);
var ConnectEmailChannelSchema = z16.object({
  rooftopId: z16.string().uuid().nullable(),
  name: z16.string().trim().min(1).max(80),
  localPart: EmailLocalPartSchema,
  subdomain: EmailSubdomainSchema
});
var EmailCustomDomainSchema = z16.string().trim().toLowerCase().min(4).max(253).regex(
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/,
  "Enter a bare domain like smithford.com."
);
var ConnectCustomDomainEmailSchema = ConnectEmailChannelSchema.extend({
  domain: EmailCustomDomainSchema
});
var DnsRecordSpecSchema = z16.object({
  name: z16.string(),
  type: z16.enum(["CNAME", "MX", "TXT"]),
  value: z16.string()
});
var CheckEmailAddressSchema = z16.object({
  localPart: EmailLocalPartSchema,
  subdomain: EmailSubdomainSchema
});
var EmailAddressAvailabilitySchema = z16.object({
  localPart: z16.string(),
  subdomain: z16.string(),
  /** The full address `<localPart>@<subdomain>.<zone>` (server-resolved zone). */
  address: z16.string(),
  available: z16.boolean(),
  /** The subdomain is owned by a DIFFERENT dealership. */
  subdomainTaken: z16.boolean(),
  /** This dealership already has this local-part on this subdomain. */
  localPartTaken: z16.boolean()
});
var HexColor = z16.string().regex(/^#[0-9a-fA-F]{6}$/u, "Use #rrggbb hex (e.g. #133b54)");
var BrandColorPairSchema = z16.object({
  background: HexColor,
  foreground: HexColor
});
var BrandImageSchema = z16.object({
  url: z16.string().url("Logo must be a fully qualified URL"),
  altText: z16.string().max(120).optional(),
  width: z16.string().max(8).optional(),
  height: z16.string().max(8).optional()
});
var WidgetBrandSchema = z16.object({
  colors: z16.object({
    primary: z16.array(BrandColorPairSchema).max(2).optional(),
    secondary: z16.array(BrandColorPairSchema).max(2).optional()
  }).optional(),
  logo: z16.object({
    image: BrandImageSchema.optional()
  }).optional()
});
var WidgetCustomizationSchema = z16.object({
  title: z16.string().trim().max(60).optional(),
  subtitle: z16.string().trim().max(120).optional(),
  chatWelcomeHeading: z16.string().trim().max(120).optional(),
  chatWelcomeDescription: z16.string().trim().max(280).optional(),
  chatWelcomeQuickQuestions: z16.array(z16.string().trim().min(1).max(120)).max(4).optional(),
  brand: WidgetBrandSchema.optional()
});
var ConnectTelegramChannelSchema = z16.object({
  rooftopId: z16.string().uuid(),
  name: z16.string().trim().min(1).max(80),
  /** Token from @BotFather: `<bot_id>:<random>`. */
  botToken: z16.string().trim().regex(/^\d+:[A-Za-z0-9_-]{20,}$/, "Invalid Telegram bot token"),
  /**
   * Set true to proceed when the bot is already connected to THIS
   * dealership — the existing connection is replaced (transferred) in
   * place. The first attempt is made without it; a same-dealership
   * conflict returns a 409 carrying `error: "ChannelConflict"`, the UI
   * shows a prominent warning, and re-submits with this flag set.
   */
  confirmTransfer: z16.boolean().optional()
});
var ConnectWhatsAppEmbeddedSignupSchema = z16.object({
  rooftopId: z16.string().uuid(),
  name: z16.string().trim().min(1).max(80),
  code: z16.string().trim().min(1),
  phoneNumberId: z16.string().trim().min(1),
  wabaId: z16.string().trim().min(1),
  /** See ConnectTelegramChannelSchema.confirmTransfer — same contract. */
  confirmTransfer: z16.boolean().optional()
});
var ChannelConflictResponseSchema = z16.object({
  statusCode: z16.literal(409),
  error: z16.literal("ChannelConflict"),
  message: z16.string(),
  conflict: z16.object({
    channelType: ChannelTypeSchema,
    accountLabel: z16.string(),
    sameDealership: z16.boolean()
  })
});
var StartMetaOAuthSchema = z16.object({
  // Null/omitted = group-wide ("All rooftops"), same as web/api channels.
  // This is a GET, so a query param can't carry JSON null — the client omits
  // the param for group-wide and the controller normalizes undefined -> null.
  rooftopId: z16.string().uuid().nullable().optional(),
  channelType: z16.enum(["facebook_messenger", "instagram"])
});
var ChannelOnboardingInfoSchema = z16.object({
  channel: ChannelSchema,
  /** For API + widget: the token to embed. */
  token: z16.string().nullable(),
  /** For API + widget: HTTPS base URL of the conversation endpoints. */
  apiBaseUrl: z16.string().nullable(),
  /** For widget: WSS URL of the realtime conversation channel (port: WS-based runtime). */
  websocketUrl: z16.string().nullable(),
  /** For widget: HTML snippet the dealer pastes into their site. */
  embedSnippet: z16.string().nullable(),
  /** For Telegram: public webhook URL registered via setWebhook. */
  telegramWebhookUrl: z16.string().nullable(),
  /** For Telegram: link to BotFather conversation. */
  botFatherUrl: z16.string().nullable(),
  /** For Meta: the public webhook URL to register in the Meta dashboard. */
  metaWebhookUrl: z16.string().nullable(),
  /** For Meta: subscribed_fields the dealer needs to enable. */
  metaSubscribedFields: z16.array(z16.string()).nullable(),
  /**
   * For custom-domain email: the DNS records the dealer must create in
   * their own zone before the channel can verify (D1). Absent for every
   * other channel type.
   */
  dnsRecords: z16.array(DnsRecordSpecSchema).nullable().optional(),
  /** Human-readable next step. */
  instructions: z16.string()
});
var ContactIntegrationSyncSchema = z16.object({
  id: z16.string().uuid(),
  integrationId: z16.string().uuid(),
  provider: IntegrationProviderSchema,
  integrationStatus: IntegrationStatusSchema,
  externalId: z16.string(),
  // Last `updated_at` we observed on each side at the last reconcile.
  externalUpdatedAt: z16.string().datetime(),
  lumikaUpdatedAt: z16.string().datetime(),
  createdAt: z16.string().datetime(),
  updatedAt: z16.string().datetime()
});

// src/dto/bdc/leads.dto.ts
import { z as z17 } from "zod";
function computeLeadScore(lead) {
  if (lead.status === "won" || lead.status === "lost") return 0;
  let score = 0;
  const lastAt = lead.lastMessageAt ?? lead.lastActivityAt;
  if (lastAt) {
    const hoursAgo = (Date.now() - new Date(lastAt).getTime()) / 36e5;
    if (hoursAgo < 1) score += 30;
    else if (hoursAgo < 6) score += 22;
    else if (hoursAgo < 24) score += 14;
    else if (hoursAgo < 72) score += 6;
  }
  if (lead.status === "working") score += 20;
  else if (lead.status === "appointment") score += 15;
  else if (lead.status === "new") score += 10;
  if (lead.escalated || lead.needsHumanAt != null) score += 20;
  if (lead.unreadAt != null) score += 15;
  if (lead.slaDueAt != null) score += 10;
  if (lead.interestVehicleId != null || lead.interestVehicle != null) score += 5;
  return Math.min(score, 100);
}
var LeadSchema = z17.object({
  id: z17.string().uuid(),
  dealershipId: z17.string().uuid(),
  rooftopId: z17.string().uuid().nullable(),
  contactId: z17.string().uuid().nullable(),
  // Denormalized from the contact so inbox rows can render the contact's
  // name/avatar without a separate fetch-all-contacts call. Null when the
  // contact wasn't joined (e.g. single conversation fetches / save paths).
  contactName: z17.string().nullable(),
  contactAvatarUrl: z17.string().nullable(),
  // Last-resort label when the contact has no name — a short snippet of the
  // conversation's first inbound message — so anonymous inbox rows read as
  // something identifying instead of "Anonymous". Computed at read time.
  fallbackLabel: z17.string().nullable(),
  // Server-computed canonical display label — the single resolved name for
  // the inbox row (businessName → first+last → email → phone →
  // socialHandle → fallbackLabel → "Anonymous"). Fixes the nameless-phone-
  // call bug where the snippet beat the phone number. Always populated by
  // the backend; nullable/optional here for frontend optimistic builds.
  displayName: z17.string().nullable().optional(),
  // A pipeline lead can exist before any conversation starts. Once a
  // channel is bound, inbound/outbound writers stamp the concrete channel.
  primaryChannel: LeadPrimaryChannelSchema.nullable(),
  channelId: z17.string().uuid().nullable(),
  // Opportunity fields: department, pipeline stage, vehicle of interest, free text.
  type: z17.enum(["sales", "service", "parts"]).nullable(),
  status: z17.enum(["new", "working", "appointment", "won", "lost"]),
  lostReason: z17.string().nullable(),
  // Denormalized from Contact.leadSource on list/board reads. Optional keeps
  // single-row/optimistic payloads backward-compatible.
  source: z17.string().nullable().optional(),
  interestVehicleId: z17.string().uuid().nullable(),
  interest: z17.string().nullable(),
  closed: z17.boolean(),
  closedAt: z17.string().datetime().nullable(),
  snoozedUntil: z17.string().datetime().nullable(),
  aiAutopilot: z17.boolean(),
  aiSuppressedUntil: z17.string().datetime().nullable(),
  escalated: z17.boolean(),
  // Latest AI handoff explanation, enriched on the single-lead read.
  // List/optimistic payloads may omit it; null means no durable reason
  // was recorded (the UI still renders an explicit fallback disclaimer).
  escalationReason: z17.string().nullable().optional(),
  // Set when the AI flagged this conversation for a human to close
  // (AI Task Management — `flag_for_human`). The AI keeps assisting;
  // the UI shows a "Needs you" banner + inbox filter. Null = not flagged.
  needsHumanAt: z17.string().datetime().nullable(),
  needsHumanReason: z17.string().nullable(),
  assignedUserId: z17.string().uuid().nullable(),
  lastActivityAt: z17.string().datetime().nullable(),
  lastActivityPreview: z17.string().nullable(),
  // Timestamp of the most recent inbound or outbound message (sms/email/social).
  // Excludes calls, notes, and system events. Null on leads with no messaging activity.
  lastMessageAt: z17.string().datetime().nullable(),
  // Joined vehicle of interest (resolved from interestVehicleId). Includes
  // sold-but-attached vehicles. Null when no primary vehicle is linked.
  interestVehicle: z17.object({
    vin: z17.string(),
    year: z17.number().int().nullable(),
    make: z17.string().nullable(),
    model: z17.string().nullable(),
    trim: z17.string().nullable(),
    status: VehicleStatusSchema
  }).nullable(),
  // Per-channel message counts (read-time, no stored column).
  channelCounts: z17.object({
    phone: z17.number().int().nonnegative(),
    sms: z17.number().int().nonnegative(),
    email: z17.number().int().nonnegative(),
    social: z17.number().int().nonnegative()
  }),
  // Sender of the most recent conversational turn (message/call) in the
  // thread. Drives the inbox preview prefix ("You: / AI: / <rep>:") so the
  // queue shows by whom the last message was sent. Populated only on the
  // inbox feed (the keyset list derives it); null on single fetches.
  lastActorType: z17.enum(["contact", "user", "ai", "system"]).nullable(),
  lastActorUserId: z17.string().uuid().nullable(),
  // Cached AI "recent conversation history" narrative for the Customer 360
  // panel. Null until first generated; `aiSummaryGeneratedAt` is its
  // freshness mark (regenerated when it falls behind `lastActivityAt`).
  aiSummary: z17.string().nullable(),
  aiSummaryGeneratedAt: z17.string().datetime().nullable(),
  unreadAt: z17.string().datetime().nullable(),
  slaDueAt: z17.string().datetime().nullable(),
  // Derived at read time by computeLeadScore — NOT a stored column, NOT ML.
  // A deterministic 0–100 proxy for lead engagement based on recency, status,
  // escalation signals, unread, SLA, and vehicle-of-interest presence.
  leadScore: z17.number().int().min(0).max(100),
  createdAt: z17.string().datetime(),
  updatedAt: z17.string().datetime()
});
var LeadAiSummaryResultSchema = z17.object({
  aiSummary: z17.string().nullable(),
  aiSummaryGeneratedAt: z17.string().datetime().nullable()
});
var MergeContactsSchema = z17.object({
  winnerContactId: z17.string().uuid(),
  loserContactIds: z17.array(z17.string().uuid()).min(1).max(3),
  fieldOverrides: z17.object({
    firstName: z17.string().nullable().optional(),
    lastName: z17.string().nullable().optional(),
    primaryEmail: OptionalEmailSchema2,
    primaryPhone: OptionalPhoneE164Schema,
    status: ContactStatusSchema.optional(),
    assignedUserId: z17.string().uuid().nullable().optional(),
    leadSource: z17.string().nullable().optional(),
    notes: z17.string().nullable().optional(),
    internalNotes: z17.string().nullable().optional()
  }).optional(),
  copyContactChannels: z17.boolean().optional()
});
var MergeContactsResultSchema = z17.object({
  winnerContactId: z17.string().uuid(),
  mergedAt: z17.string().datetime(),
  reparentSummary: z17.object({
    leads: z17.number(),
    consolidatedLeads: z17.number(),
    activities: z17.number(),
    appointments: z17.number(),
    tradeIns: z17.number(),
    vehicleInterests: z17.number(),
    followUps: z17.number(),
    contactChannels: z17.number(),
    droppedContactChannels: z17.number(),
    integrationContacts: z17.number(),
    droppedIntegrationContacts: z17.number(),
    consentEvents: z17.number()
  }),
  mergeEventIds: z17.array(z17.string().uuid())
});
var CreateLeadSchema = z17.object({
  dealershipId: z17.string().uuid(),
  contactId: z17.string().uuid().nullable(),
  rooftopId: z17.string().uuid().nullable().optional(),
  primaryChannel: LeadPrimaryChannelSchema.nullable().optional(),
  channelId: z17.string().uuid().nullable().optional(),
  type: z17.enum(["sales", "service", "parts"]).nullable().optional(),
  status: z17.enum(["new", "working", "appointment", "won", "lost"]).optional(),
  lostReason: z17.string().trim().min(1).max(500).nullable().optional(),
  // Lead source is owned by the Contact record. Accepting it here keeps the
  // New Lead workflow coherent while preserving that single source of truth.
  source: z17.string().trim().min(1).max(200).nullable().optional(),
  interestVehicleId: z17.string().uuid().nullable().optional(),
  interest: z17.string().nullable().optional(),
  closed: z17.boolean().default(false),
  closedAt: z17.string().datetime().nullable().optional(),
  snoozedUntil: z17.string().datetime().nullable().optional(),
  aiAutopilot: z17.boolean().optional(),
  aiSuppressedUntil: z17.string().datetime().nullable().optional(),
  assignedUserId: z17.string().uuid().nullable().optional(),
  lastActivityAt: z17.string().datetime().nullable().optional(),
  lastActivityPreview: OptionalTextSchema,
  unreadAt: z17.string().datetime().nullable().optional(),
  slaDueAt: z17.string().datetime().nullable().optional()
});
var UpdateLeadSchema = CreateLeadSchema.omit({
  dealershipId: true,
  contactId: true,
  source: true
}).partial();

// src/dto/bdc/activities.dto.ts
import { z as z18 } from "zod";
var ActivitySchema = z18.object({
  id: z18.string().uuid(),
  dealershipId: z18.string().uuid(),
  rooftopId: z18.string().uuid().nullable(),
  leadId: z18.string().uuid().nullable(),
  contactId: z18.string().uuid().nullable(),
  type: z18.string(),
  direction: ActivityDirectionSchema,
  actorType: ActivityActorTypeSchema,
  actorUserId: z18.string().uuid().nullable(),
  channelId: z18.string().uuid().nullable(),
  body: z18.string().nullable(),
  attachments: JsonArraySchema,
  // Free-form per-activity metadata (persisted since migration 134):
  // email threading fields, and V5's `liveCall: true` flag on a call
  // activity while its relay session is open (cleared on close/flush).
  // Optional so older serializers that omit the column still parse.
  metadata: JsonObjectSchema.nullable().optional(),
  deliveryStatus: DeliveryStatusSchema.nullable(),
  providerMessageId: z18.string().nullable(),
  externalThreadId: z18.string().nullable(),
  summary: z18.string().nullable(),
  // On-demand, LLM-generated summary. Null until an operator clicks
  // "Generate AI summary"; persisted thereafter.
  aiSummary: z18.string().nullable(),
  occurredAt: z18.string().datetime(),
  // Read-receipts (governance §4). Nullable because not every channel
  // supports them (Telegram bots, plain SMS, plain email). Frontend
  // shows ✓ for delivered (deliveryStatus=sent) and ✓✓ for read
  // (readAt set).
  readAt: z18.string().datetime().nullable(),
  readActorType: z18.enum(["contact", "user", "ai", "system"]).nullable(),
  readActorUserId: z18.string().uuid().nullable(),
  createdAt: z18.string().datetime(),
  updatedAt: z18.string().datetime()
});
var CreateActivitySchema = z18.object({
  dealershipId: z18.string().uuid(),
  rooftopId: z18.string().uuid().nullable(),
  leadId: z18.string().uuid().nullable().optional(),
  contactId: z18.string().uuid().nullable(),
  type: z18.string().trim().min(1),
  direction: ActivityDirectionSchema.default("system"),
  actorType: ActivityActorTypeSchema,
  actorUserId: z18.string().uuid().nullable().optional(),
  channelId: z18.string().uuid().nullable().optional(),
  body: z18.string().nullable().optional(),
  attachments: JsonArraySchema.default([]),
  deliveryStatus: DeliveryStatusSchema.nullable().optional(),
  providerMessageId: OptionalTextSchema,
  externalThreadId: OptionalTextSchema,
  summary: OptionalTextSchema,
  occurredAt: z18.string().datetime().optional()
});
var UpdateActivitySchema = CreateActivitySchema.omit({
  dealershipId: true,
  contactId: true
}).partial();
var LeadAssignSchema = z18.object({
  assignedUserId: z18.string().uuid().nullable()
});
var LeadSnoozeSchema = z18.object({
  snoozedUntil: z18.string().datetime().nullable()
});
var InternalNoteSchema = z18.object({
  body: z18.string().trim().min(1)
});
var BulkLeadActionSchema = z18.object({
  leadIds: z18.array(z18.string().uuid()).min(1).max(500),
  action: z18.enum(["assign", "close", "reopen", "tag", "untag", "read", "snooze"]),
  params: z18.object({
    assignedUserId: z18.string().uuid().nullable().optional(),
    snoozedUntil: z18.string().datetime().nullable().optional(),
    tagId: z18.string().uuid().optional()
  }).default({})
});
var BulkLeadActionResultSchema = z18.object({
  ok: z18.array(z18.string().uuid()),
  failed: z18.array(z18.object({ id: z18.string().uuid(), reason: z18.string() }))
});
var OutboundAttachmentKindSchema = z18.enum(["image", "audio", "video", "document"]);
var TranscribeAttachmentSchema = z18.object({
  activityId: z18.string().uuid(),
  attachmentIndex: z18.number().int().nonnegative().max(99)
});
var TranscribeAttachmentResultSchema = z18.object({
  transcript: z18.string()
});
var OutboundAttachmentSchema = z18.object({
  storage: z18.enum(["private", "public"]),
  key: z18.string().min(1).max(512),
  kind: OutboundAttachmentKindSchema,
  mimeType: z18.string().min(1).max(120),
  fileName: z18.string().min(1).max(255).nullable().optional(),
  sizeBytes: z18.number().int().nonnegative().optional(),
  // Audio-only metadata. Optional everywhere — image/video/document
  // attachments keep these null. `durationSec` lets the UI render the
  // Telegram-style mm:ss badge without probing the file; `transcript`
  // is populated lazily on first /attachments/transcribe call and
  // then cached on the attachment row.
  durationSec: z18.number().int().nonnegative().nullable().optional(),
  transcript: z18.string().nullable().optional()
});
var SendLeadMessageSchema = z18.object({
  body: z18.string().trim().max(4e3).optional().default(""),
  attachments: z18.array(OutboundAttachmentSchema).max(10).optional().default([]),
  /**
   * D10 — scheduled send. A FUTURE instant persists the message queued
   * with `metadata.sendAfter`; the outbox due-rows sweep publishes it
   * once the time passes. Past/absent = send now. Cancellable until
   * due via DELETE on the queued activity.
   */
  sendAfter: z18.string().datetime({ offset: true }).optional(),
  // Operator-typed reply subject (UIUX §4.1, email channels): wins over
  // the internal first-message subject rail; persisted to metadata.
  subject: z18.string().trim().min(1).max(200).optional(),
  // Email-only extra recipients (D3): persisted to activities.metadata so
  // the timeline shows who was copied. Ignored by non-email channels.
  cc: z18.array(z18.string().trim().toLowerCase().email()).max(10).optional(),
  bcc: z18.array(z18.string().trim().toLowerCase().email()).max(10).optional(),
  // D9 rider — client-generated idempotency key, persisted as the
  // activity's provider_message_id so operator retries/resends dedupe
  // on the existing (channel_id, provider_message_id) unique index
  // (the R13 widget mechanism, reused). Validated hard: it feeds a
  // unique index.
  clientMessageId: z18.string().regex(/^[A-Za-z0-9._:-]{8,64}$/).optional(),
  // D7 — WhatsApp approved-template (HSM) send: the only deliverable
  // shape outside WhatsApp's 24h session window. `body` should carry
  // the rendered template text for the timeline bubble; the server
  // validates the template is APPROVED on the lead's channel before
  // any Graph call. Ignored on non-WhatsApp channels.
  whatsappTemplate: z18.object({
    name: z18.string().min(1).max(512),
    language: z18.string().min(2).max(32).optional(),
    variables: z18.array(z18.string().max(1024)).max(30).optional()
  }).optional()
}).refine(
  (input) => input.body.trim().length > 0 || (input.attachments?.length ?? 0) > 0,
  {
    message: "Message must include text or at least one attachment.",
    path: ["body"]
  }
);
var StartConversationSchema = z18.object({
  channel: z18.enum(["sms", "email"]),
  /**
   * Exact open Lead to bind and send on. The New Lead -> Start conversation
   * flow supplies this so the first outbound message cannot fork a second
   * opportunity for the same Contact. The server tenant/contact/open-scopes
   * the row before atomically binding its concrete channel.
   */
  leadId: z18.string().uuid().optional(),
  // Optional rooftop hint; the server prefers a rooftop-scoped channel and
  // falls back to a dealership-wide one.
  rooftopId: z18.string().uuid().nullable().optional(),
  /**
   * Explicit channel choice (the compose dialog's "send via" picker when
   * the dealership has several active channels of this type). Validated
   * server-side: must be an ACTIVE channel of the matching type in this
   * dealership. Absent = the server resolves one.
   */
  channelId: z18.string().uuid().optional(),
  // Email only: the outbound subject line. Ignored for SMS.
  subject: z18.string().trim().min(1).max(200).optional(),
  body: z18.string().trim().min(1, "Message can't be empty.").max(4e3)
});
var ResolveOrCreateContactSchema = z18.object({
  primaryPhone: OptionalPhoneE164Schema,
  primaryEmail: OptionalEmailSchema2,
  firstName: z18.string().trim().min(1).max(120).nullable().optional(),
  lastName: z18.string().trim().min(1).max(120).nullable().optional()
}).refine((d) => d.primaryPhone ?? d.primaryEmail, {
  message: "Provide at least a phone or an email.",
  path: ["primaryPhone"]
});
var SuggestedResponseSchema = z18.object({
  text: z18.string()
});
var ALLOWED_ATTACHMENT_MIME = [
  // images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  // audio
  "audio/mpeg",
  "audio/mp4",
  "audio/aac",
  "audio/ogg",
  "audio/opus",
  "audio/wav",
  "audio/webm",
  // video
  "video/mp4",
  "video/quicktime",
  "video/webm",
  // documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv"
];
var CreateAttachmentUploadUrlSchema = z18.object({
  fileName: z18.string().trim().min(1).max(255),
  mimeType: z18.enum(ALLOWED_ATTACHMENT_MIME),
  sizeBytes: z18.number().int().positive().max(25 * 1024 * 1024, "Attachments must be 25 MB or smaller.")
});
var AttachmentUploadUrlResultSchema = z18.object({
  uploadUrl: z18.string().url(),
  /** Storage tier the dispatcher later resolves a delivery URL against. */
  storage: z18.enum(["private", "public"]),
  /** S3 key to send back in `attachments[i].key`. */
  key: z18.string(),
  /** Same MIME the caller supplied — echoed for convenience. */
  mimeType: z18.string(),
  /** ISO timestamp the signed URL expires; the client should not retry past this. */
  expiresAt: z18.string().datetime()
});

// src/dto/bdc/appointments.dto.ts
import { z as z19 } from "zod";
var AppointmentSchema = z19.object({
  id: z19.string().uuid(),
  dealershipId: z19.string().uuid(),
  rooftopId: z19.string().uuid(),
  contactId: z19.string().uuid(),
  leadId: z19.string().uuid().nullable(),
  appointmentType: AppointmentTypeSchema,
  status: AppointmentStatusSchema,
  scheduledStartAt: z19.string().datetime(),
  durationMinutes: z19.number().int(),
  assignedUserId: z19.string().uuid().nullable(),
  transportType: AppointmentTransportTypeSchema.nullable(),
  // Per-rooftop catalog entry; null for legacy rows and custom one-offs.
  serviceTypeId: z19.string().uuid().nullable(),
  // Sales link: which inventory interest this appointment is about.
  // Service appointments keep using vehicleVin (the customer's own car).
  vehicleInterestId: z19.string().uuid().nullable(),
  vehicleVin: z19.string().nullable(),
  notes: z19.string().nullable(),
  externalStatus: z19.string().nullable(),
  externalId: z19.string().nullable(),
  // TTL for `pending_customer` reservations; null outside that state.
  // Server-managed (set by the AI propose flow, cleared on confirm).
  reservationExpiresAt: z19.string().datetime().nullable(),
  createdAt: z19.string().datetime(),
  updatedAt: z19.string().datetime()
});
var CalendarAppointmentSchema = AppointmentSchema.extend({
  contactName: z19.string().min(1)
});
var CalendarAppointmentFilterSchema = z19.enum([
  "all",
  "upcoming",
  "today",
  "cancelled",
  "completed"
]);
var CalendarDateSchema = z19.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const [year, month, day] = value.split("-").map(Number);
  if (year === void 0 || month === void 0 || day === void 0) {
    return false;
  }
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}, "Invalid calendar date");
var CalendarAppointmentQuerySchema = z19.object({
  from: CalendarDateSchema,
  to: CalendarDateSchema,
  filter: CalendarAppointmentFilterSchema.default("all"),
  assignedUserId: z19.string().uuid().optional(),
  rooftopId: z19.string().uuid().optional(),
  appointmentType: AppointmentTypeSchema.optional(),
  page: z19.coerce.number().int().min(1).default(1),
  pageSize: z19.coerce.number().int().min(1).max(100).default(100)
}).refine((query) => query.to > query.from, {
  message: "to must be after from",
  path: ["to"]
});
var CreateAppointmentSchema = z19.object({
  dealershipId: z19.string().uuid(),
  rooftopId: z19.string().uuid(),
  contactId: z19.string().uuid(),
  leadId: z19.string().uuid().nullable().optional(),
  appointmentType: AppointmentTypeSchema,
  status: AppointmentStatusSchema.default("unconfirmed"),
  scheduledStartAt: z19.string().datetime(),
  durationMinutes: z19.number().int().positive().default(30),
  assignedUserId: z19.string().uuid().nullable().optional(),
  transportType: AppointmentTransportTypeSchema.nullable().optional(),
  serviceTypeId: z19.string().uuid().nullable().optional(),
  vehicleInterestId: z19.string().uuid().nullable().optional(),
  vehicleVin: OptionalVinSchema,
  notes: z19.string().nullable().optional(),
  externalStatus: OptionalTextSchema,
  externalId: OptionalTextSchema
});
var UpdateAppointmentSchema = CreateAppointmentSchema.omit({
  dealershipId: true,
  rooftopId: true,
  contactId: true
}).partial();
var ServiceTypeCategorySchema = z19.enum([
  "sales",
  "service",
  "parts",
  "delivery",
  "bodyshop"
]);
var ReminderStepSchema = z19.object({
  offsetMinutes: z19.number().int(),
  channel: z19.enum(["sms", "email", "call"])
});
var ReminderTemplateSchema = z19.object({
  confirm: ReminderStepSchema.optional(),
  remind: ReminderStepSchema.optional(),
  noShow: ReminderStepSchema.optional()
});
var DEFAULT_REMINDER_TEMPLATE = {
  confirm: { offsetMinutes: -24 * 60, channel: "sms" },
  remind: { offsetMinutes: -2 * 60, channel: "sms" },
  noShow: { offsetMinutes: 60, channel: "sms" }
};
var ServiceTypeSchema = z19.object({
  id: z19.string().uuid(),
  dealershipId: z19.string().uuid(),
  rooftopId: z19.string().uuid(),
  name: z19.string(),
  slug: z19.string(),
  category: ServiceTypeCategorySchema,
  defaultDurationMinutes: z19.number().int().positive(),
  bufferBeforeMinutes: z19.number().int().nonnegative(),
  bufferAfterMinutes: z19.number().int().nonnegative(),
  reminderTemplate: ReminderTemplateSchema,
  customerLabel: z19.string().nullable(),
  active: z19.boolean(),
  sortOrder: z19.number().int(),
  createdAt: z19.string().datetime(),
  updatedAt: z19.string().datetime()
});
var CreateServiceTypeSchema = z19.object({
  dealershipId: z19.string().uuid(),
  rooftopId: z19.string().uuid(),
  name: z19.string().trim().min(1).max(120),
  slug: z19.string().trim().regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, "lowercase kebab-case"),
  category: ServiceTypeCategorySchema,
  defaultDurationMinutes: z19.number().int().positive().max(24 * 60),
  bufferBeforeMinutes: z19.number().int().nonnegative().max(8 * 60).default(0),
  bufferAfterMinutes: z19.number().int().nonnegative().max(8 * 60).default(0),
  reminderTemplate: ReminderTemplateSchema.default(DEFAULT_REMINDER_TEMPLATE),
  customerLabel: OptionalTextSchema,
  active: z19.boolean().default(true),
  sortOrder: z19.number().int().default(100)
});
var UpdateServiceTypeSchema = CreateServiceTypeSchema.omit({
  dealershipId: true,
  rooftopId: true,
  slug: true
}).partial();

// src/dto/bdc/telephony.dto.ts
import { z as z20 } from "zod";
var TelephonyBusinessTypeSchema = z20.enum([
  "LLC",
  "CORPORATION",
  "PARTNERSHIP",
  "NON_PROFIT",
  "SOLE_PROPRIETOR"
]);
var TelephonyBrandStatusSchema = z20.enum([
  "NOT_STARTED",
  "DRAFT",
  "PENDING_OTP",
  "IN_REVIEW",
  "APPROVED",
  "FAILED"
]);
var TelephonyAccountSchema = z20.object({
  id: z20.string().uuid(),
  displayName: z20.string(),
  legalBusinessName: z20.string().nullable(),
  einLast4: z20.string().nullable(),
  businessAddressLine1: z20.string().nullable(),
  businessAddressLine2: z20.string().nullable(),
  businessCity: z20.string().nullable(),
  businessState: z20.string().nullable(),
  businessPostalCode: z20.string().nullable(),
  businessCountry: z20.string(),
  websiteUrl: z20.string().nullable(),
  businessType: TelephonyBusinessTypeSchema.nullable(),
  businessIndustry: z20.string(),
  twilioAccountSid: z20.string(),
  // Voice TwiML Application SID — routing target for outbound Voice
  // SDK calls. Provisioned alongside the subaccount; null only on
  // legacy rows that pre-date the per-account TwiML App rollout.
  twilioVoiceTwimlAppSid: z20.string().nullable(),
  // Trust Hub bundle SIDs + Brand SID — null until the A2P wizard
  // creates them. Surfaced so the UI can show "Submit Brand
  // Registration" vs "Resubmit" etc.
  twilioCustomerProfileSid: z20.string().nullable(),
  twilioA2pTrustProductSid: z20.string().nullable(),
  twilioBrandSid: z20.string().nullable(),
  brandStatus: TelephonyBrandStatusSchema,
  brandTrustScore: z20.number().int().nullable(),
  brandFailureReasons: z20.array(z20.unknown()).nullable(),
  createdAt: z20.string().datetime(),
  updatedAt: z20.string().datetime()
});
var CreateTelephonyAccountSchema = z20.object({
  displayName: z20.string().trim().min(1).max(120),
  // Optional at creation — admin can fill these later as part of the
  // A2P wizard. Validated at submission time, not here.
  legalBusinessName: z20.string().trim().min(1).max(255).optional(),
  ein: z20.string().trim().regex(/^\d{2}-?\d{7}$/u, "EIN must look like 12-3456789.").optional(),
  businessAddressLine1: z20.string().trim().max(255).optional(),
  businessAddressLine2: z20.string().trim().max(255).optional(),
  businessCity: z20.string().trim().max(120).optional(),
  businessState: UsStateCodeSchema.optional(),
  businessPostalCode: UsZipSchema.optional(),
  businessCountry: CountryCodeSchema.default("US"),
  websiteUrl: z20.string().trim().url().max(255).optional(),
  businessType: TelephonyBusinessTypeSchema.optional()
});
var UpdateTelephonyAccountSchema = CreateTelephonyAccountSchema.partial();
var TelephonyNumberTypeSchema = z20.enum(["LOCAL", "TOLL_FREE"]);
var TelephonyEmergencyStatusSchema = z20.enum(["ACTIVE", "INACTIVE"]);
var TelephonyPhoneNumberSchema = z20.object({
  id: z20.string().uuid(),
  twilioAccountId: z20.string().uuid(),
  rooftopId: z20.string().uuid().nullable(),
  twilioPhoneNumberSid: z20.string(),
  phoneNumberE164: z20.string(),
  phoneNumberFriendly: z20.string().nullable(),
  numberType: TelephonyNumberTypeSchema,
  capabilities: JsonObjectSchema,
  voiceEnabled: z20.boolean(),
  smsEnabled: z20.boolean(),
  emergencyAddressSid: z20.string().nullable(),
  emergencyStatus: TelephonyEmergencyStatusSchema,
  voiceUrl: z20.string().nullable(),
  smsUrl: z20.string().nullable(),
  messagingServiceSid: z20.string().nullable(),
  purchasedAt: z20.string().datetime().nullable(),
  releasedAt: z20.string().datetime().nullable(),
  createdAt: z20.string().datetime(),
  updatedAt: z20.string().datetime()
});
var TelephonyAccountListSchema = z20.array(TelephonyAccountSchema);
var TelephonyPhoneNumberListSchema = z20.array(TelephonyPhoneNumberSchema);
var TelephonyAccountSearchResultSchema = z20.object({
  id: z20.string().uuid(),
  displayName: z20.string(),
  legalBusinessName: z20.string().nullable(),
  twilioAccountSid: z20.string(),
  brandStatus: TelephonyBrandStatusSchema,
  connectedDealerships: z20.array(
    z20.object({ id: z20.string().uuid(), name: z20.string() })
  )
});
var TelephonyAccountSearchResultListSchema = z20.array(
  TelephonyAccountSearchResultSchema
);
var TelephonyAvailableNumberSchema = z20.object({
  phoneNumber: z20.string(),
  friendlyName: z20.string().nullable(),
  locality: z20.string().nullable(),
  region: z20.string().nullable(),
  postalCode: z20.string().nullable(),
  isoCountry: z20.string(),
  capabilities: z20.object({
    voice: z20.boolean().default(true),
    sms: z20.boolean().default(false),
    mms: z20.boolean().default(false)
  })
});
var TelephonyAvailableNumberListSchema = z20.array(TelephonyAvailableNumberSchema);
var TelephonySearchNumbersSchema = z20.object({
  countryCode: z20.string().trim().min(2).max(2).default("US"),
  areaCode: z20.string().trim().regex(/^\d{3}$/u).optional(),
  contains: z20.string().trim().min(1).max(20).optional(),
  locality: z20.string().trim().min(1).max(80).optional(),
  region: UsStateCodeSchema.optional(),
  numberType: TelephonyNumberTypeSchema.default("LOCAL"),
  limit: z20.number().int().min(1).max(30).default(10)
});
var TelephonyE911AddressSchema = z20.object({
  friendlyName: z20.string().trim().min(1).max(64),
  customerName: z20.string().trim().min(1).max(120),
  street: z20.string().trim().min(1).max(255),
  city: z20.string().trim().min(1).max(80),
  region: UsStateCodeSchema,
  postalCode: UsZipSchema,
  isoCountry: CountryCodeSchema.default("US")
});
var TelephonyPurchaseNumberSchema = z20.object({
  phoneNumber: PhoneE164Schema,
  rooftopId: z20.string().uuid().nullable().optional(),
  emergencyAddress: TelephonyE911AddressSchema
});
var TelephonyUpdateNumberSchema = z20.object({
  rooftopId: z20.string().uuid().nullable(),
  voiceUrl: z20.string().url().nullable(),
  smsUrl: z20.string().url().nullable(),
  phoneNumberFriendly: z20.string().trim().min(1).max(120).nullable()
}).partial();
var A2pBusinessProfileSchema = z20.object({
  legalBusinessName: z20.string().trim().min(1).max(255).nullable(),
  ein: z20.string().trim().regex(/^\d{2}-?\d{7}$/u, "EIN must look like 12-3456789.").nullable(),
  businessAddressLine1: z20.string().trim().max(255).nullable(),
  businessAddressLine2: z20.string().trim().max(255).nullable(),
  businessCity: z20.string().trim().max(120).nullable(),
  businessState: UsStateCodeSchema.nullable(),
  businessPostalCode: UsZipSchema.nullable(),
  businessCountry: CountryCodeSchema,
  websiteUrl: z20.string().trim().url().max(255).nullable(),
  businessType: TelephonyBusinessTypeSchema.nullable()
}).partial();
var A2pBrandTypeSchema = z20.enum([
  "STANDARD",
  "SOLE_PROPRIETOR",
  "LOW_VOLUME_STANDARD"
]);
var A2pJobPositionSchema = z20.enum([
  "Director",
  "GM",
  "VP",
  "CEO",
  "CFO",
  "General Counsel",
  "Other"
]);
var A2pSubmitBrandSchema = z20.object({
  authorizedRepFirstName: z20.string().trim().min(1).max(120),
  authorizedRepLastName: z20.string().trim().min(1).max(120),
  authorizedRepEmail: z20.string().trim().email(),
  // E.164 — Twilio rejects the profile otherwise.
  authorizedRepPhone: z20.string().trim().regex(/^\+[1-9]\d{6,14}$/, "Use E.164 format, e.g. +15551234567."),
  authorizedRepBusinessTitle: z20.string().trim().min(1).max(120),
  authorizedRepJobPosition: A2pJobPositionSchema,
  brandType: A2pBrandTypeSchema.default("STANDARD")
});
var A2pVerifyOtpSchema = z20.object({
  otp: z20.string().trim().regex(/^\d{4,8}$/u, "OTP must be 4\u20138 digits.")
});
var A2pCampaignUseCaseSchema = z20.enum([
  "CUSTOMER_CARE",
  "MARKETING",
  "MIXED",
  "LOW_VOLUME",
  "ACCOUNT_NOTIFICATION",
  "DELIVERY_NOTIFICATION"
]);
var A2pSubmitCampaignSchema = z20.object({
  useCase: A2pCampaignUseCaseSchema.default("CUSTOMER_CARE"),
  description: z20.string().trim().min(40).max(1024),
  messageSamples: z20.array(z20.string().trim().min(20).max(1024)).min(2).max(5),
  messageFlow: z20.string().trim().min(40).max(2048),
  optInMessage: z20.string().trim().min(20).max(320).optional(),
  optOutMessage: z20.string().trim().min(20).max(320).optional(),
  helpMessage: z20.string().trim().min(20).max(320).optional(),
  hasEmbeddedLinks: z20.boolean().default(false),
  hasEmbeddedPhone: z20.boolean().default(false)
});
var A2pCampaignStatusSchema = z20.enum([
  "DRAFT",
  "PENDING",
  "IN_PROGRESS",
  "VERIFIED",
  "FAILED"
]);
var A2pStatusSchema = z20.object({
  telephonyAccountId: z20.string().uuid(),
  brandStatus: TelephonyBrandStatusSchema,
  brandTrustScore: z20.number().int().nullable(),
  brandFailureReasons: z20.array(z20.string()),
  hasMessagingService: z20.boolean(),
  campaignStatus: A2pCampaignStatusSchema,
  campaignSid: z20.string().nullable(),
  campaignFailureReasons: z20.array(z20.string()),
  /**
   * Safe copy of the campaign data already submitted to the carrier. Keeping
   * it in the status read model lets a durable workflow show exactly what is
   * under review without exposing account credentials.
   */
  campaign: z20.object({
    useCase: A2pCampaignUseCaseSchema,
    description: z20.string().nullable(),
    messageSamples: z20.array(z20.string()),
    messageFlow: z20.string().nullable(),
    optInMessage: z20.string().nullable(),
    optOutMessage: z20.string().nullable(),
    helpMessage: z20.string().nullable(),
    hasEmbeddedLinks: z20.boolean(),
    hasEmbeddedPhone: z20.boolean(),
    submittedAt: z20.string().datetime().nullable(),
    verifiedAt: z20.string().datetime().nullable()
  }).nullable().default(null)
});
var TelephonyUsageRowSchema = z20.object({
  day: z20.string(),
  category: z20.string(),
  usageUnit: z20.string(),
  usageCount: z20.number(),
  priceUsd: z20.number()
});
var TelephonyUsageSummarySchema = z20.object({
  telephonyAccountId: z20.string().uuid().nullable(),
  dealershipId: z20.string().uuid().nullable(),
  start: z20.string(),
  end: z20.string(),
  rows: z20.array(TelephonyUsageRowSchema),
  totals: z20.object({
    voiceMinutes: z20.number(),
    smsSegments: z20.number(),
    mmsSegments: z20.number(),
    priceUsd: z20.number()
  })
});
var TelephonyUsageRangeSchema = z20.object({
  start: z20.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD"),
  end: z20.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
});

// src/dto/bdc/engagement.dto.ts
import { z as z21 } from "zod";
var HexColorSchema = z21.string().trim().regex(/^#[0-9a-fA-F]{6}$/u, "Must be a hex color like #3b82f6").transform((v) => v.toLowerCase());
var TagSchema = z21.object({
  id: z21.string().uuid(),
  dealershipId: z21.string().uuid(),
  slug: z21.string(),
  label: z21.string(),
  color: z21.string().nullable(),
  description: z21.string().nullable(),
  createdAt: z21.string().datetime(),
  updatedAt: z21.string().datetime()
});
var TagSlugSchema = z21.string().trim().min(1).max(60).regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, and dashes only.");
var TagLabelSchema = z21.string().trim().min(1).max(60);
function slugFromLabel(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60).replace(/-+$/g, "");
}
var CreateTagSchema = z21.object({
  dealershipId: z21.string().uuid(),
  slug: TagSlugSchema,
  label: TagLabelSchema,
  color: HexColorSchema.nullable().optional(),
  description: OptionalTextSchema
});
var UpdateTagSchema = z21.object({
  slug: TagSlugSchema,
  label: TagLabelSchema,
  color: HexColorSchema.nullable(),
  description: OptionalTextSchema
}).partial();
var ContactTagSchema = z21.object({
  id: z21.string().uuid(),
  dealershipId: z21.string().uuid(),
  contactId: z21.string().uuid(),
  tagId: z21.string().uuid(),
  appliedByUserId: z21.string().uuid().nullable(),
  appliedAt: z21.string().datetime()
});
var CreateContactTagSchema = z21.object({
  dealershipId: z21.string().uuid(),
  contactId: z21.string().uuid(),
  tagId: z21.string().uuid()
});
var CadenceEnrollOnSchema = z21.enum([
  "lead.created",
  "lead.lost",
  "appointment.no_show",
  "appointment.outcome_unknown"
]);
var CadenceStepSchema = z21.object({
  id: z21.string().uuid(),
  stepIndex: z21.number().int(),
  /** Hours after ENROLLMENT (not after the previous step). */
  offsetHours: z21.number().int(),
  channel: z21.enum(["sms", "email", "ai"]),
  body: z21.string().nullable(),
  aiPrompt: z21.string().nullable()
});
var CadenceSequenceSchema = z21.object({
  id: z21.string().uuid(),
  dealershipId: z21.string().uuid(),
  rooftopId: z21.string().uuid().nullable(),
  name: z21.string(),
  /** Sequences ship DISABLED; this is the settings enable toggle. */
  enabled: z21.boolean(),
  enrollOn: CadenceEnrollOnSchema,
  steps: z21.array(CadenceStepSchema),
  createdAt: z21.string().datetime(),
  updatedAt: z21.string().datetime()
});
var UpdateCadenceSequenceSchema = z21.object({
  name: z21.string().trim().min(1).optional(),
  enabled: z21.boolean().optional()
});
var FollowUpKindSchema = z21.enum(["call", "sms", "email", "task"]);
var FollowUpStatusSchema = z21.enum(["pending", "done", "snoozed", "cancelled"]);
var AppointmentReminderStepSchema = z21.enum([
  "confirm",
  "remind",
  "no_show"
]);
var FollowUpSchema = z21.object({
  id: z21.string().uuid(),
  dealershipId: z21.string().uuid(),
  rooftopId: z21.string().uuid().nullable(),
  contactId: z21.string().uuid().nullable(),
  leadId: z21.string().uuid().nullable(),
  // Nullable: an escalation with no human available stays unassigned
  // rather than being parked on the AI (AI Task Management, Part 1).
  assignedUserId: z21.string().uuid().nullable(),
  dueAt: z21.string().datetime(),
  kind: FollowUpKindSchema,
  reason: z21.string().nullable(),
  appointmentReminderStep: AppointmentReminderStepSchema.nullable().optional(),
  body: z21.string().nullable(),
  status: FollowUpStatusSchema,
  completedAt: z21.string().datetime().nullable(),
  completedByUserId: z21.string().uuid().nullable(),
  createdAt: z21.string().datetime(),
  updatedAt: z21.string().datetime()
});
var CreateFollowUpSchema = z21.object({
  dealershipId: z21.string().uuid(),
  rooftopId: z21.string().uuid().nullable(),
  contactId: z21.string().uuid(),
  leadId: z21.string().uuid().nullable().optional(),
  assignedUserId: z21.string().uuid(),
  dueAt: z21.string().datetime(),
  kind: FollowUpKindSchema,
  reason: OptionalTextSchema,
  body: OptionalTextSchema,
  status: FollowUpStatusSchema.default("pending")
});

// src/dto/bdc/vehicles.dto.ts
import { z as z22 } from "zod";
var VehicleInterestLevelSchema = z22.enum([
  "casual",
  "considering",
  "ready_to_buy",
  "lost"
]);
var VehicleInterestStatusSchema = z22.enum(["open", "converted", "lost", "dropped"]);
var VehicleInterestSchema = z22.object({
  id: z22.string().uuid(),
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  inventoryVehicleId: z22.string().uuid().nullable(),
  vin: z22.string().nullable(),
  year: z22.number().int().nullable(),
  make: z22.string().nullable(),
  model: z22.string().nullable(),
  trim: z22.string().nullable(),
  interestLevel: VehicleInterestLevelSchema.nullable(),
  firstMentionedAt: z22.string().datetime().nullable(),
  lastMentionedAt: z22.string().datetime().nullable(),
  leadId: z22.string().uuid().nullable(),
  status: VehicleInterestStatusSchema,
  notes: z22.string().nullable(),
  createdAt: z22.string().datetime(),
  updatedAt: z22.string().datetime()
});
var CreateVehicleInterestSchema = z22.object({
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  inventoryVehicleId: z22.string().uuid().nullable().optional(),
  vin: OptionalTextSchema,
  year: z22.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  interestLevel: VehicleInterestLevelSchema.nullable().optional(),
  firstMentionedAt: z22.string().datetime().nullable().optional(),
  lastMentionedAt: z22.string().datetime().nullable().optional(),
  leadId: z22.string().uuid().nullable().optional(),
  status: VehicleInterestStatusSchema.default("open"),
  notes: OptionalTextSchema
});
var ContactVehicleRelationshipSchema = z22.enum([
  "owns",
  "leases",
  "previously_owned"
]);
var ContactVehicleSchema = z22.object({
  id: z22.string().uuid(),
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  vin: z22.string().nullable(),
  year: z22.number().int().nullable(),
  make: z22.string().nullable(),
  model: z22.string().nullable(),
  trim: z22.string().nullable(),
  color: z22.string().nullable(),
  mileage: z22.number().int().nullable(),
  relationship: ContactVehicleRelationshipSchema,
  purchasedFromThisDealer: z22.boolean().nullable(),
  purchaseDate: DateSchema.nullable(),
  lastServiceAt: z22.string().datetime().nullable(),
  nextServiceDueAt: z22.string().datetime().nullable(),
  source: z22.string().nullable(),
  notes: z22.string().nullable(),
  createdAt: z22.string().datetime(),
  updatedAt: z22.string().datetime()
});
var CreateContactVehicleSchema = z22.object({
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  vin: OptionalTextSchema,
  year: z22.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  color: OptionalTextSchema,
  mileage: z22.number().int().nullable().optional(),
  relationship: ContactVehicleRelationshipSchema,
  purchasedFromThisDealer: z22.boolean().nullable().optional(),
  purchaseDate: DateSchema.nullable().optional(),
  lastServiceAt: z22.string().datetime().nullable().optional(),
  nextServiceDueAt: z22.string().datetime().nullable().optional(),
  source: OptionalTextSchema,
  notes: OptionalTextSchema
});
var TradeInStatusSchema = z22.enum([
  "submitted",
  "appraising",
  "offered",
  "accepted",
  "rejected",
  "withdrawn"
]);
var TradeInConditionSchema = z22.enum(["excellent", "good", "fair", "poor"]);
var TradeInSchema = z22.object({
  id: z22.string().uuid(),
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  leadId: z22.string().uuid().nullable(),
  vin: z22.string().nullable(),
  year: z22.number().int().nullable(),
  make: z22.string().nullable(),
  model: z22.string().nullable(),
  trim: z22.string().nullable(),
  color: z22.string().nullable(),
  mileage: z22.number().int().nullable(),
  condition: TradeInConditionSchema.nullable(),
  hasAccidents: z22.boolean().nullable(),
  accidentNotes: z22.string().nullable(),
  payoffAmount: z22.number().nullable(),
  payoffLender: z22.string().nullable(),
  expectedValue: z22.number().nullable(),
  offeredValue: z22.number().nullable(),
  appraisedAt: z22.string().datetime().nullable(),
  appraisedByUserId: z22.string().uuid().nullable(),
  kbbValue: z22.number().nullable(),
  blackBookValue: z22.number().nullable(),
  manheimValue: z22.number().nullable(),
  photos: z22.array(z22.string()).nullable(),
  status: TradeInStatusSchema,
  notes: z22.string().nullable(),
  createdAt: z22.string().datetime(),
  updatedAt: z22.string().datetime()
});
var CreateTradeInSchema = z22.object({
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  leadId: z22.string().uuid().nullable().optional(),
  vin: OptionalTextSchema,
  year: z22.number().int().nullable().optional(),
  make: OptionalTextSchema,
  model: OptionalTextSchema,
  trim: OptionalTextSchema,
  color: OptionalTextSchema,
  mileage: z22.number().int().nullable().optional(),
  condition: TradeInConditionSchema.nullable().optional(),
  hasAccidents: z22.boolean().nullable().optional(),
  accidentNotes: OptionalTextSchema,
  payoffAmount: z22.number().nullable().optional(),
  payoffLender: OptionalTextSchema,
  expectedValue: z22.number().nullable().optional(),
  offeredValue: z22.number().nullable().optional(),
  appraisedAt: z22.string().datetime().nullable().optional(),
  appraisedByUserId: z22.string().uuid().nullable().optional(),
  photos: z22.array(z22.string()).nullable().optional(),
  status: TradeInStatusSchema.default("submitted"),
  notes: OptionalTextSchema
});
var ConsentChannelTypeSchema = z22.enum(["sms", "email", "voice"]);
var ConsentEventTypeSchema = z22.enum(["opt_in", "opt_out"]);
var ConsentScopeSchema = z22.enum(["marketing", "transactional", "all"]);
var ConsentSourceSchema = z22.enum([
  "webform_checkbox",
  "reply_keyword",
  "one_click_link",
  "dealer_manual",
  "adf_disclosure",
  "api",
  // The AI recognized a natural-language opt-out ("please stop texting
  // me") and recorded it via the ai_opt_out escalation (C1).
  "ai_detected",
  // Opt-out carried in from a prior CRM via the contact CSV import (O2)
  // — previously-recorded consent decisions must survive the migration.
  "import"
]);
var ConsentEventSchema = z22.object({
  id: z22.string().uuid(),
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  contactChannelId: z22.string().uuid().nullable(),
  channelType: ConsentChannelTypeSchema,
  event: ConsentEventTypeSchema,
  scope: ConsentScopeSchema,
  source: ConsentSourceSchema,
  actorUserId: z22.string().uuid().nullable(),
  evidence: JsonObjectSchema.nullable(),
  occurredAt: z22.string().datetime(),
  createdAt: z22.string().datetime()
});
var CreateConsentEventSchema = z22.object({
  dealershipId: z22.string().uuid(),
  rooftopId: z22.string().uuid().nullable(),
  contactId: z22.string().uuid(),
  contactChannelId: z22.string().uuid().nullable().optional(),
  channelType: ConsentChannelTypeSchema,
  event: ConsentEventTypeSchema,
  scope: ConsentScopeSchema,
  source: ConsentSourceSchema,
  actorUserId: z22.string().uuid().nullable().optional(),
  evidence: JsonObjectSchema.nullable().optional()
});

// src/dto/bdc/pages.dto.ts
import { z as z23 } from "zod";
var ContactListSchema = z23.array(ContactSchema);
var ContactPageSchema = z23.object({
  items: ContactListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ContactChannelListSchema = z23.array(ContactChannelSchema);
var ContactChannelPageSchema = z23.object({
  items: ContactChannelListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var InventoryVehicleListSchema = z23.array(InventoryVehicleSchema);
var InventoryVehiclePageSchema = z23.object({
  items: InventoryVehicleListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ChannelListSchema = z23.array(ChannelSchema);
var ChannelPageSchema = z23.object({
  items: ChannelListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var LeadListSchema = z23.array(LeadSchema);
var LeadPageSchema = z23.object({
  items: LeadListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ContactLeadViewSchema = z23.enum(["all", "open", "closed"]);
var ContactLeadQuerySchema = z23.object({
  view: ContactLeadViewSchema.default("open"),
  page: z23.coerce.number().int().min(1).default(1),
  pageSize: z23.coerce.number().int().min(1).max(100).default(20)
});
var ContactLeadCountsSchema = z23.object({
  all: z23.number().int().nonnegative(),
  open: z23.number().int().nonnegative(),
  closed: z23.number().int().nonnegative(),
  won: z23.number().int().nonnegative(),
  lost: z23.number().int().nonnegative()
});
var ContactLeadPageSchema = LeadPageSchema.extend({
  counts: ContactLeadCountsSchema
});
var LeadPipelineSortSchema = z23.enum([
  "activity_desc",
  "activity_asc",
  "created_desc",
  "created_asc",
  "name_asc",
  "name_desc",
  "stage_asc",
  "owner_asc",
  "location_asc",
  "source_asc",
  "next_action_asc"
]);
var LeadPipelineDateSchema = z23.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === (month ?? 1) - 1 && parsed.getUTCDate() === day;
}, "Invalid calendar date");
var LeadPipelineNextActionSchema = z23.enum([
  "needs_attention",
  "reply_overdue",
  "appointment",
  "follow_up",
  "unset"
]);
var LeadPipelineFiltersObjectSchema = z23.object({
  search: z23.string().trim().max(200).optional(),
  stage: z23.enum(["new", "working", "appointment", "won", "lost"]).optional(),
  type: z23.enum(["sales", "service", "parts"]).optional(),
  assignedUserId: z23.string().uuid().optional(),
  unassigned: z23.enum(["true"]).optional(),
  rooftopId: z23.string().uuid().optional(),
  source: z23.string().trim().min(1).max(200).optional(),
  createdFrom: LeadPipelineDateSchema.optional(),
  createdTo: LeadPipelineDateSchema.optional(),
  lastActivityFrom: LeadPipelineDateSchema.optional(),
  lastActivityTo: LeadPipelineDateSchema.optional(),
  nextAction: LeadPipelineNextActionSchema.optional(),
  includeAnonymous: z23.enum(["true", "false"]).optional()
});
function validateLeadPipelineWindow(value, context) {
  if (value.assignedUserId && value.unassigned === "true") {
    context.addIssue({
      code: z23.ZodIssueCode.custom,
      path: ["unassigned"],
      message: "Choose either an assigned owner or Unassigned, not both"
    });
  }
  if (value.createdFrom && value.createdTo && value.createdFrom > value.createdTo) {
    context.addIssue({
      code: z23.ZodIssueCode.custom,
      path: ["createdTo"],
      message: "Created-to date must be on or after created-from date"
    });
  }
  if (value.lastActivityFrom && value.lastActivityTo && value.lastActivityFrom > value.lastActivityTo) {
    context.addIssue({
      code: z23.ZodIssueCode.custom,
      path: ["lastActivityTo"],
      message: "Activity-to date must be on or after activity-from date"
    });
  }
}
var LeadPipelineFiltersSchema = LeadPipelineFiltersObjectSchema.superRefine(
  validateLeadPipelineWindow
);
var LeadListQuerySchema = LeadPipelineFiltersObjectSchema.extend({
  page: z23.coerce.number().int().min(1).default(1),
  pageSize: z23.coerce.number().int().min(1).max(100).default(25),
  sort: LeadPipelineSortSchema.default("activity_desc"),
  closed: z23.enum(["true", "false"]).optional(),
  contactId: z23.string().uuid().optional()
}).superRefine(validateLeadPipelineWindow);
var LeadBoardColumnQuerySchema = LeadPipelineFiltersObjectSchema.extend({
  status: z23.enum(["new", "working", "appointment", "won", "lost"]),
  cursor: z23.string().min(1).optional(),
  limit: z23.coerce.number().int().min(1).max(100).default(50)
}).superRefine(validateLeadPipelineWindow);
var LeadFeedSchema = z23.object({
  items: LeadListSchema,
  nextCursor: z23.string().nullable()
});
var InboxViewSchema = z23.enum([
  "all",
  "unassigned",
  "assigned",
  "assigned_to_me",
  "ai",
  "escalated",
  "needs_human",
  "ai_suppressed",
  "unresponded",
  "hot",
  "engaged",
  "scheduled",
  "needs_you",
  "snoozed",
  // C2 (approval mode): leads holding at least one AI draft awaiting
  // approval (`delivery_status='pending_approval'`).
  "pending_approval"
]);
var InboxCountsSchema = z23.record(z23.string(), z23.number().int().nonnegative());
var LeadBandSchema = z23.union([
  z23.literal(1),
  z23.literal(2),
  z23.literal(3),
  z23.literal(4),
  z23.literal(5),
  z23.literal(6),
  z23.literal(7),
  z23.literal(8)
]);
var BandedLeadSchema = LeadSchema.extend({
  band: LeadBandSchema,
  bandReason: z23.string(),
  bandAt: z23.string().datetime()
});
var NeedsYouFeedSchema = z23.object({
  items: z23.array(BandedLeadSchema),
  counts: z23.record(z23.string(), z23.number().int().nonnegative()),
  nextCursor: z23.string().nullable()
});
var LeadBoardColumnSchema = z23.object({
  status: z23.enum(["new", "working", "appointment", "won", "lost"]),
  count: z23.number(),
  leads: LeadListSchema,
  nextCursor: z23.string().nullable()
});
var LeadBoardSchema = z23.object({
  columns: z23.array(LeadBoardColumnSchema)
});
var ActivityListSchema = z23.array(ActivitySchema);
var ActivityPageSchema = z23.object({
  items: ActivityListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ContactActivityFilterSchema = z23.enum([
  "focused",
  "messages",
  "calls",
  "email",
  "notes",
  "appointments",
  "all"
]);
var ContactActivityQuerySchema = z23.object({
  filter: ContactActivityFilterSchema.default("focused"),
  page: z23.coerce.number().int().min(1).default(1),
  pageSize: z23.coerce.number().int().min(1).max(100).default(20)
});
var ContactActivityPageSchema = ActivityPageSchema.extend({
  /** Only Leads referenced by this page, already scoped for the operator. */
  leads: LeadListSchema
});
var AppointmentListSchema = z23.array(AppointmentSchema);
var AppointmentPageSchema = z23.object({
  items: AppointmentListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var CalendarAppointmentPageSchema = z23.object({
  items: z23.array(CalendarAppointmentSchema),
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var FollowUpListSchema = z23.array(FollowUpSchema);
var VehicleInterestListSchema = z23.array(VehicleInterestSchema);
var ContactVehicleListSchema = z23.array(ContactVehicleSchema);
var TradeInListSchema = z23.array(TradeInSchema);
var ConsentEventListSchema = z23.array(ConsentEventSchema);
var FollowUpPageSchema = z23.object({
  items: FollowUpListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var VehicleInterestPageSchema = z23.object({
  items: VehicleInterestListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ContactVehiclePageSchema = z23.object({
  items: ContactVehicleListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var TradeInPageSchema = z23.object({
  items: TradeInListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var ConsentEventPageSchema = z23.object({
  items: ConsentEventListSchema,
  total: z23.number().int().nonnegative(),
  page: PageNumberSchema,
  pageSize: PageNumberSchema
});
var TagListSchema = z23.array(TagSchema);
var ServiceTypeListSchema = z23.array(ServiceTypeSchema);
var ContactTagListSchema = z23.array(ContactTagSchema);
var AiAgentListSchema = z23.array(AiAgentSchema);
var AiSkillListSchema = z23.array(AiSkillSchema);
var AiAgentChannelAssignmentListSchema = z23.array(AiAgentChannelAssignmentSchema);

// src/dto/bdc/reports.dto.ts
import { z as z24 } from "zod";
var ReportCurrentSlaBreachesQuerySchema = z24.object({
  page: z24.coerce.number().int().min(1).default(1),
  pageSize: z24.coerce.number().int().min(1).max(100).default(10),
  rooftopId: z24.string().uuid().optional(),
  assignedUserId: z24.string().uuid().optional(),
  type: z24.enum(["sales", "service", "parts"]).optional()
});
var ReportCurrentSlaBreachesPageSchema = z24.object({
  items: z24.array(LeadSchema),
  total: z24.number().int().nonnegative(),
  page: z24.number().int().positive(),
  pageSize: z24.number().int().positive(),
  asOf: z24.string().datetime()
});
var ReportVolumeSchema = z24.object({
  byDay: z24.array(
    z24.object({
      date: z24.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      inbound: z24.number().int().nonnegative(),
      outbound: z24.number().int().nonnegative()
    })
  ),
  byChannel: z24.array(
    z24.object({
      channel: z24.string(),
      count: z24.number().int().nonnegative()
    })
  ),
  byAgent: z24.array(
    z24.object({
      // Keyed by user id (M3) — display names collide and mutate, so
      // nothing may join report rows on `name` again.
      userId: z24.string().uuid(),
      name: z24.string(),
      count: z24.number().int().nonnegative()
    })
  )
});
var ReportResponseTrendPointSchema = z24.object({
  day: z24.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  medianSeconds: z24.number().nullable(),
  unanswered: z24.number().int().nonnegative()
});
var ReportResponseTimeSchema = z24.object({
  value: z24.number().nullable(),
  medianSeconds: z24.number().nullable(),
  sample: z24.number().int().nonnegative(),
  answeredWithinTargetPct: z24.number().min(0).max(1).nullable(),
  unansweredCount: z24.number().int().nonnegative(),
  trend: z24.array(ReportResponseTrendPointSchema)
});
var ReportLeaderboardRowSchema = z24.object({
  userId: z24.string().uuid(),
  name: z24.string(),
  kind: z24.enum(["human", "ai"]),
  medianResponseSeconds: z24.number().nullable(),
  answeredCount: z24.number().int().nonnegative(),
  unansweredCount: z24.number().int().nonnegative()
});
var ReportLeaderboardSchema = z24.array(ReportLeaderboardRowSchema);
var ReportFunnelSchema = z24.record(z24.string(), z24.number().int().nonnegative());
var ReportSourceRoiRowSchema = z24.object({
  source: z24.string(),
  leads: z24.number().int().nonnegative(),
  appointments: z24.number().int().nonnegative(),
  shows: z24.number().int().nonnegative(),
  sold: z24.number().int().nonnegative().nullable()
});
var ReportSourceRoiSchema = z24.array(ReportSourceRoiRowSchema);
var ReportAiContainmentSchema = z24.object({
  aiOnlyPct: z24.number().min(0).max(1),
  aiAssistedPct: z24.number().min(0).max(1),
  humanOnlyPct: z24.number().min(0).max(1),
  denominator: z24.number().int().nonnegative()
});
var ReportVoiceStatsSchema = z24.object({
  inboundCalls: z24.number().int().nonnegative(),
  outboundDials: z24.number().int().nonnegative(),
  /** human-answered ÷ AMD-known outbound dials; null when none known. */
  connectRate: z24.number().min(0).max(1).nullable(),
  /** AMD outcome counts over outbound dials (e.g. human / machine_end_beep). */
  amdMix: z24.record(z24.string(), z24.number().int().nonnegative()),
  /** Mean CallDuration over calls that reported one. */
  avgTalkTimeSec: z24.number().nonnegative().nullable(),
  /** Relay sessions the AI handled end-to-end. */
  aiHandled: z24.number().int().nonnegative(),
  /** Relay sessions warm-transferred to a human (V2). */
  transferred: z24.number().int().nonnegative(),
  /** transferred ÷ (aiHandled + transferred); null when neither exists. */
  transferRate: z24.number().min(0).max(1).nullable(),
  firstTokenMsP50: z24.number().nonnegative().nullable(),
  firstTokenMsP95: z24.number().nonnegative().nullable()
});
var AiDailyRecapSchema = z24.object({
  answered: z24.number().int().nonnegative(),
  booked: z24.number().int().nonnegative(),
  escalated: z24.number().int().nonnegative(),
  awaitingApproval: z24.number().int().nonnegative()
});

// src/dto/billing.dto.ts
var AiUsageBreakdownRowSchema = z25.object({
  /** Group key: provider, model, kind, day (YYYY-MM-DD), or dealership uuid. */
  key: z25.string(),
  /** Human label for the key — same as `key` unless a join enriches it. */
  label: z25.string(),
  inputTokens: z25.number(),
  outputTokens: z25.number(),
  cacheReadTokens: z25.number(),
  cacheWriteTokens: z25.number(),
  units: z25.number().nullable(),
  durationMs: z25.number().nullable(),
  costUsd: z25.number(),
  /** True if any row in this group used a fallback price (no exact model match). */
  estimated: z25.boolean(),
  /** True if any row had no price match at all (cost = 0). */
  unpriced: z25.boolean()
});
var AiUsageSummarySchema = z25.object({
  start: z25.string(),
  end: z25.string(),
  totals: z25.object({
    inputTokens: z25.number(),
    outputTokens: z25.number(),
    cacheReadTokens: z25.number(),
    cacheWriteTokens: z25.number(),
    costUsd: z25.number(),
    rows: z25.number()
  }),
  byProvider: z25.array(AiUsageBreakdownRowSchema),
  byModel: z25.array(AiUsageBreakdownRowSchema),
  byKind: z25.array(AiUsageBreakdownRowSchema),
  byDay: z25.array(AiUsageBreakdownRowSchema)
});
var DealershipBillingSummarySchema = z25.object({
  dealershipId: z25.string().uuid(),
  start: z25.string(),
  end: z25.string(),
  ai: AiUsageSummarySchema,
  twilio: TelephonyUsageSummarySchema,
  combined: z25.object({
    totalUsd: z25.number(),
    aiUsd: z25.number(),
    twilioUsd: z25.number()
  })
});
var PlatformBillingSummarySchema = z25.object({
  start: z25.string(),
  end: z25.string(),
  ai: AiUsageSummarySchema,
  /** Top-N dealerships by combined spend in the period. */
  topDealerships: z25.array(
    z25.object({
      dealershipId: z25.string().uuid(),
      dealershipName: z25.string(),
      aiUsd: z25.number(),
      twilioUsd: z25.number(),
      totalUsd: z25.number()
    })
  ),
  totals: z25.object({
    aiUsd: z25.number(),
    twilioUsd: z25.number(),
    totalUsd: z25.number()
  }),
  /**
   * Platform Twilio total minus the sum of distinct attributed account
   * costs. Non-zero when an account has no dealership association (e.g.
   * a shared or orphan subaccount) — surfaced here rather than silently
   * dropped. Floored at 0.
   */
  unattributedTwilioUsd: z25.number(),
  /**
   * Min/max day present in twilio_usage_records within the requested
   * range. Null when there are no records. Used by the UI to flag a
   * coverage gap when the synced span is narrower than the query range.
   */
  twilioCoverage: z25.object({ minDay: z25.string(), maxDay: z25.string() }).nullable()
});
var BillingRangeSchema = z25.object({
  start: z25.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD"),
  end: z25.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
});

// src/dto/auth.dto.ts
import { z as z28 } from "zod";

// src/dto/dealership.dto.ts
import { z as z27 } from "zod";

// src/dto/user.dto.ts
import { z as z26 } from "zod";
var UserStatusSchema = z26.enum(["pending", "active", "inactive"]);
var UserKindSchema = z26.enum(["human", "ai"]);
var MembershipRoleSchema = z26.enum([
  "dealership_owner",
  "dealership_admin",
  "sales_manager",
  "service_manager",
  "bdc_agent",
  "sales_agent",
  "service_agent",
  "readonly"
]);
var InboxVisibilitySchema = z26.enum([
  "assigned_only",
  "assigned_and_unassigned",
  "rooftop_all",
  "group_all"
]);
var NullableStringSchema = z26.string().trim().min(1).max(120).nullable().optional();
var UserSchema = z26.object({
  id: z26.string().uuid(),
  dealershipId: z26.string().uuid(),
  email: z26.string().email().nullable(),
  phone: z26.string().nullable(),
  firstName: z26.string().nullable(),
  lastName: z26.string().nullable(),
  displayName: z26.string().nullable(),
  // CDN URL of the re-hosted avatar (S3 public bucket) — never a raw
  // third-party URL.
  avatarUrl: z26.string().nullable(),
  department: z26.string().nullable(),
  jobTitle: z26.string().nullable(),
  // Per-rep signature HTML appended to outbound customer emails (D3);
  // falls back to the rooftop default when null. Defaulted so payloads
  // serialized before the column shipped still parse.
  emailSignatureHtml: z26.string().nullable().default(null),
  kind: UserKindSchema,
  status: UserStatusSchema,
  roundRobinEnabled: z26.boolean(),
  lastAssignedAt: z26.string().datetime().nullable(),
  lastLoginAt: z26.string().datetime().nullable(),
  createdAt: z26.string().datetime(),
  updatedAt: z26.string().datetime()
});
var CreateUserSchema = z26.object({
  dealershipId: z26.string().uuid(),
  email: z26.string().email().optional().nullable(),
  phone: OptionalPhoneE164Schema,
  firstName: NullableStringSchema,
  lastName: NullableStringSchema,
  displayName: NullableStringSchema,
  department: NullableStringSchema,
  jobTitle: NullableStringSchema,
  emailSignatureHtml: z26.string().trim().max(2e4).nullable().optional(),
  kind: UserKindSchema.default("human"),
  status: UserStatusSchema.default("pending"),
  roundRobinEnabled: z26.boolean().default(true)
});
var UpdateUserSchema = CreateUserSchema.omit({ dealershipId: true }).partial();
var UpdateMyProfileSchema = z26.object({
  firstName: NullableStringSchema,
  lastName: NullableStringSchema,
  displayName: NullableStringSchema,
  phone: OptionalPhoneE164Schema,
  jobTitle: NullableStringSchema,
  emailSignatureHtml: z26.string().trim().max(2e4).nullable().optional()
});
var InviteLocationsSchema = z26.union([
  z26.literal("all"),
  z26.array(z26.string().uuid()).min(1, "Choose at least one location.")
]);
var InviteUserSchema = z26.object({
  email: z26.string().email(),
  firstName: NullableStringSchema,
  lastName: NullableStringSchema,
  displayName: NullableStringSchema,
  jobTitle: NullableStringSchema,
  department: NullableStringSchema,
  phone: OptionalPhoneE164Schema,
  status: UserStatusSchema.default("pending"),
  roundRobinEnabled: z26.boolean().default(true),
  role: MembershipRoleSchema,
  locations: InviteLocationsSchema,
  inboxVisibility: InboxVisibilitySchema.default("assigned_and_unassigned")
});
var UserMembershipSchema = z26.object({
  id: z26.string().uuid(),
  userId: z26.string().uuid(),
  dealershipId: z26.string().uuid(),
  rooftopId: z26.string().uuid().nullable(),
  role: MembershipRoleSchema,
  inboxVisibility: InboxVisibilitySchema,
  maxOpenLeads: z26.number().int().nullable().default(null),
  workingHours: WeeklyBusinessHoursSchema.nullable().default(null),
  permissions: z26.record(z26.unknown()).nullable(),
  createdAt: z26.string().datetime(),
  updatedAt: z26.string().datetime()
});
var CreateUserMembershipSchema = z26.object({
  userId: z26.string().uuid(),
  dealershipId: z26.string().uuid(),
  rooftopId: z26.string().uuid().nullable().optional(),
  role: MembershipRoleSchema,
  inboxVisibility: InboxVisibilitySchema.default("assigned_and_unassigned"),
  maxOpenLeads: z26.number().int().min(1).max(500).nullable().optional(),
  workingHours: WeeklyBusinessHoursSchema.nullable().optional(),
  permissions: z26.record(z26.unknown()).nullable().optional()
});
var UpdateUserMembershipSchema = CreateUserMembershipSchema.omit({
  userId: true,
  dealershipId: true
}).partial();
var UserListSchema = z26.array(UserSchema);
var UserMembershipListSchema = z26.array(UserMembershipSchema);
var AVATAR_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];
var CreateAvatarUploadUrlSchema = z26.object({
  fileName: z26.string().trim().min(1).max(255).optional(),
  mimeType: z26.enum(AVATAR_MIME)
});
var AvatarUploadUrlResultSchema = z26.object({
  uploadUrl: z26.string().url(),
  /** Staging key to send back to POST /users/me/avatar. */
  key: z26.string(),
  /** ISO timestamp the signed URL expires; the client should not retry past this. */
  expiresAt: z26.string().datetime()
});
var SetAvatarSchema = z26.object({
  /** Staging key returned by the upload-url endpoint. */
  key: z26.string().min(1).max(1024)
});

// src/dto/dealership.dto.ts
var DealershipStatusSchema = z27.enum([
  "onboarding",
  "pilot",
  "active",
  "paused",
  "inactive"
]);
var RooftopStatusSchema = z27.enum(["onboarding", "active", "inactive"]);
var DealershipCountrySchema = z27.enum(["US", "CA"]);
var OptionalTextSchema2 = z27.string().trim().min(1).max(200).nullable().optional();
var DealershipSchema = z27.object({
  id: z27.string().uuid(),
  name: z27.string().min(1),
  legalName: z27.string().nullable(),
  ein: z27.string().nullable(),
  status: DealershipStatusSchema,
  // Defaulted so payloads serialized before the country rollout still parse.
  country: DealershipCountrySchema.default("US"),
  primaryTimezone: z27.string().min(1),
  // Whether AI escalations engage the human-handoff workflow + alerts
  // (true) or are recorded silently (false). Defaulted so payloads
  // serialized before this flag shipped still parse.
  escalationsEnabled: z27.boolean().default(false),
  // Defaulted so payloads serialized before archiving shipped still parse.
  // null = live; ISO timestamp = archived since that point.
  archivedAt: z27.string().datetime().nullable().default(null),
  createdAt: z27.string().datetime(),
  updatedAt: z27.string().datetime()
});
var CreateDealershipSchema = z27.object({
  name: z27.string().trim().min(1).max(200),
  legalName: OptionalTextSchema2,
  ein: OptionalEinSchema,
  status: DealershipStatusSchema.default("onboarding"),
  country: DealershipCountrySchema.default("US"),
  primaryTimezone: z27.string().trim().min(1).max(64).default("America/New_York"),
  escalationsEnabled: z27.boolean().default(false)
});
var UpdateDealershipSchema = CreateDealershipSchema.partial();
var RooftopSchema = z27.object({
  id: z27.string().uuid(),
  dealershipId: z27.string().uuid(),
  name: z27.string().min(1),
  status: RooftopStatusSchema,
  websiteUrl: z27.string().url().nullable(),
  primaryEmail: z27.string().email().nullable(),
  primaryPhone: z27.string().nullable(),
  state: z27.string().nullable(),
  city: z27.string().nullable(),
  zip: z27.string().nullable(),
  addressLine1: z27.string().nullable(),
  timezone: z27.string(),
  salesHours: z27.record(z27.unknown()),
  serviceHours: z27.record(z27.unknown()),
  // Per-department "not applicable" flags (inverse: true = this rooftop
  // does NOT run that department). Defaulted so payloads serialized
  // before these flags shipped still parse, and so absent reads as
  // "department exists" — non-breaking for every existing rooftop.
  salesNotApplicable: z27.boolean().default(false),
  serviceNotApplicable: z27.boolean().default(false),
  partsNotApplicable: z27.boolean().default(false),
  // First-response SLA targets in minutes (B2). Defaulted so payloads
  // serialized before the SLA engine shipped still parse.
  slaMinutesSales: z27.number().int().default(5),
  slaMinutesService: z27.number().int().default(15),
  // Rooftop-default email signature HTML, appended to outbound customer
  // emails when the sending rep has none of their own (D3). Defaulted so
  // payloads serialized before the column shipped still parse.
  emailSignatureHtml: z27.string().nullable().default(null),
  createdAt: z27.string().datetime(),
  updatedAt: z27.string().datetime()
});
var CreateRooftopSchema = z27.object({
  dealershipId: z27.string().uuid(),
  name: z27.string().trim().min(1).max(200),
  status: RooftopStatusSchema.default("active"),
  websiteUrl: OptionalUrlSchema,
  primaryEmail: OptionalEmailSchema,
  primaryPhone: OptionalPhoneE164Schema,
  state: OptionalStateOrProvinceCodeSchema,
  city: OptionalTextSchema2,
  zip: OptionalZipOrPostalCodeSchema,
  addressLine1: OptionalTextSchema2,
  timezone: z27.string().trim().min(1).max(64).default("America/New_York"),
  salesHours: z27.record(z27.unknown()).default({}),
  serviceHours: z27.record(z27.unknown()).default({}),
  salesNotApplicable: z27.boolean().default(false),
  serviceNotApplicable: z27.boolean().default(false),
  partsNotApplicable: z27.boolean().default(false),
  emailSignatureHtml: z27.string().trim().max(2e4).nullable().optional()
});
var UpdateRooftopSchema = CreateRooftopSchema.omit({ dealershipId: true }).partial();
var UpdateRooftopScheduleSchema = z27.object({
  timezone: z27.string().trim().min(1).max(64),
  salesHours: WeeklyBusinessHoursSchema,
  serviceHours: WeeklyBusinessHoursSchema,
  salesNotApplicable: z27.boolean().optional(),
  serviceNotApplicable: z27.boolean().optional(),
  // First-response SLA targets in minutes (B2). Optional so older
  // clients that omit them leave the existing values untouched.
  slaMinutesSales: z27.number().int().min(1).max(1440).optional(),
  slaMinutesService: z27.number().int().min(1).max(1440).optional()
});
var DealershipDetailSchema = DealershipSchema.extend({
  rooftops: z27.array(RooftopSchema),
  users: z27.array(UserSchema),
  memberships: z27.array(UserMembershipSchema)
});
var DealershipListSchema = z27.array(DealershipSchema);
var DealershipDirectorySortSchema = z27.enum([
  "name",
  "status",
  "createdAt",
  "updatedAt",
  "lastActivityAt"
]);
var DealershipDirectoryQuerySchema = z27.object({
  dealershipId: z27.string().uuid().optional(),
  search: z27.string().trim().max(200).optional(),
  status: DealershipStatusSchema.optional(),
  archived: z27.enum(["active", "archived", "all"]).default("active"),
  page: z27.coerce.number().int().min(1).default(1),
  pageSize: z27.coerce.number().int().min(1).max(100).default(25),
  sort: DealershipDirectorySortSchema.default("updatedAt"),
  direction: z27.enum(["asc", "desc"]).default("desc")
});
var DealershipDirectoryOnboardingSchema = z27.object({
  completedSteps: z27.number().int().min(0),
  totalSteps: z27.number().int().positive(),
  blockerCount: z27.number().int().min(0),
  ready: z27.boolean()
});
var DealershipDirectoryItemSchema = DealershipSchema.extend({
  rooftopCount: z27.number().int().min(0),
  humanUserCount: z27.number().int().min(0),
  onboarding: DealershipDirectoryOnboardingSchema,
  unhealthyChannelCount: z27.number().int().min(0),
  erroredIntegrationCount: z27.number().int().min(0),
  failedImportCount: z27.number().int().min(0),
  lastAdminActivityAt: z27.string().datetime().nullable()
});
var DealershipDirectoryPageSchema = z27.object({
  items: z27.array(DealershipDirectoryItemSchema),
  total: z27.number().int().min(0),
  page: z27.number().int().min(1),
  pageSize: z27.number().int().min(1)
});
var RooftopListSchema = z27.array(RooftopSchema);
var OnboardingCreateSchema = z27.object({
  dealership: CreateDealershipSchema,
  rooftops: z27.array(CreateRooftopSchema.omit({ dealershipId: true })).min(1),
  users: z27.array(
    CreateUserSchema.omit({ dealershipId: true }).extend({
      role: MembershipRoleSchema.default("dealership_owner"),
      rooftopId: z27.string().uuid().optional(),
      inboxVisibility: InboxVisibilitySchema.default("group_all")
    })
  )
});
var OnboardingStepIdSchema = z27.enum([
  "hours",
  "phone",
  "email",
  "inventory",
  "team",
  "test_ai"
]);
var DealershipOnboardingStateSchema = z27.object({
  steps: z27.record(
    OnboardingStepIdSchema,
    z27.object({
      done: z27.boolean(),
      updatedAt: z27.string().datetime().optional()
    })
  ).default({})
});
var OnboardingStepSchema = z27.object({
  id: OnboardingStepIdSchema,
  label: z27.string(),
  status: z27.enum(["done", "pending"]),
  /** What made it done: live provisioning state or a manual check-off. */
  source: z27.enum(["derived", "manual"]),
  /** Short operator-facing explanation of the current status. */
  detail: z27.string().nullable()
});
var OnboardingA2pStatusSchema = z27.object({
  telephonyAccountId: z27.string().uuid(),
  displayName: z27.string(),
  brandStatus: z27.string(),
  campaignStatus: z27.string(),
  summary: z27.string()
});
var DealershipOnboardingSchema = z27.object({
  dealershipId: z27.string().uuid(),
  steps: z27.array(OnboardingStepSchema),
  a2p: z27.array(OnboardingA2pStatusSchema),
  guidance: z27.object({
    a2pTimeline: z27.string(),
    porting: z27.string(),
    hostedSms: z27.string()
  }),
  goLive: z27.object({
    ready: z27.boolean(),
    blockers: z27.array(z27.string())
  })
});
var UpdateOnboardingStepSchema = z27.object({
  stepId: OnboardingStepIdSchema,
  done: z27.boolean()
});

// src/dto/auth.dto.ts
var AdminSchema = z28.object({
  id: z28.string().uuid(),
  email: z28.string().email(),
  firstName: z28.string().nullable(),
  lastName: z28.string().nullable(),
  displayName: z28.string().nullable(),
  role: z28.enum(["admin", "readonly"]),
  lastLoginAt: z28.string().datetime().nullable(),
  createdAt: z28.string().datetime(),
  updatedAt: z28.string().datetime()
});
var InviteAdminSchema = z28.object({
  email: z28.string().email(),
  role: z28.enum(["admin", "readonly"]).default("admin")
});
var AccessibleDealershipSchema = z28.object({
  userId: z28.string().uuid(),
  dealershipId: z28.string().uuid(),
  dealershipName: z28.string(),
  current: z28.boolean()
});
var AppMeSchema = z28.object({
  user: UserSchema,
  dealership: DealershipSchema,
  memberships: z28.array(UserMembershipSchema),
  // Defaulted so a frontend that expects the switcher list still parses a
  // response from a backend that predates it (rolling deploy window).
  accessibleDealerships: z28.array(AccessibleDealershipSchema).default([])
});
var DealershipChoiceSchema = z28.object({
  userId: z28.string().uuid(),
  dealershipId: z28.string().uuid(),
  dealershipName: z28.string()
});
var DealershipChoiceListSchema = z28.array(DealershipChoiceSchema);
var SelectDealershipSchema = z28.object({
  dealershipId: z28.string().uuid()
});
var GoogleCallbackSchema = z28.object({
  credential: z28.string().min(1),
  g_csrf_token: z28.string().min(1).optional()
});
var AdminMeSchema = z28.object({
  admin: AdminSchema
});
var RequestEmailCodeSchema = z28.object({
  email: z28.string().email()
});
var VerifyEmailCodeSchema = z28.object({
  email: z28.string().email(),
  /** 8 decimal digits; regex preserves leading zeros as a string. */
  code: z28.string().regex(/^\d{8}$/)
});
var EmailCodeLoginResultSchema = z28.object({
  kind: z28.enum(["session", "choice"])
});

// src/dto/notifications.dto.ts
import { z as z29 } from "zod";
var NotificationSeveritySchema = z29.enum(["escalation", "normal"]);
var NotificationCategorySchema = z29.enum([
  "ai",
  "engagement",
  "appointment",
  "followup",
  "mention",
  "channel",
  "task"
]);
var NotificationEventTypeSchema = z29.object({
  key: z29.string(),
  category: NotificationCategorySchema,
  severity: NotificationSeveritySchema,
  description: z29.string(),
  defaultInapp: z29.boolean()
});
var NotificationSchema = z29.object({
  id: z29.string().uuid(),
  dealershipId: z29.string().uuid(),
  userId: z29.string().uuid(),
  eventKey: z29.string(),
  title: z29.string(),
  body: z29.string(),
  payload: z29.record(z29.string(), z29.unknown()),
  sourceRecordType: z29.string().nullable(),
  sourceRecordId: z29.string().uuid().nullable(),
  createdAt: z29.string().datetime(),
  readAt: z29.string().datetime().nullable()
});
var NotificationPageSchema = z29.object({
  items: z29.array(NotificationSchema),
  // Opaque keyset cursor (`createdAt|id`). Pass it back as `before`
  // to fetch the next page. Null when there are no more rows.
  nextCursor: z29.string().nullable()
});
var NotificationCountSchema = z29.object({
  unread: z29.number().int().nonnegative()
});
var NotificationEventPreferenceSchema = z29.object({
  event: NotificationEventTypeSchema,
  inapp: z29.boolean(),
  inappOverridden: z29.boolean()
});
var NotificationSettingsSchema = z29.object({
  emailSummary: z29.boolean(),
  emailEscalations: z29.boolean(),
  summaryTimeLocal: z29.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  timezone: z29.string(),
  events: z29.array(NotificationEventPreferenceSchema)
});
var UpdateNotificationSettingsSchema = z29.object({
  emailSummary: z29.boolean(),
  emailEscalations: z29.boolean(),
  summaryTimeLocal: z29.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  timezone: z29.string().min(1).max(64),
  events: z29.array(
    z29.object({
      eventKey: z29.string().max(120),
      inapp: z29.boolean().nullable()
    })
  )
}).partial();
var PushConfigSchema = z29.object({
  enabled: z29.boolean(),
  publicKey: z29.string().nullable()
});
var PushSubscriptionInputSchema = z29.object({
  endpoint: z29.string().url().max(2048),
  keys: z29.object({
    p256dh: z29.string().min(1).max(512),
    auth: z29.string().min(1).max(512)
  })
});
var DeletePushSubscriptionSchema = z29.object({
  endpoint: z29.string().url().max(2048)
});
var ListNotificationsQuerySchema = z29.object({
  status: z29.enum(["unread", "all"]).default("all"),
  limit: z29.coerce.number().int().positive().max(100).default(20),
  // Opaque cursor from a previous page's `nextCursor`. A malformed
  // value is ignored (treated as first page), not rejected.
  before: z29.string().optional()
});

// src/dto/outreach.dto.ts
import { z as z30 } from "zod";
var OutreachStageSchema = z30.enum([
  "new",
  "ready_to_reach_out",
  "to_follow_up",
  "interested",
  "demo_scheduled",
  "negotiating",
  "won",
  "lost"
]);
var OUTREACH_STAGE_LABELS = {
  new: "New",
  ready_to_reach_out: "Ready to reach out",
  to_follow_up: "To follow up",
  interested: "Interested",
  demo_scheduled: "Demo scheduled",
  negotiating: "Negotiating",
  won: "Won",
  lost: "Lost"
};
var OUTREACH_TERMINAL_STAGES = ["won", "lost"];
var OutreachNoteKindSchema = z30.enum(["note", "call", "email", "linkedin", "meeting"]);
var OptionalText = (max = 200) => z30.string().trim().min(1).max(max).nullable().optional();
var OutreachProspectSchema = z30.object({
  id: z30.string().uuid(),
  companyName: z30.string().min(1),
  websiteUrl: z30.string().nullable(),
  linkedinUrl: z30.string().nullable(),
  contactName: z30.string().nullable(),
  contactTitle: z30.string().nullable(),
  email: z30.string().nullable(),
  phone: z30.string().nullable(),
  city: z30.string().nullable(),
  state: z30.string().nullable(),
  source: z30.string().nullable(),
  stage: OutreachStageSchema,
  ownerAdminId: z30.string().uuid().nullable(),
  nextFollowUpAt: z30.string().datetime().nullable(),
  demoAt: z30.string().datetime().nullable(),
  tags: z30.array(z30.string()),
  /** F8 — manual do-not-email flag; blocks the "Email prospect" action. */
  emailSuppressed: z30.boolean(),
  createdAt: z30.string().datetime(),
  updatedAt: z30.string().datetime()
});
var CreateOutreachProspectSchema = z30.object({
  companyName: z30.string().trim().min(1).max(200),
  websiteUrl: OptionalText(),
  linkedinUrl: OptionalText(),
  contactName: OptionalText(),
  contactTitle: OptionalText(),
  email: OptionalEmailSchema,
  phone: OptionalText(40),
  city: OptionalText(120),
  state: OptionalText(40),
  source: OptionalText(120),
  stage: OutreachStageSchema.default("new"),
  ownerAdminId: z30.string().uuid().nullable().optional(),
  nextFollowUpAt: z30.string().datetime().nullable().optional(),
  demoAt: z30.string().datetime().nullable().optional(),
  tags: z30.array(z30.string().trim().min(1).max(40)).max(20).default([])
});
var UpdateOutreachProspectSchema = CreateOutreachProspectSchema.partial().extend({
  /** F8 — manual do-not-email toggle. */
  emailSuppressed: z30.boolean().optional()
});
var OutreachNoteSchema = z30.object({
  id: z30.string().uuid(),
  prospectId: z30.string().uuid(),
  authorAdminId: z30.string().uuid().nullable(),
  kind: OutreachNoteKindSchema,
  body: z30.string(),
  createdAt: z30.string().datetime()
});
var CreateOutreachNoteSchema = z30.object({
  kind: OutreachNoteKindSchema.default("note"),
  body: z30.string().trim().min(1).max(5e3)
});
var ImportOutreachProspectsSchema = z30.object({
  rows: z30.array(CreateOutreachProspectSchema).min(1).max(500)
});
var ImportOutreachResultSchema = z30.object({
  created: z30.number().int().min(0),
  /** F8 — rows upsert-skipped because the email already exists. */
  skipped: z30.number().int().min(0)
});
var EmailOutreachProspectSchema = z30.object({
  subject: z30.string().trim().min(1).max(200),
  body: z30.string().trim().min(1).max(1e4)
});
var OutreachListQuerySchema = z30.object({
  search: z30.string().trim().max(200).optional(),
  stage: OutreachStageSchema.optional(),
  owner: z30.string().uuid().optional(),
  // Only the literal "true" enables the filter. `z.coerce.boolean()` would
  // wrongly treat "false"/"0" as true (every non-empty string is truthy).
  due: z30.string().optional().transform((v) => v === "true"),
  page: z30.coerce.number().int().min(1).default(1),
  limit: z30.coerce.number().int().min(1).max(200).default(50)
});
var OutreachProspectPageSchema = z30.object({
  items: z30.array(OutreachProspectSchema),
  total: z30.number().int().min(0),
  page: z30.number().int().min(1),
  limit: z30.number().int().min(1)
});
var OutreachSummarySchema = z30.object({
  total: z30.number().int().min(0),
  dueCount: z30.number().int().min(0),
  byStage: z30.record(OutreachStageSchema, z30.number().int().min(0))
});

// src/dto/tasks.dto.ts
import { z as z31 } from "zod";
var TaskStatusSchema = z31.enum([
  "open",
  "in_progress",
  "done",
  "cancelled",
  "snoozed"
]);
var TaskTypeSchema = z31.enum([
  "callback",
  "reply",
  "appointment_confirm",
  "no_show_recover",
  "manager_review",
  "crm_fix",
  "ai_draft_review",
  // A scheduled AI reply. Created when an inbound message would get a
  // delayed AI response (see the assignment-keyed reply delays); the
  // AI sweeps these by `todoAt` and answers when one comes due. It is
  // a real, visible task so the dealership can see the AI's pending
  // work — and a human reply completes it before the AI ever fires.
  "conversation.respond",
  // Transparent, task-driven AI behavior: every AI intent with timing
  // becomes a visible, cancellable row instead of an opaque agent loop.
  //  - follow_up: a richer follow-up the AI planned (schedule_follow_up
  //    / defer_reply) — sent customer-facing when it comes due.
  //  - recheck: scheduled on escalation at now + silence period; when it
  //    fires, the AI re-engages the customer or re-escalates if the
  //    hand-off was never picked up — making the silence window active.
  //  - handoff_check: verifies a teammate actually picked up a hand-off;
  //    if not, the AI nudges or takes the conversation back.
  "conversation.follow_up",
  "conversation.recheck",
  "conversation.handoff_check",
  // The AI spotted an opportunity a human should close (a buying
  // signal, a firm appointment ask, an explicit "talk to someone").
  // Unlike `escalate`, the AI keeps assisting — this is a positive,
  // collaborative hand-off, not "the AI is stuck." A human-actioned
  // task: it is NOT swept by the AiTaskTriggerCron; it lives as an
  // assigned task + a conversation flag (`needs_human_at`) + a
  // notification, cleared when a human takes over.
  "conversation.needs_human",
  // Reminder to send before a booked appointment (Draft 5 task model).
  // Cadence engine seeds these from service-type reminderTemplate;
  // also creatable manually for ad-hoc appointment prep.
  "appointment_reminder",
  // Long-term re-engagement check-in: 30 / 60 / 90-day drip touch for
  // leads that went cold without a sale. Distinct from conversation.follow_up
  // (short-term, conversation-scoped) — this is a multi-week cadence item.
  "re_engage",
  "other"
]);
var TaskReasonSchema = z31.enum([
  "ai_escalation",
  "callback_requested",
  "manual",
  "ai_appointment_request",
  "ai_no_response_followup",
  "ai_lost_lead_recovery",
  "other"
]);
var TaskViewSchema = z31.enum([
  "assigned_to_me",
  "ai_needs_help",
  // Leads the AI flagged for a human to close (`flag_for_human`)
  // — the open `conversation.needs_human` tasks.
  "needs_human",
  "unassigned",
  // All assigned, active work across the rooftop — the manager's
  // cross-team view, narrowable with the `assigneeUserId` filter.
  "team",
  // Whole-rooftop active tasks, assigned or not — the store-wide view.
  "store",
  "all",
  "overdue",
  "due_today"
]);
var TaskSchema = z31.object({
  id: z31.string().uuid(),
  dealershipId: z31.string().uuid(),
  rooftopId: z31.string().uuid().nullable(),
  contactId: z31.string().uuid().nullable(),
  leadId: z31.string().uuid().nullable(),
  title: z31.string(),
  description: z31.string().nullable(),
  status: TaskStatusSchema,
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.nullable(),
  createdByUserId: z31.string().uuid().nullable(),
  assignedUserId: z31.string().uuid().nullable(),
  todoAt: z31.string().datetime().nullable(),
  dueAt: z31.string().datetime().nullable(),
  availableAt: z31.string().datetime().nullable(),
  completedAt: z31.string().datetime().nullable(),
  completedByUserId: z31.string().uuid().nullable(),
  createdAt: z31.string().datetime(),
  updatedAt: z31.string().datetime(),
  /** Display context returned by list endpoints when the relations are loaded. */
  contactName: z31.string().nullable().optional(),
  leadSummary: z31.string().nullable().optional()
});
var TaskPageSchema = z31.object({
  items: z31.array(TaskSchema),
  total: z31.number().int().nonnegative(),
  page: z31.number().int().positive(),
  pageSize: z31.number().int().positive()
});
var CreateTaskSchema = z31.object({
  dealershipId: z31.string().uuid(),
  rooftopId: z31.string().uuid().nullable(),
  contactId: z31.string().uuid().nullable(),
  leadId: z31.string().uuid().nullable().optional(),
  title: z31.string().trim().min(1).max(200),
  description: z31.string().trim().max(8e3).nullable().optional(),
  taskType: TaskTypeSchema.default("other"),
  reason: TaskReasonSchema.nullable().optional(),
  assignedUserId: z31.string().uuid().nullable().optional(),
  todoAt: z31.string().datetime().nullable().optional(),
  dueAt: z31.string().datetime().nullable().optional(),
  availableAt: z31.string().datetime().nullable().optional()
});
var UpdateTaskSchema = z31.object({
  title: z31.string().trim().min(1).max(200),
  description: z31.string().trim().max(8e3).nullable(),
  taskType: TaskTypeSchema,
  reason: TaskReasonSchema.nullable(),
  todoAt: z31.string().datetime().nullable(),
  dueAt: z31.string().datetime().nullable(),
  availableAt: z31.string().datetime().nullable()
}).partial();
var SnoozeTaskSchema = z31.object({
  availableAt: z31.string().datetime()
});
var AssignTaskSchema = z31.object({
  assignedUserId: z31.string().uuid().nullable()
});
var MoveTaskSchema = z31.object({
  status: z31.enum(["open", "in_progress", "done", "cancelled"])
});
var TaskCreatedByFilterSchema = z31.enum(["human", "assistant", "all"]);
var LegacyIncludeAiQuerySchema = z31.preprocess(
  (value) => {
    if (value === "true") return true;
    if (value === "false") return false;
    return value;
  },
  z31.boolean()
);
var ListTasksQuerySchema = z31.object({
  view: TaskViewSchema.optional(),
  status: TaskStatusSchema.optional(),
  contactId: z31.string().uuid().optional(),
  leadId: z31.string().uuid().optional(),
  rooftopId: z31.string().uuid().optional(),
  assigneeUserId: z31.string().uuid().optional(),
  search: z31.string().trim().max(200).optional(),
  dueFrom: z31.string().datetime().optional(),
  dueTo: z31.string().datetime().optional(),
  availableOnly: z31.coerce.boolean().optional(),
  // Human work is the operational default. The explicit creator filter
  // makes Assistant provenance a normal query facet, not a second queue.
  createdBy: TaskCreatedByFilterSchema.optional(),
  // Backward compatibility for older clients. Prefer `createdBy`.
  includeAi: LegacyIncludeAiQuerySchema.optional(),
  page: z31.coerce.number().int().min(1).optional(),
  pageSize: z31.coerce.number().int().min(1).max(100).optional()
}).partial();
var TaskCountsQuerySchema = z31.object({
  createdBy: TaskCreatedByFilterSchema.optional(),
  // Backward compatibility for clients predating the exact creator facet.
  includeAi: LegacyIncludeAiQuerySchema.optional()
});
var TaskCountsSchema = z31.object({
  my: z31.number().int().nonnegative(),
  team: z31.number().int().nonnegative(),
  store: z31.number().int().nonnegative(),
  unassigned: z31.number().int().nonnegative(),
  mikaFollowups: z31.number().int().nonnegative(),
  mikaReminders: z31.number().int().nonnegative(),
  mikaReengage: z31.number().int().nonnegative()
});

// src/dto/health.dto.ts
import { z as z32 } from "zod";
var HealthStatusSchema = z32.object({
  status: z32.enum(["ok", "degraded", "down"]),
  uptimeSeconds: z32.number().nonnegative(),
  version: z32.string(),
  checks: z32.record(z32.string(), z32.enum(["ok", "down"])).optional()
});

// src/dto/ops.dto.ts
import { z as z33 } from "zod";
var OpsCronHeartbeatSchema = z33.object({
  name: z33.string(),
  lastRunAt: z33.string().datetime().nullable(),
  lastOkAt: z33.string().datetime().nullable(),
  note: z33.string().nullable()
});
var OpsQueueDepthSchema = z33.object({
  /** Queue name derived from the URL's last path segment. */
  name: z33.string(),
  /** ApproximateNumberOfMessages — visible backlog. */
  visible: z33.number().int().nullable(),
  /** ApproximateNumberOfMessagesNotVisible — in flight. */
  inFlight: z33.number().int().nullable(),
  /** Set when the GetQueueAttributes call failed for this queue. */
  error: z33.string().nullable()
});
var OpsAffectedDealershipSchema = z33.object({
  dealershipId: z33.string().uuid(),
  dealershipName: z33.string(),
  count: z33.number().int().min(1)
});
var OpsSnapshotSchema = z33.object({
  generatedAt: z33.string().datetime(),
  outbox: z33.object({
    queuedDepth: z33.number().int(),
    /** Age of the oldest still-queued outbound, seconds; null when empty. */
    oldestQueuedAgeSeconds: z33.number().int().nullable(),
    affectedDealerships: z33.array(OpsAffectedDealershipSchema)
  }),
  failedSendsLast24h: z33.array(
    z33.object({
      channelType: z33.string(),
      count: z33.number().int(),
      affectedDealerships: z33.array(OpsAffectedDealershipSchema)
    })
  ),
  unhealthyChannels: z33.array(
    z33.object({
      dealershipId: z33.string().uuid(),
      dealershipName: z33.string(),
      status: z33.string(),
      count: z33.number().int()
    })
  ),
  crons: z33.array(OpsCronHeartbeatSchema),
  queues: z33.array(OpsQueueDepthSchema),
  /** False when OPS_QUEUE_URLS is unset — the queues card says so. */
  queuesConfigured: z33.boolean()
});

// src/dto/inventory-import.dto.ts
import { z as z35 } from "zod";

// src/dto/inventory-rule.dto.ts
import { z as z34 } from "zod";
var InventoryRuleFieldSchema = z34.enum([
  "rooftop",
  "condition",
  "make",
  "fuel"
]);
var InventoryRuleOperatorSchema = z34.enum([
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "in",
  "not_in"
]);
var InventoryRuleCombinatorSchema = z34.enum(["all", "any"]);
var InventoryRuleSchema = z34.object({
  field: InventoryRuleFieldSchema,
  operator: InventoryRuleOperatorSchema,
  // `string` for equals/contains/not_equals/not_contains.
  // `string[]` (≤ 50 values) for in/not_in.
  value: z34.union([
    z34.string().trim().min(1).max(120),
    z34.array(z34.string().trim().min(1).max(120)).min(1).max(50)
  ])
});
var InventoryRuleGroupSchema = z34.lazy(
  () => z34.object({
    combinator: InventoryRuleCombinatorSchema,
    rules: z34.array(z34.union([InventoryRuleSchema, InventoryRuleGroupSchema])).max(50)
  })
);

// src/dto/inventory-import.dto.ts
var InventoryImportTypeSchema = z35.enum(["sftp", "scraping", "api"]);
var InventoryImportProviderSchema = z35.enum([
  "vauto",
  "spaceauto",
  "homenet",
  "website",
  "dealeron",
  "dealercom",
  "dealercloud",
  "teamvelocity",
  "overfuel",
  "dealerinspire",
  "lumika-json",
  "custom-csv"
]);
var InventoryColumnMapColumnsSchema = z35.object({
  vin: z35.string().min(1),
  stock: z35.string().optional(),
  status: z35.string().optional(),
  condition: z35.string().optional(),
  year: z35.string().optional(),
  make: z35.string().optional(),
  model: z35.string().optional(),
  trim: z35.string().optional(),
  body: z35.string().optional(),
  color: z35.string().optional(),
  interiorColor: z35.string().optional(),
  mileage: z35.string().optional(),
  driveTrain: z35.string().optional(),
  transmission: z35.string().optional(),
  engine: z35.string().optional(),
  fuel: z35.string().optional(),
  doors: z35.string().optional(),
  seats: z35.string().optional(),
  features: z35.string().optional(),
  msrp: z35.string().optional(),
  price: z35.string().optional(),
  listPrice: z35.string().optional(),
  offeredPrice: z35.string().optional(),
  photos: z35.string().optional(),
  vdpUrl: z35.string().optional(),
  description: z35.string().optional(),
  stockDate: z35.string().optional(),
  rooftop: z35.string().optional()
});
var InventoryColumnMapSchema = z35.object({
  columns: InventoryColumnMapColumnsSchema,
  statusValues: z35.record(z35.string(), z35.enum(["available", "intransit", "pending", "sold"])).optional(),
  listSeparator: z35.string().min(1).max(3).optional()
});
var InventoryImportSchema = z35.object({
  id: z35.string().uuid(),
  dealershipId: z35.string().uuid(),
  rooftopId: z35.string().uuid(),
  type: InventoryImportTypeSchema,
  provider: InventoryImportProviderSchema,
  // `null` rule means "match every parsed row" (consumer short-circuit).
  rule: InventoryRuleGroupSchema.nullable(),
  path: z35.string().nullable(),
  label: z35.string(),
  /**
   * Read-DTO masked summary of the encrypted-at-rest provider
   * credential. Cleartext never leaves the backend; this carries
   * `••••<last4>` for `api` imports and `null` for `sftp`/`scraping`.
   */
  tokenMasked: z35.string().nullable(),
  /**
   * Pinned Cars Commerce listing id for DealerInspire scrapes whose
   * auto-discovery is Cloudflare-challenged. Numeric string; null for
   * every other provider. Not a secret, so it ships in clear (unlike
   * `tokenMasked`).
   */
  ccid: z35.string().nullable(),
  lastPulledAt: z35.string().datetime().nullable(),
  lastPulledError: z35.string().nullable(),
  /** Consecutive failed runs — 3+ has already alerted dealership admins. */
  consecutiveFailures: z35.number().int(),
  /** Set = paused: no scheduled runs, no S3 ingest, no sync-now. */
  pausedAt: z35.string().datetime().nullable(),
  /** Cadence override in hours; null = type default (24h scraping / 1h api). */
  syncIntervalHours: z35.number().int().nullable(),
  /** I9 — header mapping; only set for `provider='custom-csv'`. */
  columnMap: InventoryColumnMapSchema.nullable(),
  createdAt: z35.string().datetime(),
  updatedAt: z35.string().datetime()
});
var InventoryImportRunSchema = z35.object({
  id: z35.string().uuid(),
  importId: z35.string().uuid(),
  /** What kicked the run. Null on legacy rows written before I1. */
  trigger: z35.enum(["cron", "sync_now", "s3_event"]).nullable(),
  startedAt: z35.string().datetime(),
  finishedAt: z35.string().datetime().nullable(),
  inserted: z35.number().int().nullable(),
  updated: z35.number().int().nullable(),
  sold: z35.number().int().nullable(),
  /** Rows dropped at parse (bad VIN, malformed lumika-json row). */
  skipped: z35.number().int().nullable(),
  /** Rows adopted from another import (`(dealership_id, vin)` ping-pong). */
  reassigned: z35.number().int().nullable(),
  /** True when a safety rail blocked the sold-mark this run. */
  suppressed: z35.boolean(),
  suppressedReason: z35.enum(["empty_feed", "undercount", "drop"]).nullable(),
  error: z35.string().nullable()
});
var InventoryImportRunListSchema = z35.array(InventoryImportRunSchema);
var CreateInventoryImportSchema = z35.object({
  rooftopId: z35.string().uuid(),
  type: InventoryImportTypeSchema,
  provider: InventoryImportProviderSchema,
  rule: InventoryRuleGroupSchema.nullable().default(null),
  // Operator-typed S3 key; the controller normalises leading/trailing
  // slashes before persistence.
  path: z35.string().trim().max(512).nullable().default(null),
  label: z35.string().trim().min(1).max(120),
  /**
   * Provider credential (API key). Required for `type='api'`
   * imports — used by the hourly pull cron to authenticate
   * against the provider's API. Encrypted at rest server-side.
   */
  token: z35.string().trim().min(1).max(512).nullable().default(null),
  /**
   * Pinned Cars Commerce listing id — only meaningful for a
   * `dealerinspire` scraping import whose discovery is challenged.
   * Numeric; the server stores it only for that provider.
   */
  ccid: z35.string().trim().regex(/^\d{4,9}$/, "Cars Commerce listing id must be numeric.").nullable().default(null),
  /** I9 — required when `provider='custom-csv'`, null otherwise. */
  columnMap: InventoryColumnMapSchema.nullable().default(null)
}).refine((v) => v.provider !== "custom-csv" || v.columnMap != null, {
  message: "A column mapping is required for custom CSV imports.",
  path: ["columnMap"]
}).refine(
  (v) => v.type !== "sftp" || (v.path?.trim().length ?? 0) > 0,
  {
    message: "Path is required for SFTP imports.",
    path: ["path"]
  }
).refine((v) => v.type !== "api" || (v.token?.trim().length ?? 0) > 0, {
  message: "Token is required for API imports.",
  path: ["token"]
}).refine(
  (v) => v.type !== "scraping" || !v.path?.trim() || /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v.path.trim()),
  {
    message: "Use a bare domain, e.g. example.com.",
    path: ["path"]
  }
);
var _UpdateBodySchema = z35.object({
  rooftopId: z35.string().uuid().optional(),
  type: InventoryImportTypeSchema.optional(),
  provider: InventoryImportProviderSchema.optional(),
  rule: InventoryRuleGroupSchema.nullable().optional(),
  path: z35.string().trim().max(512).nullable().optional(),
  label: z35.string().trim().min(1).max(120).optional(),
  /** Token rotation — re-encrypted server-side on every set. */
  token: z35.string().trim().min(1).max(512).nullable().optional(),
  /** Pinned Cars Commerce listing id (DealerInspire only); null clears it. */
  ccid: z35.string().trim().regex(/^\d{4,9}$/, "Cars Commerce listing id must be numeric.").nullable().optional(),
  /** true = pause the import (server stamps `paused_at`); false = resume. */
  paused: z35.boolean().optional(),
  /** Cadence override in hours (1–168); null restores the type default. */
  syncIntervalHours: z35.number().int().min(1).max(168).nullable().optional(),
  /** I9 — replace (or clear) the custom-csv header mapping. */
  columnMap: InventoryColumnMapSchema.nullable().optional()
});
var UpdateInventoryImportSchema = _UpdateBodySchema.refine(
  (v) => (
    // If the body sets `type='sftp'`, it must also carry a non-empty path.
    v.type !== "sftp" || v.path !== void 0 && (v.path?.trim().length ?? 0) > 0
  ),
  {
    message: "Path is required for SFTP imports.",
    path: ["path"]
  }
).refine(
  (v) => v.type !== "scraping" || !v.path?.trim() || /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v.path.trim()),
  {
    message: "Use a bare domain, e.g. example.com.",
    path: ["path"]
  }
);
var InventoryImportListSchema = z35.array(InventoryImportSchema);

// src/dto/inventory-search.dto.ts
import { z as z36 } from "zod";
var InventoryRangeSchema = z36.object({
  from: z36.number().optional(),
  to: z36.number().optional()
});
var InventoryStatusSchema = z36.enum([
  "available",
  "intransit",
  "pending",
  "sold"
]);
var INVENTORY_STATUS_LABELS = {
  available: "Available",
  intransit: "In Transit",
  pending: "Pending",
  sold: "Sold"
};
var INVENTORY_AGE_BUCKETS = ["0-30", "31-60", "61-90", "90+"];
var InventoryAgeBucketSchema = z36.enum(INVENTORY_AGE_BUCKETS);
var InventorySearchFiltersSchema = z36.object({
  vins: z36.array(z36.string()).optional(),
  make: z36.array(z36.string()).optional(),
  model: z36.array(z36.string()).optional(),
  trim: z36.array(z36.string()).optional(),
  body: z36.array(z36.string()).optional(),
  color: z36.array(z36.string()).optional(),
  interiorColor: z36.array(z36.string()).optional(),
  fuel: z36.array(z36.string()).optional(),
  transmission: z36.array(z36.string()).optional(),
  driveTrain: z36.array(z36.string()).optional(),
  condition: z36.array(z36.string()).optional(),
  /**
   * Feature keywords, ALL of which must substring-match somewhere in
   * the vehicle's `features text[]` (case-insensitive). Substring, not
   * array overlap — feeds disagree on phrasing ("3rd Row Seat" vs
   * "Third Row Seating"), so `["third row"]` must match both.
   */
  features: z36.array(z36.string()).optional(),
  status: z36.array(InventoryStatusSchema).optional(),
  rooftopId: z36.array(z36.string().uuid()).optional(),
  importId: z36.array(z36.string().uuid()).optional(),
  year: InventoryRangeSchema.optional(),
  mileage: InventoryRangeSchema.optional(),
  price: InventoryRangeSchema.optional(),
  /** I8 — days-on-lot buckets (multi-select, OR semantics). */
  age: z36.array(InventoryAgeBucketSchema).optional()
});
var InventorySortFieldSchema = z36.enum([
  "price",
  "mileage",
  "year",
  "make",
  "model",
  "stock",
  "stockDate",
  "soldDate",
  "createdAt"
]);
var InventorySearchRequestSchema = z36.object({
  query: z36.string().trim().max(120).optional(),
  filters: InventorySearchFiltersSchema.default({}),
  sort: z36.object({
    field: InventorySortFieldSchema,
    order: z36.enum(["asc", "desc"]).default("asc")
  }).optional(),
  pagination: z36.object({
    skip: z36.number().int().min(0).max(1e5).default(0),
    limit: z36.number().int().min(1).max(200).default(24)
  }).default({ skip: 0, limit: 24 }),
  facets: z36.boolean().default(true)
});
var TermFacetSchema = z36.array(
  z36.object({ value: z36.string(), count: z36.number().int() })
);
var RooftopFacetSchema = z36.array(
  z36.object({
    value: z36.string().uuid(),
    label: z36.string(),
    count: z36.number().int()
  })
);
var RangeFacetSchema = z36.object({
  from: z36.number().nullable(),
  to: z36.number().nullable()
});
var InventoryFacetsSchema = z36.object({
  make: TermFacetSchema,
  model: TermFacetSchema,
  trim: TermFacetSchema,
  body: TermFacetSchema,
  color: TermFacetSchema,
  interiorColor: TermFacetSchema,
  fuel: TermFacetSchema,
  transmission: TermFacetSchema,
  driveTrain: TermFacetSchema,
  condition: TermFacetSchema,
  status: TermFacetSchema,
  /** I8 — fixed aging buckets (value = bucket label, e.g. "31-60"). */
  age: TermFacetSchema,
  rooftop: RooftopFacetSchema,
  year: RangeFacetSchema,
  mileage: RangeFacetSchema,
  price: RangeFacetSchema
});
var InventorySearchResponseSchema = z36.object({
  total: z36.number().int(),
  vehicles: z36.array(InventoryVehicleSchema),
  facets: InventoryFacetsSchema.nullable()
});
var InventorySuggestionSchema = z36.object({
  // What kind of thing the suggestion points at, so the UI can route a
  // click to the right filter (or straight to a VDP for a VIN/stock).
  type: z36.enum(["vin", "stock", "make", "model", "trim", "make_model"]),
  value: z36.string(),
  label: z36.string(),
  count: z36.number().int().optional()
});
var InventorySuggestRequestSchema = z36.object({
  query: z36.string().trim().min(1).max(80),
  limit: z36.number().int().min(1).max(20).default(10)
});
var InventorySuggestResponseSchema = z36.array(InventorySuggestionSchema);

// src/dto/sftp.dto.ts
import { z as z37 } from "zod";
var SftpAccountStatusSchema = z37.enum(["active", "disabled", "rotating"]);
var SftpUsernameSchema = z37.string().trim().min(3).max(32).regex(/^[a-z0-9_-]+$/, "Username may contain only lowercase letters, digits, '-', '_'.");
var SftpFolderSchema = z37.string().trim().min(1).max(128).regex(
  /^[a-z0-9_/-]+$/,
  "Folder may contain only lowercase letters, digits, '-', '_', '/'."
).refine((v) => !v.startsWith("/") && !v.endsWith("/"), {
  message: "Folder must not start or end with '/'."
}).refine((v) => !v.includes("//"), {
  message: "Folder must not contain '//'."
});
var SftpPasswordSchema = z37.string().min(16, "Password must be at least 16 characters.").max(128).regex(
  /^[\x20-\x7E]+$/u,
  "Password must contain only printable ASCII characters."
);
var SftpAccountRecordSchema = z37.object({
  id: z37.string().uuid(),
  username: SftpUsernameSchema,
  folder: z37.string(),
  status: SftpAccountStatusSchema,
  notes: z37.string().nullable(),
  lastSeenAt: z37.string().datetime().nullable(),
  createdByAdminId: z37.string().uuid().nullable(),
  createdAt: z37.string().datetime(),
  updatedAt: z37.string().datetime()
});
var CreateSftpAccountSchema = z37.object({
  // Required — the integrator-facing handle. Unique.
  username: SftpUsernameSchema,
  // Reserved folder root on the bucket. When omitted, the service
  // falls back to the username (so the credential's home is
  // discoverable without typing).
  folder: SftpFolderSchema.optional(),
  // Optional cleartext password. When omitted, the service
  // auto-generates a strong base64url value. The cleartext is
  // returned exactly once in the create response — no echo back later.
  password: SftpPasswordSchema.optional(),
  notes: z37.string().max(2e3).nullable().optional()
});
var UpdateSftpAccountSchema = z37.object({
  status: SftpAccountStatusSchema.optional(),
  notes: z37.string().max(2e3).nullable().optional()
});
var RotateSftpAccountSchema = z37.object({
  password: SftpPasswordSchema.optional()
});
var SftpAccountWithSecretSchema = SftpAccountRecordSchema.extend({
  password: z37.string().min(16),
  host: z37.string(),
  port: z37.number().int().positive()
});
var SftpFileEntrySchema = z37.object({
  name: z37.string(),
  path: z37.string(),
  type: z37.enum(["file", "directory"]),
  size: z37.number().int().nullable(),
  lastModified: z37.string().datetime().nullable()
});
var SftpListResponseSchema = z37.object({
  currentPath: z37.string(),
  entries: z37.array(SftpFileEntrySchema)
});
var SftpUploadUrlRequestSchema = z37.object({
  path: z37.string().min(1).max(1024),
  contentType: z37.string().max(127).regex(/^[\w.+-]+\/[\w.+-]+$/, "Use a valid MIME type.").optional()
});
var SftpUploadUrlResponseSchema = z37.object({
  url: z37.string().url(),
  path: z37.string(),
  headers: z37.record(z37.string(), z37.string())
});
var SftpDownloadUrlResponseSchema = z37.object({
  url: z37.string().url(),
  path: z37.string()
});
var SftpDeletePathRequestSchema = z37.object({
  path: z37.string().min(1).max(1024)
});
var SftpCreateFolderRequestSchema = z37.object({
  path: z37.string().min(1).max(128)
});

// src/dto/webhook.dto.ts
import { z as z38 } from "zod";
var WEBHOOK_EVENTS = [
  "lead.created",
  "activity.created",
  "appointment.created",
  "appointment.updated",
  "contact.updated"
];
var WebhookEventSchema = z38.enum(WEBHOOK_EVENTS);
var WebhookEndpointSchema = z38.object({
  id: z38.string().uuid(),
  dealershipId: z38.string().uuid(),
  url: z38.string().url(),
  /** Dealer-visible by necessity — the receiver verifies with it. */
  secret: z38.string(),
  events: z38.array(WebhookEventSchema),
  active: z38.boolean(),
  createdAt: z38.string().datetime(),
  updatedAt: z38.string().datetime()
});
var CreateWebhookEndpointSchema = z38.object({
  url: z38.string().trim().url().max(2e3).refine((u) => u.startsWith("https://"), {
    message: "Webhook URLs must be https."
  }),
  events: z38.array(WebhookEventSchema).min(1)
});
var UpdateWebhookEndpointSchema = z38.object({
  url: z38.string().trim().url().max(2e3).refine((u) => u.startsWith("https://"), {
    message: "Webhook URLs must be https."
  }).optional(),
  events: z38.array(WebhookEventSchema).min(1).optional(),
  active: z38.boolean().optional()
});
var WebhookDeliveryStatusSchema = z38.enum([
  "pending",
  "delivered",
  "failed"
]);
var WebhookDeliverySchema = z38.object({
  id: z38.string().uuid(),
  endpointId: z38.string().uuid(),
  event: WebhookEventSchema,
  status: WebhookDeliveryStatusSchema,
  attempts: z38.number().int(),
  nextRetryAt: z38.string().datetime().nullable(),
  lastError: z38.string().nullable(),
  createdAt: z38.string().datetime()
});

// src/email/invitation-email.ts
var MEMBERSHIP_ROLE_LABELS = {
  dealership_owner: "Owner",
  dealership_admin: "Admin",
  sales_manager: "Sales Manager",
  service_manager: "Service Manager",
  bdc_agent: "BDC Agent",
  sales_agent: "Sales Agent",
  service_agent: "Service Agent",
  readonly: "Viewer"
};
var COLOR = {
  pageBg: "#eef0f2",
  card: "#ffffff",
  border: "#e6e8eb",
  ink: "#0f0e0d",
  muted: "#8a9097",
  body: "#3f4448",
  brand: "#5c7a9c",
  panel: "#f5f7f9"
};
var FONT_STACK = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function trimTrailingSlash(value) {
  return value.replace(/\/+$/, "");
}
function renderInvitationEmail(input) {
  const appUrl = trimTrailingSlash(input.appUrl);
  const logoUrl = input.logoUrl ?? `${appUrl}/lumika-email.png`;
  const roleLabel = MEMBERSHIP_ROLE_LABELS[input.role];
  const dealership = input.dealershipName.trim();
  const greetingName = input.recipientName?.trim();
  const subject = `Welcome to Lumika \u2014 you've been added to ${dealership}`;
  const text = [
    `Welcome to Lumika!`,
    ``,
    greetingName ? `Hi ${greetingName},` : `Hi there,`,
    ``,
    `You've been added as ${roleLabel} to manage ${dealership} on Lumika.`,
    ``,
    `Lumika is your AI-powered command center for every customer conversation. Log in to get started:`,
    appUrl,
    ``,
    `You're receiving this email because you were added to a team on Lumika.`
  ].join("\n");
  const safeDealership = escapeHtml(dealership);
  const safeRole = escapeHtml(roleLabel);
  const safeAppUrl = escapeHtml(appUrl);
  const safeLogoUrl = escapeHtml(logoUrl);
  const greeting = greetingName ? `Hi ${escapeHtml(greetingName)},` : "Hi there,";
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:${COLOR.pageBg};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;">You've been added as ${safeRole} to manage ${safeDealership} on Lumika.</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR.pageBg};">
<tr>
<td align="center" style="padding:32px 16px;">
<table role="presentation" width="480" cellpadding="0" cellspacing="0" border="0" style="width:480px;max-width:100%;background-color:${COLOR.card};border:1px solid ${COLOR.border};border-radius:16px;overflow:hidden;">
<!-- Header -->
<tr>
<td style="padding:32px 32px 0 32px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="vertical-align:middle;padding-right:10px;">
<img src="${safeLogoUrl}" width="34" height="34" alt="Lumika" style="display:block;width:34px;height:34px;border:0;">
</td>
<td style="vertical-align:middle;">
<span style="font-family:${FONT_STACK};font-size:21px;font-weight:600;letter-spacing:0.2px;color:${COLOR.ink};">lumika</span>
</td>
</tr>
</table>
</td>
</tr>
<!-- Body -->
<tr>
<td style="padding:28px 32px 4px 32px;font-family:${FONT_STACK};">
<div style="font-size:12px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${COLOR.brand};">You're invited</div>
<h1 style="margin:8px 0 0 0;font-size:26px;line-height:1.25;font-weight:700;color:${COLOR.ink};">Welcome to Lumika</h1>
<p style="margin:18px 0 0 0;font-size:15px;line-height:1.6;color:${COLOR.body};">${greeting}</p>
<p style="margin:12px 0 0 0;font-size:15px;line-height:1.6;color:${COLOR.body};">You've been added as <strong style="color:${COLOR.ink};">${safeRole}</strong> to manage <strong style="color:${COLOR.ink};">${safeDealership}</strong> on Lumika.</p>
</td>
</tr>
<!-- Detail panel -->
<tr>
<td style="padding:20px 32px 0 32px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR.panel};border-radius:12px;">
<tr>
<td style="padding:16px 18px;font-family:${FONT_STACK};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="font-size:12px;color:${COLOR.muted};padding-bottom:2px;">Dealership</td>
</tr>
<tr>
<td style="font-size:15px;font-weight:600;color:${COLOR.ink};padding-bottom:12px;">${safeDealership}</td>
</tr>
<tr>
<td style="font-size:12px;color:${COLOR.muted};padding-bottom:2px;">Your role</td>
</tr>
<tr>
<td style="font-size:15px;font-weight:600;color:${COLOR.ink};">${safeRole}</td>
</tr>
</table>
</td>
</tr>
</table>
</td>
</tr>
<!-- CTA -->
<tr>
<td style="padding:26px 32px 4px 32px;font-family:${FONT_STACK};">
<p style="margin:0 0 18px 0;font-size:15px;line-height:1.6;color:${COLOR.body};">Lumika is your AI-powered command center for every customer conversation. Sign in to get started.</p>
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td align="center" bgcolor="${COLOR.brand}" style="border-radius:10px;">
<a href="${safeAppUrl}" target="_blank" style="display:inline-block;padding:14px 30px;font-family:${FONT_STACK};font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:10px;">Log in to Lumika</a>
</td>
</tr>
</table>
<p style="margin:18px 0 0 0;font-size:13px;line-height:1.6;color:${COLOR.muted};">Or paste this link into your browser:<br><a href="${safeAppUrl}" target="_blank" style="color:${COLOR.brand};text-decoration:none;">${safeAppUrl}</a></p>
</td>
</tr>
<!-- Footer -->
<tr>
<td style="padding:28px 32px 32px 32px;font-family:${FONT_STACK};">
<div style="border-top:1px solid ${COLOR.border};padding-top:18px;font-size:12px;line-height:1.6;color:${COLOR.muted};">You're receiving this email because you were added to a team on Lumika.</div>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`;
  return { subject, text, html };
}

// src/email/email-code-email.ts
var COLOR2 = {
  pageBg: "#eef0f2",
  card: "#ffffff",
  border: "#e6e8eb",
  ink: "#0f0e0d",
  muted: "#8a9097",
  body: "#3f4448",
  brand: "#5c7a9c",
  panel: "#f5f7f9"
};
var FONT_STACK2 = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
function escapeHtml2(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function renderEmailCodeEmail(input) {
  const logoUrl = input.logoUrl ?? "https://app.lumika.ai/lumika-email.png";
  const safeLogoUrl = escapeHtml2(logoUrl);
  const safeCode = escapeHtml2(input.code);
  const subject = "Your Lumika sign-in code";
  const text = [
    `Your Lumika sign-in code`,
    ``,
    `Code: ${input.code}`,
    ``,
    `This code expires in ${input.expiryMinutes} minute${input.expiryMinutes === 1 ? "" : "s"}.`,
    ``,
    `If you didn't request this code, you can safely ignore this email.`
  ].join("\n");
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escapeHtml2(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:${COLOR2.pageBg};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;">Your Lumika sign-in code: ${safeCode}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR2.pageBg};">
<tr>
<td align="center" style="padding:32px 16px;">
<table role="presentation" width="480" cellpadding="0" cellspacing="0" border="0" style="width:480px;max-width:100%;background-color:${COLOR2.card};border:1px solid ${COLOR2.border};border-radius:16px;overflow:hidden;">
<!-- Header -->
<tr>
<td style="padding:32px 32px 0 32px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0">
<tr>
<td style="vertical-align:middle;padding-right:10px;">
<img src="${safeLogoUrl}" width="34" height="34" alt="Lumika" style="display:block;width:34px;height:34px;border:0;">
</td>
<td style="vertical-align:middle;">
<span style="font-family:${FONT_STACK2};font-size:21px;font-weight:600;letter-spacing:0.2px;color:${COLOR2.ink};">lumika</span>
</td>
</tr>
</table>
</td>
</tr>
<!-- Body -->
<tr>
<td style="padding:28px 32px 4px 32px;font-family:${FONT_STACK2};">
<div style="font-size:12px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${COLOR2.brand};">Sign in</div>
<h1 style="margin:8px 0 0 0;font-size:26px;line-height:1.25;font-weight:700;color:${COLOR2.ink};">Your sign-in code</h1>
<p style="margin:18px 0 0 0;font-size:15px;line-height:1.6;color:${COLOR2.body};">Use the code below to sign in to Lumika. It expires in ${input.expiryMinutes} minute${input.expiryMinutes === 1 ? "" : "s"}.</p>
</td>
</tr>
<!-- Code panel -->
<tr>
<td style="padding:20px 32px 0 32px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${COLOR2.panel};border-radius:12px;">
<tr>
<td align="center" style="padding:24px 18px;font-family:${FONT_STACK2};">
<div style="font-size:36px;font-weight:700;letter-spacing:8px;color:${COLOR2.ink};font-variant-numeric:tabular-nums;">${safeCode}</div>
</td>
</tr>
</table>
</td>
</tr>
<!-- Footer -->
<tr>
<td style="padding:28px 32px 32px 32px;font-family:${FONT_STACK2};">
<div style="border-top:1px solid ${COLOR2.border};padding-top:18px;font-size:12px;line-height:1.6;color:${COLOR2.muted};">If you didn't request this code, you can safely ignore this email.</div>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`;
  return { subject, text, html };
}

// src/mentions.ts
var MENTION_REGEX = /@\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;
function formatMention(userId) {
  return `@[${userId}]`;
}
function extractMentionIds(text) {
  if (!text) return [];
  const ids = [];
  const seen = /* @__PURE__ */ new Set();
  for (const match of text.matchAll(MENTION_REGEX)) {
    const id = match[1];
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}
function splitMentions(text) {
  if (!text) return [];
  const segments = [];
  let cursor = 0;
  for (const match of text.matchAll(MENTION_REGEX)) {
    const id = match[1];
    const token = match[0];
    if (!id || !token) continue;
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ type: "text", value: text.slice(cursor, start) });
    }
    segments.push({ type: "mention", id });
    cursor = start + token.length;
  }
  if (cursor < text.length) {
    segments.push({ type: "text", value: text.slice(cursor) });
  }
  return segments;
}
function replaceMentions(text, resolve) {
  return text.replace(MENTION_REGEX, (_token, id) => resolve(id));
}
function truncateMentions(text, max) {
  const collapsed = (text ?? "").replace(/\s+/g, " ").trim();
  if (collapsed.length <= max) return collapsed;
  let cut = collapsed.slice(0, max - 1);
  if (cut.lastIndexOf("@[") > cut.lastIndexOf("]")) {
    cut = cut.slice(0, cut.lastIndexOf("@[")).trimEnd();
  }
  return `${cut}\u2026`;
}

// src/inventory-providers.ts
var INVENTORY_PROVIDER_IDS = [
  "spaceauto",
  "vauto",
  "homenet",
  // Legacy recipe-based scraping rows; kept for existing data, hidden
  // from the create form. New scraping imports pick a platform below.
  "website",
  // Website-scraping platforms — each maps to a first-class scraper in
  // apps/backend/src/inventory-scrapers (no CSV mapper).
  "dealeron",
  "dealercom",
  "dealercloud",
  "teamvelocity",
  "overfuel",
  "dealerinspire",
  // SFTP-uploaded JSON file in the canonical ScrapedVehicle shape —
  // the upload path for inventory scraped outside the platform (e.g.
  // Cloudflare-hard sites scraped locally).
  "lumika-json",
  // I9 — operator-mapped CSV for any DMS export we have no bespoke
  // mapper for (CDK, Reynolds, Dealertrack, ...). The header mapping
  // lives on `inventory_imports.column_map`, not in code.
  "custom-csv"
];
var INVENTORY_PROVIDERS = [
  { id: "spaceauto", name: "Space Auto" },
  { id: "vauto", name: "vAuto" },
  { id: "homenet", name: "Homenet" },
  { id: "website", name: "Website Scraping (legacy)" },
  { id: "dealeron", name: "DealerOn" },
  { id: "dealercom", name: "Dealer.com" },
  { id: "dealercloud", name: "dealr.cloud" },
  { id: "teamvelocity", name: "Team Velocity" },
  { id: "overfuel", name: "Overfuel" },
  { id: "dealerinspire", name: "DealerInspire" },
  { id: "lumika-json", name: "Lumika JSON" },
  { id: "custom-csv", name: "Custom CSV" }
];
var SCRAPING_PROVIDER_IDS = [
  "dealeron",
  "dealercom",
  "dealercloud",
  "teamvelocity",
  "overfuel",
  "dealerinspire"
];
function isInventoryProviderId(value) {
  return typeof value === "string" && INVENTORY_PROVIDER_IDS.includes(value);
}
function inventoryProviderName(id) {
  return INVENTORY_PROVIDERS.find((p) => p.id === id)?.name ?? id;
}
function isScrapingProviderId(value) {
  return typeof value === "string" && SCRAPING_PROVIDER_IDS.includes(value);
}
function normalizeScrapeDomain(input) {
  let v = input.trim().toLowerCase();
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  v = v.replace(/[/?#].*$/, "");
  v = v.replace(/:\d+$/, "");
  v = v.replace(/^www\./, "");
  return v;
}

// src/timezone.ts
function wallParts(instant, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
  const get = (type) => {
    const v = fmt.formatToParts(instant).find((p) => p.type === type)?.value ?? "0";
    return Number(v === "24" ? "0" : v);
  };
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second")
  };
}
var toDate = (instant) => instant instanceof Date ? instant : new Date(instant);
function formatInTimeZone(instant, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).formatToParts(toDate(instant));
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")}, ${get("month")} ${get("day")}, ${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
}
function utcToWallTime(instant, timeZone) {
  const p = wallParts(toDate(instant), timeZone);
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}
function wallTimeToUtc(wallTime, timeZone) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(
    wallTime.trim()
  );
  if (!m) throw new Error(`wallTimeToUtc: invalid wall time "${wallTime}"`);
  const [, y, mo, d, h, mi, s] = m;
  const target = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s ?? "0")
  );
  let guess = target;
  for (let i = 0; i < 2; i++) {
    const p = wallParts(new Date(guess), timeZone);
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    guess += target - seen;
  }
  return new Date(guess);
}
function wallClockDate(instant, timeZone) {
  const p = wallParts(toDate(instant), timeZone);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

// src/business-hours.ts
var WEEKDAYS2 = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];
function dateWithinBusinessHours(hours, instant, timezone) {
  const context = wallContext(instant, timezone);
  if (!context || !hours || typeof hours !== "object") return null;
  const day = dayWindow(
    hours[weekdayAt(context.weekdayIndex)]
  );
  if (!day) return null;
  if (day.closed) return false;
  return context.minutes >= day.start && context.minutes < day.end;
}
function nextOpenInstant(hours, instant, timezone) {
  if (!hours || typeof hours !== "object") return null;
  const schedule = hours;
  const context = wallContext(instant, timezone);
  if (!context) return null;
  for (let offset = 0; offset < 7; offset += 1) {
    const weekday = weekdayAt(context.weekdayIndex + offset);
    const day = dayWindow(schedule[weekday]);
    if (!day) {
      if (offset === 0) return null;
      continue;
    }
    if (day.closed) continue;
    if (offset === 0) {
      if (context.minutes >= day.start && context.minutes < day.end) {
        return instant;
      }
      if (context.minutes < day.start) {
        return openInstantOn(context.date, day.start, timezone);
      }
      continue;
    }
    return openInstantOn(addDays(context.date, offset), day.start, timezone);
  }
  return null;
}
function weekdayAt(index) {
  return WEEKDAYS2[index % WEEKDAYS2.length] ?? "sunday";
}
function wallContext(instant, timezone) {
  try {
    const wall = utcToWallTime(instant, timezone);
    const date = wall.slice(0, 10);
    return {
      date,
      minutes: Number(wall.slice(11, 13)) * 60 + Number(wall.slice(14, 16)),
      weekdayIndex: (/* @__PURE__ */ new Date(`${date}T12:00:00Z`)).getUTCDay()
    };
  } catch {
    return null;
  }
}
function dayWindow(value) {
  if (!value || typeof value !== "object") return null;
  const day = value;
  if (day.open === false) return { closed: true, start: 0, end: 0 };
  const start = typeof day.startMinute === "number" ? day.startMinute : typeof day.startHour === "number" ? day.startHour * 60 : null;
  const end = typeof day.endMinute === "number" ? day.endMinute : typeof day.endHour === "number" ? day.endHour * 60 : null;
  if (start === null || end === null || start < 0 || end > 24 * 60 || start >= end) {
    return null;
  }
  return { closed: false, start, end };
}
function addDays(date, days) {
  const value = /* @__PURE__ */ new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}
function openInstantOn(date, minutes, timezone) {
  const pad = (value) => String(value).padStart(2, "0");
  return wallTimeToUtc(
    `${date}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`,
    timezone
  );
}

// src/lib/activity-changes.ts
var LEAD_STATUS_LABELS = {
  new: "New",
  working: "Working",
  appointment: "Appointment",
  won: "Won",
  lost: "Lost"
};
function leadStatusLabel(status) {
  return LEAD_STATUS_LABELS[status] ?? status;
}
var CONTACT_STATUS_LABELS = {
  anonymous: "Anonymous",
  new_lead: "New lead",
  engaged: "Engaged",
  appointment_scheduled: "Appointment scheduled",
  appointment_attended: "Appointment attended",
  sold: "Sold",
  service_customer: "Service customer",
  inactive: "Inactive",
  lost: "Lost"
};
function contactStatusLabel(status) {
  return CONTACT_STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}
var CHANGE_FIELD_LABELS = {
  assignedUserId: "assignee",
  snoozedUntil: "snooze",
  channelId: "channel",
  primaryChannel: "channel",
  closed: "state",
  status: "status",
  firstName: "first name",
  lastName: "last name",
  businessName: "business name",
  primaryEmail: "email",
  primaryPhone: "phone",
  city: "city",
  state: "state",
  zip: "zip",
  addressLine1: "address",
  preferredChannel: "preferred channel",
  preferredLanguage: "language",
  leadSource: "lead source",
  birthDate: "birthday",
  optoutEmail: "email opt-out",
  optoutSms: "SMS opt-out",
  optoutVoice: "voice opt-out",
  notes: "notes",
  tag: "tag",
  value: "value",
  isPrimary: "primary",
  type: "type"
};
function changeFieldLabel(field) {
  return CHANGE_FIELD_LABELS[field] ?? field.replace(/_/g, " ");
}
function parseActivityChanges(body) {
  if (!body) return null;
  const trimmed = body.trimStart();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    const changes = parsed.changes;
    if (!changes || typeof changes !== "object" || Array.isArray(changes)) return null;
    const out = {};
    for (const [field, value] of Object.entries(changes)) {
      if (value && typeof value === "object" && "before" in value && "after" in value) {
        out[field] = value;
      }
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}
var ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var VALUE_MAX = 60;
function truncate(value) {
  return value.length > VALUE_MAX ? `${value.slice(0, VALUE_MAX - 1)}\u2026` : value;
}
function formatChangeValue(field, value, resolvers = {}) {
  if (value === null || value === void 0 || value === "") {
    return field === "assignedUserId" ? "Unassigned" : "\u2014";
  }
  if (field === "status") {
    const str2 = String(value);
    return LEAD_STATUS_LABELS[str2] ?? CONTACT_STATUS_LABELS[str2] ?? str2.replace(/_/g, " ");
  }
  if (field === "closed") return value ? "closed" : "open";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (field === "assignedUserId") {
    return resolvers.userName?.(String(value)) ?? "a teammate";
  }
  if (field === "channelId" || field === "primaryChannel") {
    const resolved = field === "channelId" ? resolvers.channelName?.(String(value)) : null;
    if (resolved) return resolved;
    return UUID.test(String(value)) ? "another channel" : String(value);
  }
  const str = String(value);
  if (ISO_DATETIME.test(str)) {
    return resolvers.formatDateTime?.(str) ?? str.slice(0, 10);
  }
  if (UUID.test(str)) return "\u2014";
  return truncate(str);
}
var LABEL_ONLY_FIELDS = /* @__PURE__ */ new Set(["notes"]);
function summarizeActivityChanges(body, resolvers = {}) {
  const changes = parseActivityChanges(body);
  if (!changes) return null;
  const fields = Object.keys(changes);
  const visible = fields.slice(0, 3);
  const parts = visible.map((field) => {
    const label = changeFieldLabel(field);
    const titled = label.charAt(0).toUpperCase() + label.slice(1);
    if (LABEL_ONLY_FIELDS.has(field)) return `${titled} updated`;
    const { before, after } = changes[field];
    return `${titled}: ${formatChangeValue(field, before, resolvers)} \u2192 ${formatChangeValue(field, after, resolvers)}`;
  });
  const extra = fields.length - visible.length;
  return `${parts.join("; ")}${extra > 0 ? ` (+${extra} more)` : ""}`;
}

// src/lib/telephony-error-codes.ts
var TELEPHONY_ERROR_MAP = {
  // Trust Hub / A2P submission
  "30001": {
    message: "The EIN we sent doesn't match an active business with the IRS.",
    fix: "Double-check the EIN in Business profile. Format must be 12-3456789."
  },
  "30002": {
    message: "The website couldn't be reached.",
    fix: "Make sure the website is publicly reachable over HTTPS and includes a privacy policy."
  },
  "30003": {
    message: "Sample SMS messages don't comply with carrier guidelines.",
    fix: "Every sample needs the dealership name, opt-out (STOP), and avoid prohibited content."
  },
  "30004": {
    message: "The authorised representative email is on a free webmail domain.",
    fix: "Use an email on the dealership's domain (e.g. you@dealership.com)."
  },
  // Brand registration
  "23104": {
    message: "Brand registration was rejected by the carrier.",
    fix: "Open the brand on the A2P wizard; the rejection reasons are listed there."
  },
  // Campaign
  "23108": {
    message: "Campaign rejected \u2014 use case doesn't match the sample messages.",
    fix: "Pick a use case (e.g. Customer Care) that fits the sample messages, or resubmit with samples that match."
  },
  // SMS send-time failures
  "30007": {
    message: "Carrier filtered the message as spam.",
    fix: "Soften the marketing tone, remove excessive caps/punctuation, and ensure STOP instructions."
  },
  "30034": {
    message: "This number isn't registered for A2P SMS yet.",
    fix: "Finish the A2P wizard for this Telephony Account before sending SMS."
  }
};
function translateCarrierError(args) {
  const code = args.code != null ? String(args.code).trim() : "";
  const known = code ? TELEPHONY_ERROR_MAP[code] : void 0;
  if (known) return known;
  const raw = (args.rawMessage ?? "").trim();
  if (!raw) {
    return { message: "Something went wrong on the carrier side. Try again or contact support." };
  }
  const stripped = raw.replace(/^Error\s+\d+[:\s-]+/i, "");
  return { message: stripped };
}

// src/lib/telephony-pricing.ts
var TELEPHONY_PRICING = {
  /** Monthly carrier fee to keep a local 10-digit number. */
  phoneNumberLocalMonthly: 1.15,
  /** Monthly carrier fee to keep a toll-free number. */
  phoneNumberTollFreeMonthly: 2,
  /** One-time A2P brand registration fee. */
  a2pBrandRegistration: 4,
  /** One-time external vetting fee — mandatory for STANDARD brands. */
  a2pStandardVetting: 40,
  /** One-time A2P campaign registration / vetting fee. */
  a2pCampaignRegistration: 15,
  /** Monthly carrier fee for a standard-tier campaign. */
  a2pCampaignMonthlyStandard: 10,
  /** Monthly carrier fee for a low-volume campaign. */
  a2pCampaignMonthlyLowVolume: 2
};
function formatUsd(amount) {
  return `$${amount.toFixed(2)}`;
}

// src/lib/usage-taxonomy.ts
var USAGE_TYPES = ["ai", "twilio", "dms"];
var AI_USAGE_KINDS = [
  "text_generation",
  "transcription",
  "image_generation",
  "phone_call"
];
function isAiUsageKind(value) {
  return AI_USAGE_KINDS.includes(value);
}

// src/lib/contact-display.ts
function contactDisplayName(input) {
  if (input.businessName?.trim()) return input.businessName.trim();
  const composed = [input.firstName, input.lastName].map((p) => (p ?? "").trim()).filter(Boolean).join(" ");
  if (composed) return composed;
  if (input.primaryEmail?.trim()) return input.primaryEmail.trim();
  if (input.primaryPhone?.trim()) {
    return formatPhoneDisplay(input.primaryPhone.trim());
  }
  if (input.socialHandle?.trim()) return input.socialHandle.trim();
  if (input.fallbackLabel?.trim()) return input.fallbackLabel.trim();
  return "Anonymous";
}
function formatPhoneDisplay(e164) {
  const nanp = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (nanp) {
    return `+1 (${nanp[1]}) ${nanp[2]}-${nanp[3]}`;
  }
  return e164;
}

// src/lib/user-display.ts
function userDisplayLabel(user) {
  if (!user) return "Unknown user";
  const composed = [user.firstName, user.lastName].map((part) => (part ?? "").trim()).filter(Boolean).join(" ");
  return composed || user.displayName?.trim() || user.email?.trim() || user.phone?.trim() || "Unknown user";
}

// src/types/api.ts
var API_PREFIX = "/api/v1";

// src/types/realtime.ts
var CLIENT_VISIBLE_TOOL_NAMES = [
  "search_vehicles",
  "search_facets",
  "get_vehicle",
  "rooftop_context",
  "create_cards",
  "brave_search",
  "submit_contact",
  "submit_vehicle",
  "submit_tradein",
  "submit_appointment"
];
var CLIENT_TOOL_EVENT_META = {
  search_vehicles: { type: "search_vehicles", label: "Searching vehicles" },
  search_facets: { type: "search_facets", label: "Checking inventory" },
  get_vehicle: { type: "get_vehicle", label: "Getting vehicle details" },
  rooftop_context: {
    type: "rooftop_context",
    label: "Getting dealer information"
  },
  create_cards: { type: "create_cards", label: "Showing cards" },
  brave_search: { type: "brave_search", label: "Searching the web" },
  submit_contact: { type: "submit_contact", label: "Saving information" },
  submit_vehicle: { type: "submit_vehicle", label: "Saving interest" },
  submit_tradein: { type: "submit_tradein", label: "Valuating trade-in" },
  submit_appointment: {
    type: "submit_appointment",
    label: "Scheduling appointment"
  }
};
var INTERNAL_TOOL_EVENT_META = {
  type: "internal_tool",
  label: "Thinking"
};
var CLIENT_TOOL_EVENT_TYPES = [
  "search_vehicles",
  "search_facets",
  "get_vehicle",
  "rooftop_context",
  "create_cards",
  "brave_search",
  "submit_contact",
  "submit_vehicle",
  "submit_tradein",
  "submit_appointment",
  "internal_tool"
];
export {
  A2pBrandTypeSchema,
  A2pBusinessProfileSchema,
  A2pCampaignStatusSchema,
  A2pCampaignUseCaseSchema,
  A2pJobPositionSchema,
  A2pStatusSchema,
  A2pSubmitBrandSchema,
  A2pSubmitCampaignSchema,
  A2pVerifyOtpSchema,
  AAP_EXTENSION_URI,
  AAP_METHODS,
  AAP_SKILL_IDS,
  AI_ALWAYS_ON_CHANNEL_TYPES,
  AI_USAGE_KINDS,
  API_KEY_PREFIX,
  API_PREFIX,
  AapValidationResultSchema,
  AccessibleDealershipSchema,
  ActivityActorTypeSchema,
  ActivityDirectionSchema,
  ActivityListSchema,
  ActivityPageSchema,
  ActivitySchema,
  AddTagActionConfigSchema,
  AdminActivityFacetsSchema,
  AdminActivityListSchema,
  AdminActivityPageQuerySchema,
  AdminActivityPageSchema,
  AdminActivitySchema,
  AdminConnectionHealthSchema,
  AdminConnectionKindSchema,
  AdminConnectionLocationScopeSchema,
  AdminConnectionSchema,
  AdminConnectionsResponseSchema,
  AdminContactDirectoryQuerySchema,
  AdminContactDirectorySortSchema,
  AdminMeSchema,
  AdminSchema,
  AdminSearchGroupSchema,
  AdminSearchItemSchema,
  AdminSearchKindSchema,
  AdminSearchQuerySchema,
  AdminSearchResponseSchema,
  AgentCardExtensionSchema,
  AgentCardInterfaceSchema,
  AgentCardSchema,
  AgentCardSkillSchema,
  AgentRunListSchema,
  AgentRunOutcomeSchema,
  AgentRunSchema,
  AgentRunToolCallSchema,
  AiAgentChannelAssignmentListSchema,
  AiAgentChannelAssignmentSchema,
  AiAgentDetailSchema,
  AiAgentDispatchCheckRequestSchema,
  AiAgentDispatchDecisionSchema,
  AiAgentDispatchSkipReasonSchema,
  AiAgentListSchema,
  AiAgentRespondToSchema,
  AiAgentResponseSplitModeSchema,
  AiAgentSchema,
  AiAgentSkillSchema,
  AiAgentStatusSchema,
  AiAssignmentMediumSchema,
  AiBudgetSummarySchema,
  AiDailyRecapSchema,
  AiRooftopPromptListSchema,
  AiRooftopPromptSchema,
  AiSkillListSchema,
  AiSkillSchema,
  AiUsageBreakdownRowSchema,
  AiUsageSummarySchema,
  ApiKeyCreatedSchema,
  ApiKeySchema,
  ApiKeyScopeSchema,
  AppMeSchema,
  AppointmentListSchema,
  AppointmentPageSchema,
  AppointmentReminderStepSchema,
  AppointmentSchema,
  AppointmentStatusSchema,
  AppointmentTransportTypeSchema,
  AppointmentTypeSchema,
  AssignActionConfigSchema,
  AssignTaskSchema,
  AttachmentUploadUrlResultSchema,
  AutomationActionTypeSchema,
  AutomationConditionsSchema,
  AutomationRuleSchema,
  AvatarUploadUrlResultSchema,
  BandedLeadSchema,
  BillingRangeSchema,
  BrandColorPairSchema,
  BrandImageSchema,
  BulkLeadActionResultSchema,
  BulkLeadActionSchema,
  CA_POSTAL_RE,
  CA_PROVINCES,
  CA_TIMEZONES,
  CERTIFICATION_PENDING_PROVIDERS,
  CHANGE_FIELD_LABELS,
  CLIENT_TOOL_EVENT_META,
  CLIENT_TOOL_EVENT_TYPES,
  CLIENT_VISIBLE_TOOL_NAMES,
  CONTACT_LANGUAGES,
  CONTACT_STATUS_LABELS,
  COUNTRIES,
  CaPostalCodeSchema,
  CadenceEnrollOnSchema,
  CadenceSequenceSchema,
  CadenceStepSchema,
  CalendarAccountSchema,
  CalendarAccountStatusSchema,
  CalendarAppointmentFilterSchema,
  CalendarAppointmentPageSchema,
  CalendarAppointmentQuerySchema,
  CalendarAppointmentSchema,
  CalendarConnectUrlSchema,
  CalendarSyncStatusSchema,
  ChannelCapabilitySchema,
  ChannelConflictResponseSchema,
  ChannelListSchema,
  ChannelOnboardingInfoSchema,
  ChannelPageSchema,
  ChannelSchema,
  ChannelStatusSchema,
  ChannelTypeSchema,
  CheckEmailAddressSchema,
  ConnectAapChannelSchema,
  ConnectApiChannelSchema,
  ConnectCustomDomainEmailSchema,
  ConnectEmailChannelSchema,
  ConnectTelegramChannelSchema,
  ConnectWhatsAppEmbeddedSignupSchema,
  ConnectWidgetChannelSchema,
  ConsentChannelTypeSchema,
  ConsentEventListSchema,
  ConsentEventPageSchema,
  ConsentEventSchema,
  ConsentEventTypeSchema,
  ConsentScopeSchema,
  ConsentSourceSchema,
  ContactActivityFilterSchema,
  ContactActivityPageSchema,
  ContactActivityQuerySchema,
  ContactChannelListSchema,
  ContactChannelPageSchema,
  ContactChannelSchema,
  ContactChannelTypeSchema,
  ContactIntegrationSyncSchema,
  ContactLanguageSchema,
  ContactLeadCountsSchema,
  ContactLeadPageSchema,
  ContactLeadQuerySchema,
  ContactLeadViewSchema,
  ContactListSchema,
  ContactPageSchema,
  ContactSchema,
  ContactStatusSchema,
  ContactTagListSchema,
  ContactTagSchema,
  ContactTopOpenLeadSchema,
  ContactVehicleListSchema,
  ContactVehiclePageSchema,
  ContactVehicleRelationshipSchema,
  ContactVehicleSchema,
  CountryCodeSchema,
  CreateActivitySchema,
  CreateAiAgentChannelAssignmentSchema,
  CreateAiAgentSchema,
  CreateAiAgentSkillSchema,
  CreateAiSkillSchema,
  CreateApiKeySchema,
  CreateAppointmentSchema,
  CreateAttachmentUploadUrlSchema,
  CreateAutomationRuleSchema,
  CreateAvatarUploadUrlSchema,
  CreateChannelSchema,
  CreateConsentEventSchema,
  CreateContactChannelBodySchema,
  CreateContactChannelSchema,
  CreateContactSchema,
  CreateContactTagSchema,
  CreateContactVehicleSchema,
  CreateDealershipSchema,
  CreateFollowUpSchema,
  CreateIntegrationSchema,
  CreateInventoryImportSchema,
  CreateInventoryVehicleSchema,
  CreateLeadSchema,
  CreateOutreachNoteSchema,
  CreateOutreachProspectSchema,
  CreateRooftopSchema,
  CreateServiceTypeSchema,
  CreateSftpAccountSchema,
  CreateTagSchema,
  CreateTaskActionConfigSchema,
  CreateTaskSchema,
  CreateTelephonyAccountSchema,
  CreateTradeInSchema,
  CreateUserMembershipSchema,
  CreateUserSchema,
  CreateVehicleInterestSchema,
  CreateWebhookEndpointSchema,
  DEFAULT_REMINDER_TEMPLATE,
  DayHoursSchema,
  DealerInformationRequestSchema,
  DealerInformationResponseSchema,
  DealershipBillingSummarySchema,
  DealershipChoiceListSchema,
  DealershipChoiceSchema,
  DealershipCountrySchema,
  DealershipDetailSchema,
  DealershipDirectoryItemSchema,
  DealershipDirectoryOnboardingSchema,
  DealershipDirectoryPageSchema,
  DealershipDirectoryQuerySchema,
  DealershipDirectorySortSchema,
  DealershipListSchema,
  DealershipOnboardingSchema,
  DealershipOnboardingStateSchema,
  DealershipSchema,
  DealershipStatusSchema,
  DeletePushSubscriptionSchema,
  DeliveryStatusSchema,
  DnsRecordSpecSchema,
  E164_RE,
  EIN_RE,
  EinSchema,
  EmailAddressAvailabilitySchema,
  EmailCodeLoginResultSchema,
  EmailCustomDomainSchema,
  EmailLocalPartSchema,
  EmailOutreachProspectSchema,
  EmailSchema,
  EmailSubdomainSchema,
  FollowUpKindSchema,
  FollowUpListSchema,
  FollowUpPageSchema,
  FollowUpSchema,
  FollowUpStatusSchema,
  GoogleCallbackSchema,
  HealthStatusSchema,
  HexColorSchema,
  INTERNAL_TOOL_EVENT_META,
  INVENTORY_AGE_BUCKETS,
  INVENTORY_PROVIDERS,
  INVENTORY_PROVIDER_IDS,
  INVENTORY_STATUS_LABELS,
  ImportContactRowSchema,
  ImportContactsResultSchema,
  ImportContactsSchema,
  ImportOutreachProspectsSchema,
  ImportOutreachResultSchema,
  InboxCountsSchema,
  InboxViewSchema,
  InboxVisibilitySchema,
  IntegrationBackfillStateSchema,
  IntegrationDeliverySchema,
  IntegrationDeliveryStatusSchema,
  IntegrationListSchema,
  IntegrationLocationsMappingSchema,
  IntegrationProviderLabels,
  IntegrationProviderSchema,
  IntegrationSchema,
  IntegrationStatusSchema,
  InternalNoteSchema,
  InventoryAgeBucketSchema,
  InventoryColumnMapColumnsSchema,
  InventoryColumnMapSchema,
  InventoryFacetsSchema,
  InventoryImportListSchema,
  InventoryImportProviderSchema,
  InventoryImportRunListSchema,
  InventoryImportRunSchema,
  InventoryImportSchema,
  InventoryImportTypeSchema,
  InventoryQuerySchema,
  InventoryRangeSchema,
  InventoryRuleCombinatorSchema,
  InventoryRuleFieldSchema,
  InventoryRuleGroupSchema,
  InventoryRuleOperatorSchema,
  InventoryRuleSchema,
  InventorySearchFiltersSchema,
  InventorySearchRequestSchema,
  InventorySearchResponseSchema,
  InventorySortFieldSchema,
  InventoryStatusSchema,
  InventorySuggestRequestSchema,
  InventorySuggestResponseSchema,
  InventorySuggestionSchema,
  InventoryVehicleListSchema,
  InventoryVehiclePageSchema,
  InventoryVehicleRequestSchema,
  InventoryVehicleSchema,
  InviteAdminSchema,
  InviteLocationsSchema,
  InviteUserSchema,
  JsonRpcErrorSchema,
  JsonRpcRequestSchema,
  LEAD_STATUS_LABELS,
  LeadAiSummaryResultSchema,
  LeadAssignSchema,
  LeadBandSchema,
  LeadBoardColumnQuerySchema,
  LeadBoardColumnSchema,
  LeadBoardSchema,
  LeadFeedSchema,
  LeadListQuerySchema,
  LeadListSchema,
  LeadPageSchema,
  LeadPipelineFiltersSchema,
  LeadPipelineNextActionSchema,
  LeadPipelineSortSchema,
  LeadPrimaryChannelSchema,
  LeadSchema,
  LeadSnoozeSchema,
  LeadStatusSchema,
  LeadSubmitRequestSchema,
  LeadSubmitResponseSchema,
  ListNotificationsQuerySchema,
  ListTasksQuerySchema,
  MEMBERSHIP_ROLE_LABELS,
  MENTION_REGEX,
  ManualVehicleCreateSchema,
  ManualVehicleUpdateSchema,
  MembershipRoleSchema,
  MergeContactsResultSchema,
  MergeContactsSchema,
  MoveTaskSchema,
  NeedsYouFeedSchema,
  NotificationCategorySchema,
  NotificationCountSchema,
  NotificationEventPreferenceSchema,
  NotificationEventTypeSchema,
  NotificationPageSchema,
  NotificationSchema,
  NotificationSettingsSchema,
  NotificationSeveritySchema,
  OPERATOR_PAUSABLE_CHANNEL_TYPES,
  OUTREACH_STAGE_LABELS,
  OUTREACH_TERMINAL_STAGES,
  OnboardingA2pStatusSchema,
  OnboardingCreateSchema,
  OnboardingStepIdSchema,
  OnboardingStepSchema,
  OpsAffectedDealershipSchema,
  OpsCronHeartbeatSchema,
  OpsQueueDepthSchema,
  OpsSnapshotSchema,
  OptionalCaPostalCodeSchema,
  OptionalCountryCodeSchema,
  OptionalEinSchema,
  OptionalEmailSchema,
  OptionalPhoneE164Schema,
  OptionalStateOrProvinceCodeSchema,
  OptionalUrlSchema,
  OptionalUsStateCodeSchema,
  OptionalUsZipSchema,
  OptionalVinSchema,
  OptionalZipOrPostalCodeSchema,
  OutboundAttachmentKindSchema,
  OutboundAttachmentSchema,
  OutreachListQuerySchema,
  OutreachNoteKindSchema,
  OutreachNoteSchema,
  OutreachProspectPageSchema,
  OutreachProspectSchema,
  OutreachStageSchema,
  OutreachSummarySchema,
  PROVIDER_MANAGED_CHANNEL_TYPES,
  PhoneE164Schema,
  PlatformBillingSummarySchema,
  PreferredCommunicationChannelSchema,
  PushConfigSchema,
  PushSubscriptionInputSchema,
  RangeFacetSchema,
  RejectActivityApprovalSchema,
  RejectAiDraftSchema,
  ReminderStepSchema,
  ReminderTemplateSchema,
  ReportAiContainmentSchema,
  ReportCurrentSlaBreachesPageSchema,
  ReportCurrentSlaBreachesQuerySchema,
  ReportFunnelSchema,
  ReportLeaderboardRowSchema,
  ReportLeaderboardSchema,
  ReportResponseTimeSchema,
  ReportResponseTrendPointSchema,
  ReportSourceRoiRowSchema,
  ReportSourceRoiSchema,
  ReportVoiceStatsSchema,
  ReportVolumeSchema,
  RequestEmailCodeSchema,
  ResolveOrCreateContactSchema,
  RooftopFacetSchema,
  RooftopListSchema,
  RooftopSchema,
  RooftopStatusSchema,
  RotateSftpAccountSchema,
  SCRAPING_PROVIDER_IDS,
  SelectDealershipSchema,
  SendLeadMessageSchema,
  ServiceTypeCategorySchema,
  ServiceTypeListSchema,
  ServiceTypeSchema,
  SetAvatarSchema,
  SftpAccountRecordSchema,
  SftpAccountStatusSchema,
  SftpAccountWithSecretSchema,
  SftpCreateFolderRequestSchema,
  SftpDeletePathRequestSchema,
  SftpDownloadUrlResponseSchema,
  SftpFileEntrySchema,
  SftpFolderSchema,
  SftpListResponseSchema,
  SftpPasswordSchema,
  SftpUploadUrlRequestSchema,
  SftpUploadUrlResponseSchema,
  SftpUsernameSchema,
  SnoozeTaskSchema,
  StartConversationSchema,
  StartMetaOAuthSchema,
  StateOrProvinceCodeSchema,
  SuggestedResponseSchema,
  TELEPHONY_PRICING,
  TagListSchema,
  TagSchema,
  TaskCountsQuerySchema,
  TaskCountsSchema,
  TaskCreatedByFilterSchema,
  TaskPageSchema,
  TaskReasonSchema,
  TaskSchema,
  TaskStatusSchema,
  TaskTypeSchema,
  TaskViewSchema,
  TelephonyAccountListSchema,
  TelephonyAccountSchema,
  TelephonyAccountSearchResultListSchema,
  TelephonyAccountSearchResultSchema,
  TelephonyAvailableNumberListSchema,
  TelephonyAvailableNumberSchema,
  TelephonyBrandStatusSchema,
  TelephonyBusinessTypeSchema,
  TelephonyE911AddressSchema,
  TelephonyEmergencyStatusSchema,
  TelephonyNumberTypeSchema,
  TelephonyPhoneNumberListSchema,
  TelephonyPhoneNumberSchema,
  TelephonyPurchaseNumberSchema,
  TelephonySearchNumbersSchema,
  TelephonyUpdateNumberSchema,
  TelephonyUsageRangeSchema,
  TelephonyUsageRowSchema,
  TelephonyUsageSummarySchema,
  TermFacetSchema,
  TradeInConditionSchema,
  TradeInListSchema,
  TradeInPageSchema,
  TradeInSchema,
  TradeInStatusSchema,
  TranscribeAttachmentResultSchema,
  TranscribeAttachmentSchema,
  USAGE_TYPES,
  US_STATES,
  US_TIMEZONES,
  US_ZIP_RE,
  UpdateActivitySchema,
  UpdateAiAgentSchema,
  UpdateAiSkillSchema,
  UpdateAppointmentSchema,
  UpdateAutomationRuleSchema,
  UpdateCadenceSequenceSchema,
  UpdateChannelSchema,
  UpdateContactChannelSchema,
  UpdateContactSchema,
  UpdateDealershipSchema,
  UpdateIntegrationSchema,
  UpdateInventoryImportSchema,
  UpdateInventoryVehicleSchema,
  UpdateLeadSchema,
  UpdateMyProfileSchema,
  UpdateNotificationSettingsSchema,
  UpdateOnboardingStepSchema,
  UpdateOutreachProspectSchema,
  UpdateRooftopScheduleSchema,
  UpdateRooftopSchema,
  UpdateServiceTypeSchema,
  UpdateSftpAccountSchema,
  UpdateTagSchema,
  UpdateTaskSchema,
  UpdateTelephonyAccountSchema,
  UpdateUserMembershipSchema,
  UpdateUserSchema,
  UpdateWebhookEndpointSchema,
  UpsertAiRooftopPromptSchema,
  UrlSchema,
  UsStateCodeSchema,
  UsZipSchema,
  UserKindSchema,
  UserListSchema,
  UserMembershipListSchema,
  UserMembershipSchema,
  UserSchema,
  UserStatusSchema,
  VIN_RE,
  VehicleConditionSchema,
  VehicleInterestLevelSchema,
  VehicleInterestListSchema,
  VehicleInterestPageSchema,
  VehicleInterestSchema,
  VehicleInterestStatusSchema,
  VehicleSourceSchema,
  VehicleStatusSchema,
  VerifyEmailCodeSchema,
  VerifyIntegrationLocationSchema,
  VerifyIntegrationTokenRequestSchema,
  VerifyIntegrationTokenResponseSchema,
  VinSchema,
  WEBHOOK_EVENTS,
  WEEKDAYS,
  WebhookDeliverySchema,
  WebhookDeliveryStatusSchema,
  WebhookEndpointSchema,
  WebhookEventSchema,
  WeeklyBusinessHoursSchema,
  WidgetBrandSchema,
  WidgetCustomizationSchema,
  ZipOrPostalCodeSchema,
  changeFieldLabel,
  channelStatusActions,
  computeLeadScore,
  contactDisplayName,
  contactLanguageAltLabelList,
  contactLanguageLabel,
  contactLanguageLabelList,
  contactLanguageVoiceLocale,
  contactStatusLabel,
  countryLabel,
  currencyForCountry,
  dateWithinBusinessHours,
  extractMentionIds,
  formatChangeValue,
  formatInTimeZone,
  formatMention,
  formatPhoneDisplay,
  formatUsd,
  inventoryProviderName,
  isAiAlwaysOnChannelType,
  isAiUsageKind,
  isInventoryProviderId,
  isOperatorPausableChannelType,
  isProviderManagedChannelType,
  isScrapingProviderId,
  leadStatusLabel,
  nextOpenInstant,
  normalizeContactLanguage,
  normalizeScrapeDomain,
  parseActivityChanges,
  renderEmailCodeEmail,
  renderInvitationEmail,
  replaceMentions,
  slugFromLabel,
  splitMentions,
  stateOptionsForCountry,
  summarizeActivityChanges,
  timezonesForCountry,
  translateCarrierError,
  truncateMentions,
  userDisplayLabel,
  utcToWallTime,
  wallClockDate,
  wallTimeToUtc
};
//# sourceMappingURL=index.js.map